import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, NgModule, Directive, Component, Input, HostBinding, ComponentFactoryResolver, ViewChild, PLATFORM_ID, ChangeDetectionStrategy, ElementRef, ContentChildren, HostListener, ViewContainerRef, EventEmitter, Output, SimpleChange, ContentChild, forwardRef, Pipe, TemplateRef, Type } from '@angular/core';
import * as i1$1 from '@angular/common';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ReplaySubject, Subject, Observable, merge, fromEvent, EMPTY, BehaviorSubject, combineLatest, from } from 'rxjs';
import { map, startWith, pairwise, filter, distinctUntilChanged, share, takeUntil, delay, repeat, switchMap, debounceTime, takeWhile, publish, refCount, take, skip, finalize, tap } from 'rxjs/operators';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i6 from '@angular/router';
import { RouterModule, NavigationEnd } from '@angular/router';
import * as i1 from '@angular/cdk/a11y';
import { FocusTrap, FocusTrapFactory, FocusKeyManager, ActiveDescendantKeyManager, FocusMonitor } from '@angular/cdk/a11y';
import { CdkPortal, CdkPortalOutlet, ComponentPortal, TemplatePortal, PortalInjector, PortalModule } from '@angular/cdk/portal';
import * as i3 from '@angular/cdk/overlay';
import { Overlay, OverlayPositionBuilder, OverlayContainer, FlexibleConnectedPositionStrategy, OverlayModule, ViewportRuler, GlobalPositionStrategy, ScrollDispatcher, BlockScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import * as i1$2 from '@angular/platform-browser';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Directionality, BidiModule } from '@angular/cdk/bidi';

const CJ_THEME_OPTIONS = new InjectionToken('CashJar UI Theme Options');
const CJ_MEDIA_BREAKPOINTS = new InjectionToken('CashJar UI Media Breakpoints');
const CJ_BUILT_IN_JS_THEMES = new InjectionToken('CashJar UI Built-in JS Themes');
const CJ_JS_THEMES = new InjectionToken('CashJar UI JS Themes');
const CJ_WINDOW = new InjectionToken('Window');
const CJ_DOCUMENT = new InjectionToken('Document');

const DEFAULT_MEDIA_BREAKPOINTS = [
    {
        name: 'xs',
        width: 0,
    },
    {
        name: 'is',
        width: 400,
    },
    {
        name: 'sm',
        width: 576,
    },
    {
        name: 'md',
        width: 768,
    },
    {
        name: 'lg',
        width: 992,
    },
    {
        name: 'xl',
        width: 1200,
    },
    {
        name: 'xxl',
        width: 1400,
    },
    {
        name: 'xxxl',
        width: 1600,
    },
];
/**
 * Manages media breakpoints
 *
 * Provides access to available media breakpoints to convert window width to a configured breakpoint,
 * e.g. 200px - *xs* breakpoint
 */
class CjMediaBreakpointsService {
    constructor(breakpoints) {
        this.breakpoints = breakpoints;
        this.breakpointsMap = this.breakpoints.reduce((res, b) => {
            res[b.name] = b.width;
            return res;
        }, {});
    }
    /**
     * Returns a configured breakpoint by width
     * @param width number
     * @returns {Z|{name: string, width: number}}
     */
    getByWidth(width) {
        const unknown = { name: 'unknown', width: width };
        const breakpoints = this.getBreakpoints();
        return breakpoints
            .find((point, index) => {
            const next = breakpoints[index + 1];
            return width >= point.width && (!next || width < next.width);
        }) || unknown;
    }
    /**
     * Returns a configured breakpoint by name
     * @param name string
     * @returns CjMediaBreakpoint
     */
    getByName(name) {
        const unknown = { name: 'unknown', width: NaN };
        const breakpoints = this.getBreakpoints();
        return breakpoints.find((point) => name === point.name) || unknown;
    }
    /**
     * Returns a list of configured breakpoints for the theme
     * @returns CjMediaBreakpoint[]
     */
    getBreakpoints() {
        return this.breakpoints;
    }
    /**
     * Returns a map of configured breakpoints for the theme
     * @returns {[p: string]: number}
     */
    getBreakpointsMap() {
        return this.breakpointsMap;
    }
}
CjMediaBreakpointsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMediaBreakpointsService, deps: [{ token: CJ_MEDIA_BREAKPOINTS }], target: i0.ɵɵFactoryTarget.Injectable });
CjMediaBreakpointsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMediaBreakpointsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMediaBreakpointsService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_MEDIA_BREAKPOINTS]
                }] }]; } });

const palette$3 = {
    primary: '#3366ff',
    success: '#00d68f',
    info: '#0095ff',
    warning: '#ffaa00',
    danger: '#ff3d71',
};
const LIGHT_THEME = {
    name: 'light',
    variables: {
        fontMain: 'Inter, sans-serif',
        fontSecondary: 'Raleway, sans-serif',
        bg: '#ffffff',
        bg2: '#f7f9fc',
        bg3: '#edf1f7',
        bg4: '#e4e9f2',
        border: '#ffffff',
        border2: '#f7f9fc',
        border3: '#edf1f7',
        border4: '#e4e9f2',
        border5: '#c5cee0',
        fg: '#8f9bb3',
        fgHeading: '#1a2138',
        fgText: '#1a2138',
        fgHighlight: palette$3.primary,
        layoutBg: '#f7f9fc',
        separator: '#edf1f7',
        primary: palette$3.primary,
        success: palette$3.success,
        info: palette$3.info,
        warning: palette$3.warning,
        danger: palette$3.danger,
        primaryLight: '#598bff',
        successLight: '#2ce69b',
        infoLight: '#42aaff',
        warningLight: '#ffc94d',
        dangerLight: '#ff708d',
    },
};

const palette$2 = {
    primary: '#3366ff',
    success: '#00d68f',
    info: '#0095ff',
    warning: '#ffaa00',
    danger: '#ff3d71',
};
const DARK_THEME = {
    name: 'dark',
    variables: {
        fontMain: 'Inter, sans-serif',
        fontSecondary: 'Raleway, sans-serif',
        bg: '#222b45',
        bg2: '#1a2138',
        bg3: '#151a30',
        bg4: '#101426',
        border: '#222b45',
        border2: '#1a2138',
        border3: '#151a30',
        border4: '#101426',
        border5: '#101426',
        fg: '#8f9bb3',
        fgHeading: '#ffffff',
        fgText: '#ffffff',
        fgHighlight: palette$2.primary,
        layoutBg: '#1b1b38',
        separator: '#1b1b38',
        primary: palette$2.primary,
        success: palette$2.success,
        info: palette$2.info,
        warning: palette$2.warning,
        danger: palette$2.danger,
        primaryLight: '#598bff',
        successLight: '#2ce69b',
        infoLight: '#42aaff',
        warningLight: '#ffc94d',
        dangerLight: '#ff708d',
    },
};

const palette$1 = {
    primary: '#3366ff',
    success: '#00d68f',
    info: '#0095ff',
    warning: '#ffaa00',
    danger: '#ff3d71',
};
const VEGA_LIGHT_THEME = {
    name: 'vega-light',
    variables: {
        fontMain: 'Inter, sans-serif',
        fontSecondary: 'Raleway, sans-serif',
        bg: '#ffffff',
        bg2: '#f7f9fc',
        bg3: '#edf1f7',
        bg4: '#e4e9f2',
        border: '#ffffff',
        border2: '#f7f9fc',
        border3: '#edf1f7',
        border4: '#e4e9f2',
        border5: '#c5cee0',
        fg: '#8f9bb3',
        fgHeading: '#1a2138',
        fgText: '#1a2138',
        fgHighlight: palette$1.primary,
        layoutBg: '#f7f9fc',
        separator: '#edf1f7',
        primary: palette$1.primary,
        success: palette$1.success,
        info: palette$1.info,
        warning: palette$1.warning,
        danger: palette$1.danger,
        primaryLight: '#598bff',
        successLight: '#2ce69b',
        infoLight: '#42aaff',
        warningLight: '#ffc94d',
        dangerLight: '#ff708d',
    },
};

const palette = {
    primary: '#3366ff',
    success: '#00d68f',
    info: '#0095ff',
    warning: '#ffaa00',
    danger: '#ff3d71',
};
const VEGA_DARK_THEME = {
    name: 'vega-dark',
    variables: {
        fontMain: 'Inter, sans-serif',
        fontSecondary: 'Raleway, sans-serif',
        bg: '#222b45',
        bg2: '#1a2138',
        bg3: '#151a30',
        bg4: '#101426',
        border: '#222b45',
        border2: '#1a2138',
        border3: '#151a30',
        border4: '#101426',
        border5: '#101426',
        fg: '#8f9bb3',
        fgHeading: '#ffffff',
        fgText: '#ffffff',
        fgHighlight: palette.primary,
        layoutBg: '#1b1b38',
        separator: '#1b1b38',
        primary: palette.primary,
        success: palette.success,
        info: palette.info,
        warning: palette.warning,
        danger: palette.danger,
        primaryLight: '#598bff',
        successLight: '#2ce69b',
        infoLight: '#42aaff',
        warningLight: '#ffc94d',
        dangerLight: '#ff708d',
    },
};

const BUILT_IN_THEMES = [
    LIGHT_THEME,
    DARK_THEME,
    VEGA_LIGHT_THEME,
    VEGA_DARK_THEME,
];
/**
 * Js Themes registry - provides access to the JS themes' variables.
 * Usually shouldn't be used directly, but through the CjThemeService class methods (getJsTheme).
 */
class CjJSThemesRegistry {
    constructor(builtInThemes, newThemes = []) {
        this.themes = {};
        const themes = this.combineByNames(newThemes, builtInThemes);
        themes.forEach((theme) => {
            this.register(theme, theme.name, theme.base);
        });
    }
    /**
     * Registers a new JS theme
     * @param config any
     * @param themeName string
     * @param baseTheme string
     */
    register(config, themeName, baseTheme) {
        const base = this.has(baseTheme) ? this.get(baseTheme) : {};
        this.themes[themeName] = this.mergeDeep({}, base, config);
    }
    /**
     * Checks whether the theme is registered
     * @param themeName
     * @returns boolean
     */
    has(themeName) {
        return !!this.themes[themeName];
    }
    /**
     * Return a theme
     * @param themeName
     * @returns CjJSThemeOptions
     */
    get(themeName) {
        if (!this.themes[themeName]) {
            throw Error(`CjThemeConfig: no theme '${themeName}' found registered.`);
        }
        return JSON.parse(JSON.stringify(this.themes[themeName]));
    }
    combineByNames(newThemes, oldThemes) {
        if (newThemes) {
            const mergedThemes = [];
            newThemes.forEach((theme) => {
                const sameOld = oldThemes.find((tm) => tm.name === theme.name)
                    || {};
                const mergedTheme = this.mergeDeep({}, sameOld, theme);
                mergedThemes.push(mergedTheme);
            });
            oldThemes.forEach((theme) => {
                if (!mergedThemes.find((tm) => tm.name === theme.name)) {
                    mergedThemes.push(theme);
                }
            });
            return mergedThemes;
        }
        return oldThemes;
    }
    isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }
    // TODO: move to helpers
    mergeDeep(target, ...sources) {
        if (!sources.length) {
            return target;
        }
        const source = sources.shift();
        if (this.isObject(target) && this.isObject(source)) {
            for (const key in source) {
                if (this.isObject(source[key])) {
                    if (!target[key]) {
                        Object.assign(target, { [key]: {} });
                    }
                    this.mergeDeep(target[key], source[key]);
                }
                else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }
        return this.mergeDeep(target, ...sources);
    }
}
CjJSThemesRegistry.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjJSThemesRegistry, deps: [{ token: CJ_BUILT_IN_JS_THEMES }, { token: CJ_JS_THEMES }], target: i0.ɵɵFactoryTarget.Injectable });
CjJSThemesRegistry.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjJSThemesRegistry });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjJSThemesRegistry, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_BUILT_IN_JS_THEMES]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_JS_THEMES]
                }] }]; } });

/**
 * Main Nebular service. Includes various helper methods.
 */
class CjThemeService {
    constructor(options, breakpointService, jsThemesRegistry) {
        this.options = options;
        this.breakpointService = breakpointService;
        this.jsThemesRegistry = jsThemesRegistry;
        this.themeChanges$ = new ReplaySubject(1);
        this.appendLayoutClass$ = new Subject();
        this.removeLayoutClass$ = new Subject();
        this.changeWindowWidth$ = new ReplaySubject(2);
        if (options && options.name) {
            this.changeTheme(options.name);
        }
    }
    /**
     * Change current application theme
     * @param {string} name
     */
    changeTheme(name) {
        this.themeChanges$.next({ name, previous: this.currentTheme });
        this.currentTheme = name;
    }
    changeWindowWidth(width) {
        this.changeWindowWidth$.next(width);
    }
    /**
     * Returns a theme object with variables (color/paddings/etc) on a theme change.
     * Once subscribed - returns current theme.
     *
     * @returns {Observable<CjJSThemeOptions>}
     */
    getJsTheme() {
        return this.onThemeChange().pipe(map((theme) => {
            return this.jsThemesRegistry.get(theme.name);
        }));
    }
    /**
     * Triggers media query breakpoint change
     * Returns a pair where the first item is previous media breakpoint and the second item is current breakpoit.
     * ```ts
     *  [{ name: 'xs', width: 0 }, { name: 'md', width: 768 }] // change from `xs` to `md`
     * ```
     * @returns {Observable<[CjMediaBreakpoint, CjMediaBreakpoint]>}
     */
    onMediaQueryChange() {
        return this.changeWindowWidth$
            .pipe(startWith(undefined), pairwise(), map(([prevWidth, width]) => {
            return [
                this.breakpointService.getByWidth(prevWidth),
                this.breakpointService.getByWidth(width),
            ];
        }), filter(([prevPoint, point]) => {
            return prevPoint.name !== point.name;
        }), distinctUntilChanged(null, params => params[0].name + params[1].name), share());
    }
    /**
     * Triggered when current theme is changed
     * @returns {Observable<any>}
     */
    onThemeChange() {
        return this.themeChanges$.pipe(share());
    }
    /**
     * Append a class to cj-layout
     * @param {string} className
     */
    appendLayoutClass(className) {
        this.appendLayoutClass$.next(className);
    }
    /**
     * Triggered when a new class is added to cj-layout through `appendLayoutClass` method
     * @returns {Observable<any>}
     */
    onAppendLayoutClass() {
        return this.appendLayoutClass$.pipe(share());
    }
    /**
     * Removes a class from cj-layout
     * @param {string} className
     */
    removeLayoutClass(className) {
        this.removeLayoutClass$.next(className);
    }
    /**
     * Triggered when a class is removed from cj-layout through `removeLayoutClass` method
     * @returns {Observable<any>}
     */
    onRemoveLayoutClass() {
        return this.removeLayoutClass$.pipe(share());
    }
}
CjThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeService, deps: [{ token: CJ_THEME_OPTIONS }, { token: CjMediaBreakpointsService }, { token: CjJSThemesRegistry }], target: i0.ɵɵFactoryTarget.Injectable });
CjThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_THEME_OPTIONS]
                }] }, { type: CjMediaBreakpointsService }, { type: CjJSThemesRegistry }]; } });

class CjSpinnerService {
    constructor(document) {
        this.document = document;
        this.loaders = [];
        this.selector = 'cj-global-spinner';
    }
    /**
     * Appends new loader to the list of loader to be completed before
     * spinner will be hidden
     * @param method Promise<any>
     */
    registerLoader(method) {
        this.loaders.push(method);
    }
    /**
     * Clears the list of loader
     */
    clear() {
        this.loaders = [];
    }
    /**
     * Start the loader process, show spinnder and execute loaders
     */
    load() {
        this.showSpinner();
        this.executeAll();
    }
    executeAll(done = (values) => { }) {
        Promise.all(this.loaders).then((values) => {
            this.hideSpinner();
            done.call(null, values);
        })
            .catch((error) => {
            // TODO: Promise.reject
            console.error(error);
        });
    }
    // TODO is there any better way of doing this?
    showSpinner() {
        const el = this.getSpinnerElement();
        if (el) {
            el.style['display'] = 'block';
        }
    }
    hideSpinner() {
        const el = this.getSpinnerElement();
        if (el) {
            el.style['display'] = 'none';
        }
    }
    getSpinnerElement() {
        return this.document.getElementById(this.selector);
    }
}
CjSpinnerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSpinnerService, deps: [{ token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjSpinnerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSpinnerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSpinnerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

/**
 * Layout direction.
 * */
var CjLayoutDirection;
(function (CjLayoutDirection) {
    CjLayoutDirection["LTR"] = "ltr";
    CjLayoutDirection["RTL"] = "rtl";
})(CjLayoutDirection || (CjLayoutDirection = {}));
/**
 * Layout direction setting injection token.
 * */
const CJ_LAYOUT_DIRECTION = new InjectionToken('Layout direction');
/**
 * Layout Direction Service.
 * Allows to set or get layout direction and listen to its changes
 */
class CjLayoutDirectionService {
    constructor(direction = CjLayoutDirection.LTR) {
        this.direction = direction;
        this.$directionChange = new ReplaySubject(1);
        this.setDirection(direction);
    }
    /**
     * Returns true if layout direction set to left to right.
     * @returns boolean.
     * */
    isLtr() {
        return this.direction === CjLayoutDirection.LTR;
    }
    /**
     * Returns true if layout direction set to right to left.
     * @returns boolean.
     * */
    isRtl() {
        return this.direction === CjLayoutDirection.RTL;
    }
    /**
     * Returns current layout direction.
     * @returns CjLayoutDirection.
     * */
    getDirection() {
        return this.direction;
    }
    /**
     * Sets layout direction
     * @param {CjLayoutDirection} direction
     */
    setDirection(direction) {
        this.direction = direction;
        this.$directionChange.next(direction);
    }
    /**
     * Triggered when direction was changed.
     * @returns Observable<CjLayoutDirection>.
     */
    onDirectionChange() {
        return this.$directionChange.pipe(share());
    }
}
CjLayoutDirectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutDirectionService, deps: [{ token: CJ_LAYOUT_DIRECTION, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
CjLayoutDirectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutDirectionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutDirectionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CJ_LAYOUT_DIRECTION]
                }] }]; } });

/**
 * Layout scroll service. Provides information about current scroll position,
 * as well as methods to update position of the scroll.
 *
 * The reason we added this service is that in Nebular there are two scroll modes:
 * - the default mode when scroll is on body
 * - and the `withScroll` mode, when scroll is removed from the body and moved to an element inside of the
 * `Cc-layout` component
 */
class CjLayoutScrollService {
    constructor() {
        this.scrollPositionReq$ = new Subject();
        this.manualScroll$ = new Subject();
        this.scroll$ = new Subject();
        this.scrollable$ = new Subject();
    }
    /**
     * Returns scroll position
     *
     * @returns {Observable<CjScrollPosition>}
     */
    getPosition() {
        return new Observable((observer) => {
            const listener = new Subject();
            listener.subscribe(observer);
            this.scrollPositionReq$.next({ listener });
            return () => listener.complete();
        });
    }
    /**
     * Sets scroll position
     *
     * @param {number} x
     * @param {number} y
     */
    scrollTo(x = null, y = null) {
        this.manualScroll$.next({ x, y });
    }
    /**
     * Returns a stream of scroll events
     *
     * @returns {Observable<any>}
     */
    onScroll() {
        return this.scroll$.pipe(share());
    }
    /**
     * @private
     * @returns Observable<CjScrollPosition>.
     */
    onManualScroll() {
        return this.manualScroll$.pipe(share());
    }
    /**
     * @private
     * @returns {Subject<any>}
     */
    onGetPosition() {
        return this.scrollPositionReq$;
    }
    onScrollableChange() {
        return this.scrollable$.pipe(share());
    }
    /**
     * @private
     * @param {any} event
     */
    fireScrollChange(event) {
        this.scroll$.next(event);
    }
    scrollable(scrollable) {
        this.scrollable$.next(scrollable);
    }
}
CjLayoutScrollService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutScrollService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjLayoutScrollService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutScrollService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutScrollService, decorators: [{
            type: Injectable
        }] });

/**
 * Simple helper service to return Layout dimensions
 * Depending of current Layout scroll mode (default or `withScroll` when scroll is moved to an element
 * inside of the layout) corresponding dimensions will be returns  - of `documentElement` in first case and
 * `.scrollable-container` in the second.
 */
class CjLayoutRulerService {
    constructor() {
        this.contentDimensionsReq$ = new Subject();
    }
    /**
     * Content dimensions
     * @returns {Observable<CjLayoutDimensions>}
     */
    getDimensions() {
        return new Observable((observer) => {
            const listener = new Subject();
            listener.subscribe(observer);
            this.contentDimensionsReq$.next({ listener });
            return () => listener.complete();
        });
    }
    /**
     * @private
     * @returns {Subject<any>}
     */
    onGetDimensions() {
        return this.contentDimensionsReq$;
    }
}
CjLayoutRulerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutRulerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjLayoutRulerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutRulerService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutRulerService, decorators: [{
            type: Injectable
        }] });

class CjSharedModule {
}
CjSharedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjSharedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSharedModule, exports: [CommonModule,
        FormsModule,
        RouterModule] });
CjSharedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSharedModule, imports: [CommonModule,
        FormsModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSharedModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        CommonModule,
                        FormsModule,
                        RouterModule,
                    ],
                }]
        }] });

/**
 * Overrides angular cdk focus trap to keep restore functionality inside trap.
 * */
class CjFocusTrap extends FocusTrap {
    constructor(element, checker, ngZone, document, deferAnchors) {
        super(element, checker, ngZone, document, deferAnchors);
        this.element = element;
        this.checker = checker;
        this.ngZone = ngZone;
        this.document = document;
        this.savePreviouslyFocusedElement();
    }
    restoreFocus() {
        this.previouslyFocusedElement.focus();
        this.destroy();
    }
    blurPreviouslyFocusedElement() {
        this.previouslyFocusedElement.blur();
    }
    savePreviouslyFocusedElement() {
        this.previouslyFocusedElement = this.document.activeElement;
    }
}
class CjFocusTrapFactoryService extends FocusTrapFactory {
    constructor(checker, ngZone, document) {
        super(checker, ngZone, document);
        this.checker = checker;
        this.ngZone = ngZone;
        this.document = document;
    }
    create(element, deferCaptureElements) {
        return new CjFocusTrap(element, this.checker, this.ngZone, this.document, deferCaptureElements);
    }
}
CjFocusTrapFactoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusTrapFactoryService, deps: [{ token: i1.InteractivityChecker }, { token: i0.NgZone }, { token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjFocusTrapFactoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusTrapFactoryService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusTrapFactoryService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.InteractivityChecker }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

class CjFocusKeyManager extends FocusKeyManager {
}
class CjFocusKeyManagerFactoryService {
    create(items) {
        return new CjFocusKeyManager(items);
    }
}

class CjActiveDescendantKeyManager extends ActiveDescendantKeyManager {
}
class CjActiveDescendantKeyManagerFactoryService {
    create(items) {
        return new CjActiveDescendantKeyManager(items);
    }
}
var CjKeyManagerActiveItemMode;
(function (CjKeyManagerActiveItemMode) {
    CjKeyManagerActiveItemMode[CjKeyManagerActiveItemMode["RESET_ACTIVE"] = -1] = "RESET_ACTIVE";
    CjKeyManagerActiveItemMode[CjKeyManagerActiveItemMode["FIRST_ACTIVE"] = 0] = "FIRST_ACTIVE";
})(CjKeyManagerActiveItemMode || (CjKeyManagerActiveItemMode = {}));

class CjFocusMonitor extends FocusMonitor {
}
CjFocusMonitor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusMonitor, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjFocusMonitor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusMonitor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFocusMonitor, decorators: [{
            type: Injectable
        }] });
class CjA11yModule {
    static forRoot() {
        return {
            ngModule: CjA11yModule,
            providers: [
                CjFocusTrapFactoryService,
                CjFocusKeyManagerFactoryService,
                CjActiveDescendantKeyManagerFactoryService,
                { provide: CjFocusMonitor, useClass: FocusMonitor },
            ],
        };
    }
}
CjA11yModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjA11yModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjA11yModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjA11yModule });
CjA11yModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjA11yModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjA11yModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

class CjPortalDirective extends CdkPortal {
}
CjPortalDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPortalDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
CjPortalDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjPortalDirective, selector: "[cjPortal]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPortalDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[cjPortal]' }]
        }] });
class CjPortalOutletDirective extends CdkPortalOutlet {
}
CjPortalOutletDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPortalOutletDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
CjPortalOutletDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjPortalOutletDirective, selector: "[cjPortalOutlet]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPortalOutletDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[cjPortalOutlet]' }]
        }] });
class CjComponentPortal extends ComponentPortal {
}
class CjOverlay extends Overlay {
}
CjOverlay.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlay, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjOverlay.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlay });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlay, decorators: [{
            type: Injectable
        }] });
class CjOverlayPositionBuilder extends OverlayPositionBuilder {
}
CjOverlayPositionBuilder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayPositionBuilder, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjOverlayPositionBuilder.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayPositionBuilder });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayPositionBuilder, decorators: [{
            type: Injectable
        }] });
class CjTemplatePortal extends TemplatePortal {
    constructor(template, viewContainerRef, context) {
        super(template, viewContainerRef, context);
    }
}
class CjOverlayContainer extends OverlayContainer {
}
CjOverlayContainer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainer, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjOverlayContainer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainer, decorators: [{
            type: Injectable
        }] });
class CjFlexibleConnectedPositionStrategy extends FlexibleConnectedPositionStrategy {
}
class CjPortalInjector extends PortalInjector {
}
const CDK_MODULES = [OverlayModule, PortalModule];
/**
 * This module helps us to keep all angular/cdk deps inside our cdk module via providing aliases.
 * Approach will help us move cdk in separate npm package and refactor nebular/theme code.
 * */
class CjCdkMappingModule {
    static forRoot() {
        return {
            ngModule: CjCdkMappingModule,
            providers: [CjOverlay, CjOverlayPositionBuilder],
        };
    }
}
CjCdkMappingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkMappingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjCdkMappingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkMappingModule, declarations: [CjPortalDirective, CjPortalOutletDirective], imports: [OverlayModule, PortalModule], exports: [OverlayModule, PortalModule, CjPortalDirective, CjPortalOutletDirective] });
CjCdkMappingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkMappingModule, imports: [[...CDK_MODULES], OverlayModule, PortalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkMappingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...CDK_MODULES],
                    exports: [...CDK_MODULES, CjPortalDirective, CjPortalOutletDirective],
                    declarations: [CjPortalDirective, CjPortalOutletDirective],
                }]
        }] });

var CjGlobalLogicalPosition;
(function (CjGlobalLogicalPosition) {
    CjGlobalLogicalPosition["TOP_START"] = "top-start";
    CjGlobalLogicalPosition["TOP_END"] = "top-end";
    CjGlobalLogicalPosition["BOTTOM_START"] = "bottom-start";
    CjGlobalLogicalPosition["BOTTOM_END"] = "bottom-end";
})(CjGlobalLogicalPosition || (CjGlobalLogicalPosition = {}));
var CjGlobalPhysicalPosition;
(function (CjGlobalPhysicalPosition) {
    CjGlobalPhysicalPosition["TOP_RIGHT"] = "top-right";
    CjGlobalPhysicalPosition["TOP_LEFT"] = "top-left";
    CjGlobalPhysicalPosition["BOTTOM_RIGHT"] = "bottom-right";
    CjGlobalPhysicalPosition["BOTTOM_LEFT"] = "bottom-left";
})(CjGlobalPhysicalPosition || (CjGlobalPhysicalPosition = {}));
class CjPositionHelper {
    constructor(layoutDirection) {
        this.layoutDirection = layoutDirection;
    }
    toLogicalPosition(position) {
        if (Object.values(CjGlobalLogicalPosition).includes(position)) {
            return position;
        }
        if (this.layoutDirection.isLtr()) {
            return this.toLogicalPositionWhenLtr(position);
        }
        else {
            return this.toLogicalPositionWhenRtl(position);
        }
    }
    toPhysicalPosition(position) {
        if (Object.values(CjGlobalPhysicalPosition).includes(position)) {
            return position;
        }
        if (this.layoutDirection.isLtr()) {
            return this.toPhysicalPositionWhenLtr(position);
        }
        else {
            return this.toPhysicalPositionWhenRtl(position);
        }
    }
    isTopPosition(position) {
        const logicalPosition = this.toLogicalPosition(position);
        return logicalPosition === CjGlobalLogicalPosition.TOP_END || logicalPosition === CjGlobalLogicalPosition.TOP_START;
    }
    isRightPosition(position) {
        const physicalPosition = this.toPhysicalPosition(position);
        return (physicalPosition === CjGlobalPhysicalPosition.TOP_RIGHT ||
            physicalPosition === CjGlobalPhysicalPosition.BOTTOM_RIGHT);
    }
    toLogicalPositionWhenLtr(position) {
        switch (position) {
            case CjGlobalPhysicalPosition.TOP_RIGHT:
                return CjGlobalLogicalPosition.TOP_END;
            case CjGlobalPhysicalPosition.TOP_LEFT:
                return CjGlobalLogicalPosition.TOP_START;
            case CjGlobalPhysicalPosition.BOTTOM_RIGHT:
                return CjGlobalLogicalPosition.BOTTOM_END;
            case CjGlobalPhysicalPosition.BOTTOM_LEFT:
                return CjGlobalLogicalPosition.BOTTOM_START;
        }
    }
    toLogicalPositionWhenRtl(position) {
        switch (position) {
            case CjGlobalPhysicalPosition.TOP_RIGHT:
                return CjGlobalLogicalPosition.TOP_START;
            case CjGlobalPhysicalPosition.TOP_LEFT:
                return CjGlobalLogicalPosition.TOP_END;
            case CjGlobalPhysicalPosition.BOTTOM_RIGHT:
                return CjGlobalLogicalPosition.BOTTOM_START;
            case CjGlobalPhysicalPosition.BOTTOM_LEFT:
                return CjGlobalLogicalPosition.BOTTOM_END;
        }
    }
    toPhysicalPositionWhenLtr(position) {
        switch (position) {
            case CjGlobalLogicalPosition.TOP_START:
                return CjGlobalPhysicalPosition.TOP_LEFT;
            case CjGlobalLogicalPosition.TOP_END:
                return CjGlobalPhysicalPosition.TOP_RIGHT;
            case CjGlobalLogicalPosition.BOTTOM_START:
                return CjGlobalPhysicalPosition.BOTTOM_LEFT;
            case CjGlobalLogicalPosition.BOTTOM_END:
                return CjGlobalPhysicalPosition.BOTTOM_RIGHT;
        }
    }
    toPhysicalPositionWhenRtl(position) {
        switch (position) {
            case CjGlobalLogicalPosition.TOP_START:
                return CjGlobalPhysicalPosition.TOP_RIGHT;
            case CjGlobalLogicalPosition.TOP_END:
                return CjGlobalPhysicalPosition.TOP_LEFT;
            case CjGlobalLogicalPosition.BOTTOM_START:
                return CjGlobalPhysicalPosition.BOTTOM_RIGHT;
            case CjGlobalLogicalPosition.BOTTOM_END:
                return CjGlobalPhysicalPosition.BOTTOM_LEFT;
        }
    }
}
CjPositionHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionHelper, deps: [{ token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Injectable });
CjPositionHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionHelper });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionHelper, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjLayoutDirectionService }]; } });

class CjPlatform extends Platform {
}
CjPlatform.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPlatform, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjPlatform.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPlatform, providedIn: 'root', useClass: Platform });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPlatform, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                    useClass: Platform,
                }]
        }] });

class CjViewportRulerAdapter extends ViewportRuler {
    constructor(platform, ngZone, ruler, scroll, document) {
        super(platform, ngZone, document);
        this.ruler = ruler;
        this.scroll = scroll;
    }
    getViewportSize() {
        let res;
        /*
        * getDimensions call is really synchronous operation.
        * And we have to conform with the interface of the original service.
        * */
        this.ruler.getDimensions()
            .pipe(map(dimensions => ({ width: dimensions.clientWidth, height: dimensions.clientHeight })))
            .subscribe(rect => res = rect);
        return res;
    }
    getViewportScrollPosition() {
        let res;
        /*
        * getPosition call is really synchronous operation.
        * And we have to conform with the interface of the original service.
        * */
        this.scroll.getPosition()
            .pipe(map((position) => ({ top: position.y, left: position.x })))
            .subscribe(position => res = position);
        return res;
    }
}
CjViewportRulerAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjViewportRulerAdapter, deps: [{ token: CjPlatform }, { token: i0.NgZone }, { token: CjLayoutRulerService }, { token: CjLayoutScrollService }, { token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjViewportRulerAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjViewportRulerAdapter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjViewportRulerAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjPlatform }, { type: i0.NgZone }, { type: CjLayoutRulerService }, { type: CjLayoutScrollService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

function throwLayoutNotFoundError() {
    throw new Error(`[CjOverlayContainerAdapter]: Layout not found.
  When using CashJar UI '<cj-layout>' is required and should wrap other nebular components.`);
}
/**
 * Provides cj-layout as overlay container.
 * Container has to be cleared when layout destroys.
 * Another way previous version of the container will be used
 * but it isn't inserted in DOM and exists in memory only.
 * This case important only if you switch between multiple layouts.
 * */
class CjOverlayContainerAdapter extends CjOverlayContainer {
    setContainer(container) {
        this.container = container;
    }
    clearContainer() {
        this.container = null;
        this._containerElement = null;
    }
    _createContainer() {
        this.checkContainer();
        const container = this._document.createElement('div');
        container.classList.add('cdk-overlay-container');
        this.container.appendChild(container);
        this._containerElement = container;
    }
    checkContainer() {
        if (!this.container) {
            throwLayoutNotFoundError();
        }
    }
}
CjOverlayContainerAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainerAdapter, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjOverlayContainerAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainerAdapter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainerAdapter, decorators: [{
            type: Injectable
        }] });

var CjAdjustment;
(function (CjAdjustment) {
    CjAdjustment["NOOP"] = "noop";
    CjAdjustment["CLOCKWISE"] = "clockwise";
    CjAdjustment["COUNTERCLOCKWISE"] = "counterclockwise";
    CjAdjustment["VERTICAL"] = "vertical";
    CjAdjustment["HORIZONTAL"] = "horizontal";
})(CjAdjustment || (CjAdjustment = {}));
var CjPosition;
(function (CjPosition) {
    CjPosition["TOP"] = "top";
    CjPosition["BOTTOM"] = "bottom";
    CjPosition["LEFT"] = "left";
    CjPosition["RIGHT"] = "right";
    CjPosition["START"] = "start";
    CjPosition["END"] = "end";
    CjPosition["TOP_END"] = "top-end";
    CjPosition["TOP_START"] = "top-start";
    CjPosition["BOTTOM_END"] = "bottom-end";
    CjPosition["BOTTOM_START"] = "bottom-start";
    CjPosition["END_TOP"] = "end-top";
    CjPosition["END_BOTTOM"] = "end-bottom";
    CjPosition["START_TOP"] = "start-top";
    CjPosition["START_BOTTOM"] = "start-bottom";
})(CjPosition || (CjPosition = {}));
const POSITIONS = {
    [CjPosition.RIGHT](offset) {
        return { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset };
    },
    [CjPosition.BOTTOM](offset) {
        return { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset };
    },
    [CjPosition.LEFT](offset) {
        return { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset };
    },
    [CjPosition.TOP](offset) {
        return { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset };
    },
    [CjPosition.START](offset) {
        return this[CjPosition.LEFT](offset);
    },
    [CjPosition.END](offset) {
        return this[CjPosition.RIGHT](offset);
    },
    [CjPosition.END_TOP](offset) {
        return { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: offset };
    },
    [CjPosition.END_BOTTOM](offset) {
        return { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: offset };
    },
    [CjPosition.BOTTOM_START](offset) {
        return { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: offset };
    },
    [CjPosition.BOTTOM_END](offset) {
        return { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: offset };
    },
    [CjPosition.START_TOP](offset) {
        return { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -offset };
    },
    [CjPosition.START_BOTTOM](offset) {
        return { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -offset };
    },
    [CjPosition.TOP_START](offset) {
        return { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offset };
    },
    [CjPosition.TOP_END](offset) {
        return { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offset };
    },
};
const RTL_PHYSICAL_POSITIONS = {
    ...POSITIONS,
    [CjPosition.RIGHT](offset) {
        return { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: offset };
    },
    [CjPosition.LEFT](offset) {
        return { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: -offset };
    },
    [CjPosition.START](offset) {
        return this[CjPosition.RIGHT](offset);
    },
    [CjPosition.END](offset) {
        return this[CjPosition.LEFT](offset);
    },
};
const COUNTER_CLOCKWISE_POSITIONS = [
    CjPosition.TOP,
    CjPosition.TOP_END,
    CjPosition.TOP_START,
    CjPosition.START,
    CjPosition.START_TOP,
    CjPosition.START_BOTTOM,
    CjPosition.BOTTOM,
    CjPosition.BOTTOM_START,
    CjPosition.BOTTOM_END,
    CjPosition.END,
    CjPosition.END_BOTTOM,
    CjPosition.END_TOP,
];
const CLOCKWISE_POSITIONS = [
    CjPosition.TOP,
    CjPosition.TOP_START,
    CjPosition.TOP_END,
    CjPosition.END,
    CjPosition.END_TOP,
    CjPosition.END_BOTTOM,
    CjPosition.BOTTOM,
    CjPosition.BOTTOM_END,
    CjPosition.BOTTOM_START,
    CjPosition.START,
    CjPosition.START_BOTTOM,
    CjPosition.START_TOP,
];
const VERTICAL_POSITIONS = [CjPosition.BOTTOM, CjPosition.TOP];
const HORIZONTAL_POSITIONS = [CjPosition.START, CjPosition.END];
function comparePositions(p1, p2) {
    return (p1.originX === p2.originX && p1.originY === p2.originY && p1.overlayX === p2.overlayX && p1.overlayY === p2.overlayY);
}
/**
 * The main idea of the adjustable connected strategy is to provide predefined set of positions for your overlay.
 * You have to provide adjustment and appropriate strategy will be chosen in runtime.
 * */
class CjAdjustableConnectedPositionStrategy extends CjFlexibleConnectedPositionStrategy {
    constructor() {
        super(...arguments);
        this._offset = 8;
        this.positionChange = this.positionChanges.pipe(map((positionChange) => positionChange.connectionPair), map((connectionPair) => {
            return this.appliedPositions.find(({ connectedPosition }) => {
                return comparePositions(connectedPosition, connectionPair);
            }).key;
        }));
    }
    attach(overlayRef) {
        /**
         * We have to apply positions before attach because super.attach() validates positions and crashes app
         * if no positions provided.
         * */
        this.applyPositions();
        super.attach(overlayRef);
    }
    direction(direction) {
        this._direction = direction;
        return this;
    }
    apply() {
        this.applyPositions();
        super.apply();
    }
    position(position) {
        this._position = position;
        return this;
    }
    adjustment(adjustment) {
        this._adjustment = adjustment;
        return this;
    }
    offset(offset) {
        this._offset = offset;
        return this;
    }
    applyPositions() {
        const positions = this.createPositions();
        this.persistChosenPositions(positions);
        this.withPositions(this.appliedPositions.map(({ connectedPosition }) => connectedPosition));
    }
    createPositions() {
        switch (this._adjustment) {
            case CjAdjustment.NOOP:
                return [this._position];
            case CjAdjustment.CLOCKWISE:
                return this.reorderPreferredPositions(CLOCKWISE_POSITIONS);
            case CjAdjustment.COUNTERCLOCKWISE:
                return this.reorderPreferredPositions(COUNTER_CLOCKWISE_POSITIONS);
            case CjAdjustment.HORIZONTAL:
                return this.reorderPreferredPositions(HORIZONTAL_POSITIONS);
            case CjAdjustment.VERTICAL:
                return this.reorderPreferredPositions(VERTICAL_POSITIONS);
        }
    }
    mapToLogicalPosition(position) {
        if (position === CjPosition.LEFT) {
            return this._direction === CjLayoutDirection.LTR ? CjPosition.START : CjPosition.END;
        }
        if (position === CjPosition.RIGHT) {
            return this._direction === CjLayoutDirection.LTR ? CjPosition.END : CjPosition.START;
        }
        return position;
    }
    persistChosenPositions(positions) {
        const positionGrid = this._direction === CjLayoutDirection.RTL ? RTL_PHYSICAL_POSITIONS : POSITIONS;
        this.appliedPositions = positions.map((position) => ({
            key: position,
            connectedPosition: positionGrid[position](this._offset),
        }));
    }
    reorderPreferredPositions(positions) {
        // Physical positions should be mapped to logical as adjustments use logical positions.
        const startPosition = this.mapToLogicalPosition(this._position);
        const startPositionIndex = positions.indexOf(startPosition);
        const firstPart = positions.slice(startPositionIndex);
        const secondPart = positions.slice(0, startPositionIndex);
        return firstPart.concat(secondPart);
    }
}
class CjGlobalPositionStrategy extends GlobalPositionStrategy {
    position(position) {
        switch (position) {
            case CjGlobalLogicalPosition.TOP_START:
                return this.top().left();
            case CjGlobalLogicalPosition.TOP_END:
                return this.top().right();
            case CjGlobalLogicalPosition.BOTTOM_START:
                return this.bottom().left();
            case CjGlobalLogicalPosition.BOTTOM_END:
                return this.bottom().right();
        }
    }
}
class CjPositionBuilderService {
    constructor(document, viewportRuler, platform, positionBuilder, overlayContainer) {
        this.document = document;
        this.viewportRuler = viewportRuler;
        this.platform = platform;
        this.positionBuilder = positionBuilder;
        this.overlayContainer = overlayContainer;
    }
    global() {
        return new CjGlobalPositionStrategy();
    }
    connectedTo(elementRef) {
        return new CjAdjustableConnectedPositionStrategy(elementRef, this.viewportRuler, this.document, this.platform, this.overlayContainer)
            .withFlexibleDimensions(false)
            .withPush(false);
    }
}
CjPositionBuilderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionBuilderService, deps: [{ token: CJ_DOCUMENT }, { token: CjViewportRulerAdapter }, { token: CjPlatform }, { token: CjOverlayPositionBuilder }, { token: CjOverlayContainerAdapter }], target: i0.ɵɵFactoryTarget.Injectable });
CjPositionBuilderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionBuilderService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionBuilderService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }, { type: CjViewportRulerAdapter }, { type: CjPlatform }, { type: CjOverlayPositionBuilder }, { type: CjOverlayContainerAdapter }]; } });

class CjPositionedContainerComponent {
    get top() {
        return this.position === CjPosition.TOP;
    }
    get topStart() {
        return this.position === CjPosition.TOP_START;
    }
    get topEnd() {
        return this.position === CjPosition.TOP_END;
    }
    get right() {
        return this.position === CjPosition.RIGHT || this.position === CjPosition.END;
    }
    get endTop() {
        return this.position === CjPosition.END_TOP;
    }
    get endBottom() {
        return this.position === CjPosition.END_BOTTOM;
    }
    get bottom() {
        return this.position === CjPosition.BOTTOM;
    }
    get bottomStart() {
        return this.position === CjPosition.BOTTOM_START;
    }
    get bottomEnd() {
        return this.position === CjPosition.BOTTOM_END;
    }
    get left() {
        return this.position === CjPosition.LEFT || this.position === CjPosition.START;
    }
    get startTop() {
        return this.position === CjPosition.START_TOP;
    }
    get startBottom() {
        return this.position === CjPosition.START_BOTTOM;
    }
}
CjPositionedContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionedContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjPositionedContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjPositionedContainerComponent, selector: "ng-component", inputs: { position: "position" }, host: { properties: { "class.cj-overlay-top": "this.top", "class.cj-overlay-top-start": "this.topStart", "class.cj-overlay-top-end": "this.topEnd", "class.cj-overlay-right": "this.right", "class.cj-overlay-end-top": "this.endTop", "class.cj-overlay-end-bottom": "this.endBottom", "class.cj-overlay-bottom": "this.bottom", "class.cj-overlay-bottom-start": "this.bottomStart", "class.cj-overlay-bottom-end": "this.bottomEnd", "class.cj-overlay-left": "this.left", "class.cj-overlay-start-top": "this.startTop", "class.cj-overlay-start-bottom": "this.startBottom" } }, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPositionedContainerComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                }]
        }], propDecorators: { position: [{
                type: Input
            }], top: [{
                type: HostBinding,
                args: ['class.cj-overlay-top']
            }], topStart: [{
                type: HostBinding,
                args: ['class.cj-overlay-top-start']
            }], topEnd: [{
                type: HostBinding,
                args: ['class.cj-overlay-top-end']
            }], right: [{
                type: HostBinding,
                args: ['class.cj-overlay-right']
            }], endTop: [{
                type: HostBinding,
                args: ['class.cj-overlay-end-top']
            }], endBottom: [{
                type: HostBinding,
                args: ['class.cj-overlay-end-bottom']
            }], bottom: [{
                type: HostBinding,
                args: ['class.cj-overlay-bottom']
            }], bottomStart: [{
                type: HostBinding,
                args: ['class.cj-overlay-bottom-start']
            }], bottomEnd: [{
                type: HostBinding,
                args: ['class.cj-overlay-bottom-end']
            }], left: [{
                type: HostBinding,
                args: ['class.cj-overlay-left']
            }], startTop: [{
                type: HostBinding,
                args: ['class.cj-overlay-start-top']
            }], startBottom: [{
                type: HostBinding,
                args: ['class.cj-overlay-start-bottom']
            }] } });
class CjOverlayContainerComponent {
    constructor(vcr, injector, changeDetectorRef) {
        this.vcr = vcr;
        this.injector = injector;
        this.changeDetectorRef = changeDetectorRef;
        this.isAttached = false;
    }
    get isStringContent() {
        return !!this.content;
    }
    attachComponentPortal(portal, context) {
        portal.injector = this.createChildInjector(portal.componentFactoryResolver);
        const componentRef = this.portalOutlet.attachComponentPortal(portal);
        if (context) {
            Object.assign(componentRef.instance, context);
        }
        componentRef.changeDetectorRef.markForCheck();
        componentRef.changeDetectorRef.detectChanges();
        this.isAttached = true;
        return componentRef;
    }
    attachTemplatePortal(portal) {
        const templateRef = this.portalOutlet.attachTemplatePortal(portal);
        templateRef.detectChanges();
        this.isAttached = true;
        return templateRef;
    }
    attachStringContent(content) {
        this.content = content;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        this.isAttached = true;
    }
    detach() {
        if (this.portalOutlet.hasAttached()) {
            this.portalOutlet.detach();
        }
        this.attachStringContent(null);
        this.isAttached = false;
    }
    createChildInjector(cfr) {
        return new CjPortalInjector(this.injector, new WeakMap([
            [ComponentFactoryResolver, cfr],
        ]));
    }
}
CjOverlayContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainerComponent, deps: [{ token: i0.ViewContainerRef }, { token: i0.Injector }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CjOverlayContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjOverlayContainerComponent, selector: "cj-overlay-container", viewQueries: [{ propertyName: "portalOutlet", first: true, predicate: CjPortalOutletDirective, descendants: true, static: true }], ngImport: i0, template: `
    <div *ngIf="isStringContent" class="primitive-overlay">{{ content }}</div>
    <ng-template cjPortalOutlet></ng-template>
  `, isInline: true, directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: CjPortalOutletDirective, selector: "[cjPortalOutlet]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-overlay-container',
                    template: `
    <div *ngIf="isStringContent" class="primitive-overlay">{{ content }}</div>
    <ng-template cjPortalOutlet></ng-template>
  `,
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: i0.Injector }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { portalOutlet: [{
                type: ViewChild,
                args: [CjPortalOutletDirective, { static: true }]
            }] } });

function patch(container, containerContext) {
    Object.assign(container.instance, containerContext);
    container.changeDetectorRef.detectChanges();
    return container;
}
function createContainer(ref, container, context, componentFactoryResolver) {
    const containerRef = ref.attach(new CjComponentPortal(container, null, null, componentFactoryResolver));
    patch(containerRef, context);
    return containerRef;
}
class CjOverlayService {
    constructor(overlay, layoutDirection) {
        this.overlay = overlay;
        this.layoutDirection = layoutDirection;
    }
    get scrollStrategies() {
        return this.overlay.scrollStrategies;
    }
    create(config) {
        const overlayRef = this.overlay.create(config);
        this.layoutDirection.onDirectionChange()
            .subscribe(dir => overlayRef.setDirection(dir));
        return overlayRef;
    }
}
CjOverlayService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayService, deps: [{ token: CjOverlay }, { token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Injectable });
CjOverlayService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjOverlay }, { type: CjLayoutDirectionService }]; } });

class CjScrollDispatcherAdapter extends ScrollDispatcher {
    constructor(ngZone, platform, scrollService, document) {
        super(ngZone, platform, document);
        this.scrollService = scrollService;
    }
    scrolled(auditTimeInMs) {
        return merge(super.scrolled(auditTimeInMs), this.scrollService.onScroll());
    }
}
CjScrollDispatcherAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollDispatcherAdapter, deps: [{ token: i0.NgZone }, { token: CjPlatform }, { token: CjLayoutScrollService }, { token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjScrollDispatcherAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollDispatcherAdapter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollDispatcherAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: CjPlatform }, { type: CjLayoutScrollService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

/**
 * Overrides default block scroll strategy because default strategy blocks scrolling on the body only.
 * But CashJar UI has its own scrollable container - cj-layout. So, we need to block scrolling in it to.
 * */
class CjBlockScrollStrategyAdapter extends BlockScrollStrategy {
    constructor(document, viewportRuler, scrollService) {
        super(viewportRuler, document);
        this.scrollService = scrollService;
    }
    enable() {
        super.enable();
        this.scrollService.scrollable(false);
    }
    disable() {
        super.disable();
        this.scrollService.scrollable(true);
    }
}
CjBlockScrollStrategyAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBlockScrollStrategyAdapter, deps: [{ token: CJ_DOCUMENT }, { token: CjViewportRulerAdapter }, { token: CjLayoutScrollService }], target: i0.ɵɵFactoryTarget.Injectable });
CjBlockScrollStrategyAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBlockScrollStrategyAdapter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBlockScrollStrategyAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }, { type: CjViewportRulerAdapter }, { type: CjLayoutScrollService }]; } });
class CjScrollStrategyOptions extends ScrollStrategyOptions {
    constructor(scrollService, scrollDispatcher, viewportRuler, ngZone, document) {
        super(scrollDispatcher, viewportRuler, ngZone, document);
        this.scrollService = scrollService;
        this.scrollDispatcher = scrollDispatcher;
        this.viewportRuler = viewportRuler;
        this.ngZone = ngZone;
        this.document = document;
        this.block = () => new CjBlockScrollStrategyAdapter(this.document, this.viewportRuler, this.scrollService);
    }
}
CjScrollStrategyOptions.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollStrategyOptions, deps: [{ token: CjLayoutScrollService }, { token: i3.ScrollDispatcher }, { token: CjViewportRulerAdapter }, { token: i0.NgZone }, { token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjScrollStrategyOptions.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollStrategyOptions });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjScrollStrategyOptions, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjLayoutScrollService }, { type: i3.ScrollDispatcher }, { type: CjViewportRulerAdapter }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

class CjCdkAdapterModule {
    static forRoot() {
        return {
            ngModule: CjCdkAdapterModule,
            providers: [
                CjViewportRulerAdapter,
                CjOverlayContainerAdapter,
                CjBlockScrollStrategyAdapter,
                CjScrollDispatcherAdapter,
                CjScrollStrategyOptions,
                { provide: OverlayContainer, useExisting: CjOverlayContainerAdapter },
                { provide: CjOverlayContainer, useExisting: CjOverlayContainerAdapter },
                { provide: ScrollDispatcher, useExisting: CjScrollDispatcherAdapter },
                { provide: ScrollStrategyOptions, useExisting: CjScrollStrategyOptions },
            ],
        };
    }
}
CjCdkAdapterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkAdapterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjCdkAdapterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkAdapterModule });
CjCdkAdapterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkAdapterModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCdkAdapterModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

var CjTrigger;
(function (CjTrigger) {
    CjTrigger["NOOP"] = "noop";
    CjTrigger["CLICK"] = "click";
    CjTrigger["HOVER"] = "hover";
    CjTrigger["HINT"] = "hint";
    CjTrigger["FOCUS"] = "focus";
})(CjTrigger || (CjTrigger = {}));
/**
 * TODO maybe we have to use renderer.listen instead of observableFromEvent?
 * Renderer provides capability use it in service worker, ssr and so on.
 * */
class CjTriggerStrategyBase {
    constructor(document, host, container) {
        this.document = document;
        this.host = host;
        this.container = container;
        this.destroyed$ = new Subject();
    }
    destroy() {
        this.destroyed$.next();
    }
    isNotOnHostOrContainer(element) {
        return !this.isOnHost(element) && !this.isOnContainer(element);
    }
    isOnHostOrContainer(element) {
        return this.isOnHost(element) || this.isOnContainer(element);
    }
    isOnHost(element) {
        return this.host.contains(element);
    }
    isOnContainer(element) {
        return this.container() && this.container().location.nativeElement.contains(element);
    }
}
/**
 * Creates show and hide event streams.
 * Fires toggle event when the click was performed on the host element.
 * Fires close event when the click was performed on the document but
 * not on the host or container.
 * */
class CjClickTriggerStrategy extends CjTriggerStrategyBase {
    constructor() {
        super(...arguments);
        // since we should track click for both SHOW and HIDE event we firstly need to track the click and the state
        // of the container and then later on decide should we hide it or show
        // if we track the click & state separately this will case a behavior when the container is getting shown
        // and then hidden right away
        this.click$ = fromEvent(this.document, 'click').pipe(map((event) => [!this.container() && this.isOnHost(event.target), event]), share(), takeUntil(this.destroyed$));
        this.show$ = this.click$.pipe(filter(([shouldShow]) => shouldShow), map(([, event]) => event), takeUntil(this.destroyed$));
        this.hide$ = this.click$.pipe(filter(([shouldShow, event]) => !shouldShow && !this.isOnContainer(event.target)), map(([, event]) => event), takeUntil(this.destroyed$));
    }
}
/**
 * Creates show and hide event streams.
 * Fires open event when a mouse hovers over the host element and stay over at least 100 milliseconds.
 * Fires close event when the mouse leaves the host element and stops out of the host and popover container.
 * */
class CjHoverTriggerStrategy extends CjTriggerStrategyBase {
    constructor() {
        super(...arguments);
        this.show$ = fromEvent(this.host, 'mouseenter').pipe(filter(() => !this.container()), 
        // this `delay & takeUntil & repeat` operators combination is a synonym for `conditional debounce`
        // meaning that if one event occurs in some time after the initial one we won't react to it
        delay(100), 
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil
        takeUntil(fromEvent(this.host, 'mouseleave')), repeat(), takeUntil(this.destroyed$));
        this.hide$ = fromEvent(this.host, 'mouseleave').pipe(switchMap(() => fromEvent(this.document, 'mousemove').pipe(debounceTime(100), takeWhile(() => !!this.container()), filter((event) => this.isNotOnHostOrContainer(event.target)))), takeUntil(this.destroyed$));
    }
}
/**
 * Creates show and hide event streams.
 * Fires open event when a mouse hovers over the host element and stay over at least 100 milliseconds.
 * Fires close event when the mouse leaves the host element.
 * */
class CjHintTriggerStrategy extends CjTriggerStrategyBase {
    constructor() {
        super(...arguments);
        this.show$ = fromEvent(this.host, 'mouseenter').pipe(
        // this `delay & takeUntil & repeat` operators combination is a synonym for `conditional debounce`
        // meaning that if one event occurs in some time after the initial one we won't react to it
        delay(100), 
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil
        takeUntil(fromEvent(this.host, 'mouseleave')), repeat(), takeUntil(this.destroyed$));
        this.hide$ = fromEvent(this.host, 'mouseleave').pipe(takeUntil(this.destroyed$));
    }
}
/**
 * Creates show and hide event streams.
 * Fires open event when a focus is on the host element and stay over at least 100 milliseconds.
 * Fires close event when the focus leaves the host element.
 * */
class CjFocusTriggerStrategy extends CjTriggerStrategyBase {
    constructor() {
        super(...arguments);
        this.focusOut$ = fromEvent(this.host, 'focusout').pipe(switchMap(() => fromEvent(this.document, 'focusin').pipe(takeWhile(() => !!this.container()), filter((event) => this.isNotOnHostOrContainer(event.target)))), takeUntil(this.destroyed$));
        this.clickIn$ = fromEvent(this.host, 'click').pipe(filter(() => !this.container()), takeUntil(this.destroyed$));
        this.clickOut$ = fromEvent(this.document, 'click').pipe(filter(() => !!this.container()), 
        /**
         * Event target of `click` could be different from `activeElement`.
         * If during click you return focus to the host, it won't be opened.
         */
        filter((event) => {
            if (this.isNotOnHostOrContainer(event.target)) {
                return this.isNotOnHostOrContainer(this.document.activeElement);
            }
            return false;
        }), takeUntil(this.destroyed$));
        this.tabKeyPress$ = fromEvent(this.document, 'keydown').pipe(filter((event) => event.keyCode === 9), filter(() => !!this.container()), takeUntil(this.destroyed$));
        this.show$ = merge(fromEvent(this.host, 'focusin'), this.clickIn$).pipe(filter(() => !this.container()), debounceTime(100), 
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil
        takeUntil(fromEvent(this.host, 'focusout')), repeat(), takeUntil(this.destroyed$));
        this.hide$ = merge(this.focusOut$, this.tabKeyPress$, this.clickOut$).pipe(takeUntil(this.destroyed$));
    }
}
/**
 * Creates empty show and hide event streams.
 * */
class CjNoopTriggerStrategy extends CjTriggerStrategyBase {
    constructor() {
        super(...arguments);
        this.show$ = EMPTY;
        this.hide$ = EMPTY;
    }
}
class CjTriggerStrategyBuilderService {
    constructor(_document) {
        this._document = _document;
    }
    trigger(trigger) {
        this._trigger = trigger;
        return this;
    }
    host(host) {
        this._host = host;
        return this;
    }
    container(container) {
        this._container = container;
        return this;
    }
    build() {
        switch (this._trigger) {
            case CjTrigger.CLICK:
                return new CjClickTriggerStrategy(this._document, this._host, this._container);
            case CjTrigger.HINT:
                return new CjHintTriggerStrategy(this._document, this._host, this._container);
            case CjTrigger.HOVER:
                return new CjHoverTriggerStrategy(this._document, this._host, this._container);
            case CjTrigger.FOCUS:
                return new CjFocusTriggerStrategy(this._document, this._host, this._container);
            case CjTrigger.NOOP:
                return new CjNoopTriggerStrategy(this._document, this._host, this._container);
            default:
                throw new Error('Trigger have to be provided');
        }
    }
}
CjTriggerStrategyBuilderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTriggerStrategyBuilderService, deps: [{ token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
CjTriggerStrategyBuilderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTriggerStrategyBuilderService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTriggerStrategyBuilderService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; } });

class CjOverlayModule {
    static forRoot() {
        return {
            ngModule: CjOverlayModule,
            providers: [
                CjPositionBuilderService,
                CjTriggerStrategyBuilderService,
                CjOverlayService,
                CjPositionHelper,
                ...CjCdkMappingModule.forRoot().providers,
                ...CjCdkAdapterModule.forRoot().providers,
                ...CjA11yModule.forRoot().providers,
            ],
        };
    }
}
CjOverlayModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjOverlayModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayModule, declarations: [CjPositionedContainerComponent,
        CjOverlayContainerComponent], imports: [CjCdkMappingModule,
        CjSharedModule], exports: [CjCdkMappingModule,
        CjCdkAdapterModule,
        CjOverlayContainerComponent] });
CjOverlayModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayModule, imports: [[
            CjCdkMappingModule,
            CjSharedModule,
        ], CjCdkMappingModule,
        CjCdkAdapterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjCdkMappingModule,
                        CjSharedModule,
                    ],
                    declarations: [
                        CjPositionedContainerComponent,
                        CjOverlayContainerComponent,
                    ],
                    exports: [
                        CjCdkMappingModule,
                        CjCdkAdapterModule,
                        CjOverlayContainerComponent,
                    ],
                }]
        }] });

class CjStatusService {
    constructor() {
        this.coreStatuses = ['primary', 'info', 'warning', 'danger'];
    }
    isCoreStatus(status) {
        return this.coreStatuses.includes(status);
    }
    isCustomStatus(status) {
        if (this.isValidStatusString(status)) {
            return !this.isCoreStatus(status);
        }
        return false;
    }
    getStatusClass(status) {
        if (this.isValidStatusString(status)) {
            return `status-${status}`;
        }
        return undefined;
    }
    isValidStatusString(status) {
        return typeof status === 'string' && status.length > 0;
    }
}
CjStatusService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjStatusService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjStatusService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjStatusService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjStatusService, decorators: [{
            type: Injectable
        }] });

function windowFactory(platformId) {
    if (isPlatformBrowser(platformId)) {
        return window;
    }
    // Provide undefined to get the error when trying to access the window as it
    // shouldn't be used outside the browser. Those who need to provide something
    // instead of window (e.g. domino window when running in node) could override
    // CJ_WINDOW token.
    return undefined;
}
class CjThemeModule {
    /**
     * Main Theme Module
     *
     * @param cjThemeOptions {CjThemeOptions} Main theme options
     * @param cjJSThemes {CjJSThemeOptions[]} List of JS Themes, will be merged with default themes
     * @param cjMediaBreakpoints {CjMediaBreakpoint} Available media breakpoints
     * @param layoutDirection {CjLayoutDirection} Layout direction
     *
     * @returns {ModuleWithProviders}
     */
    static forRoot(cjThemeOptions = { name: 'light' }, cjJSThemes, cjMediaBreakpoints, layoutDirection) {
        return {
            ngModule: CjThemeModule,
            providers: [
                { provide: CJ_THEME_OPTIONS, useValue: cjThemeOptions || {} },
                { provide: CJ_BUILT_IN_JS_THEMES, useValue: BUILT_IN_THEMES },
                { provide: CJ_JS_THEMES, useValue: cjJSThemes || [] },
                { provide: CJ_MEDIA_BREAKPOINTS, useValue: cjMediaBreakpoints || DEFAULT_MEDIA_BREAKPOINTS },
                { provide: CJ_DOCUMENT, useExisting: DOCUMENT },
                { provide: CJ_WINDOW, useFactory: windowFactory, deps: [PLATFORM_ID] },
                CjJSThemesRegistry,
                CjThemeService,
                CjMediaBreakpointsService,
                CjSpinnerService,
                { provide: CJ_LAYOUT_DIRECTION, useValue: layoutDirection || CjLayoutDirection.LTR },
                CjLayoutDirectionService,
                CjLayoutScrollService,
                CjLayoutRulerService,
                ...CjOverlayModule.forRoot().providers,
                CjStatusService,
            ],
        };
    }
}
CjThemeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjThemeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeModule, imports: [CommonModule] });
CjThemeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjThemeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    exports: [],
                }]
        }] });

class CjColorHelper {
    static shade(color, weight) {
        return CjColorHelper.mix('#000000', color, weight);
    }
    static tint(color, weight) {
        return CjColorHelper.mix('#ffffff', color, weight);
    }
    static mix(color1, color2, weight) {
        const d2h = (d) => d.toString(16);
        const h2d = (h) => parseInt(h, 16);
        let result = '#';
        for (let i = 1; i < 7; i += 2) {
            const firstPart = h2d(color1.substr(i, 2));
            const secondPart = h2d(color2.substr(i, 2));
            const resultPart = d2h(Math.floor(secondPart + (firstPart - secondPart) * (weight / 100.0)));
            result += ('0' + resultPart).slice(-2);
        }
        return result;
    }
    static hexToRgbA(hex, alpha) {
        let c;
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + alpha + ')';
        }
        throw new Error('Bad Hex');
    }
}

function convertToBoolProperty(value) {
    if (typeof value === 'string') {
        value = value.toLowerCase().trim();
        return value === 'true' || value === '';
    }
    return !!value;
}
function getElementHeight(el) {
    const style = window.getComputedStyle(el);
    const marginTop = parseInt(style.getPropertyValue('margin-top'), 10);
    const marginBottom = parseInt(style.getPropertyValue('margin-bottom'), 10);
    return el.offsetHeight + marginTop + marginBottom;
}

var CjIconPackType;
(function (CjIconPackType) {
    CjIconPackType["SVG"] = "svg";
    CjIconPackType["FONT"] = "font";
})(CjIconPackType || (CjIconPackType = {}));

class CjFontIcon {
    constructor(name, content, params = {}) {
        this.name = name;
        this.content = content;
        this.params = params;
    }
    getClasses(options) {
        const classes = [];
        if (this.params.packClass) {
            classes.push(this.params.packClass);
        }
        const name = this.params.iconClassPrefix ? `${this.params.iconClassPrefix}-${this.name}` : this.name;
        classes.push(name);
        return classes;
    }
    getContent(options) {
        return this.content;
    }
}
class CjSvgIcon {
    constructor(name, content, params = {}) {
        this.name = name;
        this.content = content;
        this.params = params;
    }
    getClasses(options) {
        const classes = [];
        if (this.params.packClass) {
            classes.push(this.params.packClass);
        }
        return classes;
    }
    getContent(options) {
        return this.content;
    }
}

class CjIconDefinition {
}
function throwPackNotFoundError(name) {
    throw Error(`Icon Pack '${name}' is not registered`);
}
function throwNoDefaultPackError() {
    throw Error('Default pack is not registered.');
}
function throwWrongPackTypeError(name, type, desiredType) {
    throw Error(`Pack '${name}' is not an '${desiredType}' Pack and its type is '${type}'`);
}
/**
 * This service allows to register multiple icon packs to use them later within `<cj-icon></cj-icon>` component.
 */
class CjIconLibraries {
    constructor() {
        this.packs = new Map();
    }
    /**
     * Registers new Svg icon pack
     * @param {string} name
     * @param {CjIcon} icons
     * @param {CjIconPackParams} params
     */
    registerSvgPack(name, icons, params = {}) {
        this.packs.set(name, {
            name,
            icons: new Map(Object.entries(icons)),
            params,
            type: CjIconPackType.SVG,
        });
    }
    /**
     * Registers new font pack
     * @param {string} name
     * @param {CjIconPackParams} params
     */
    registerFontPack(name, params = {}) {
        this.packs.set(name, {
            name,
            params,
            icons: new Map(),
            type: CjIconPackType.FONT,
        });
    }
    /**
     * Returns pack by name
     * @param {string} name
     */
    getPack(name) {
        return this.packs.get(name);
    }
    /**
     * Sets pack as a default
     * @param {string} name
     */
    setDefaultPack(name) {
        if (!this.packs.has(name)) {
            throwPackNotFoundError(name);
        }
        this.defaultPack = this.packs.get(name);
    }
    /**
     * Returns Svg icon
     * @param {string} name
     * @param {string} pack
     *
     * @returns CjIconDefinition
     */
    getSvgIcon(name, pack) {
        const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();
        if (iconsPack.type !== CjIconPackType.SVG) {
            throwWrongPackTypeError(iconsPack.name, iconsPack.type, 'SVG');
        }
        const icon = this.getIconFromPack(name, iconsPack);
        if (!icon) {
            return null;
        }
        return {
            name,
            pack: iconsPack.name,
            type: CjIconPackType.SVG,
            icon: this.createSvgIcon(name, icon, iconsPack.params),
        };
    }
    /**
     * Returns Font icon
     * @param {string} name
     * @param {string} pack
     *
     * @returns CjIconDefinition
     */
    getFontIcon(name, pack) {
        const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();
        if (iconsPack.type !== CjIconPackType.FONT) {
            throwWrongPackTypeError(iconsPack.name, iconsPack.type, 'Font');
        }
        const icon = this.getIconFromPack(name, iconsPack) ?? '';
        const iconContent = iconsPack.params.ligature ? name : icon;
        return {
            name,
            pack: iconsPack.name,
            type: CjIconPackType.FONT,
            icon: this.createFontIcon(name, iconContent, iconsPack.params),
        };
    }
    /**
     * Returns an icon
     * @param {string} name
     * @param {string} pack
     *
     * @returns CjIconDefinition
     */
    getIcon(name, pack) {
        const iconsPack = pack ? this.getPackOrThrow(pack) : this.getDefaultPackOrThrow();
        if (iconsPack.type === CjIconPackType.SVG) {
            return this.getSvgIcon(name, pack);
        }
        return this.getFontIcon(name, pack);
    }
    createSvgIcon(name, content, params) {
        return content instanceof CjSvgIcon ? content : new CjSvgIcon(name, content, params);
    }
    createFontIcon(name, content, params) {
        return content instanceof CjFontIcon ? content : new CjFontIcon(name, content, params);
    }
    getPackOrThrow(name) {
        const pack = this.packs.get(name);
        if (!pack) {
            throwPackNotFoundError(name);
        }
        return pack;
    }
    getDefaultPackOrThrow() {
        if (!this.defaultPack) {
            throwNoDefaultPackError();
        }
        return this.defaultPack;
    }
    getIconFromPack(name, pack) {
        if (pack.icons.has(name)) {
            return pack.icons.get(name);
        }
        return null;
    }
}
CjIconLibraries.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconLibraries, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjIconLibraries.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconLibraries, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconLibraries, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class CjIconComponent {
    constructor(sanitizer, iconLibrary, el, renderer, statusService) {
        this.sanitizer = sanitizer;
        this.iconLibrary = iconLibrary;
        this.el = el;
        this.renderer = renderer;
        this.statusService = statusService;
        this.prevClasses = [];
        this.html = '';
    }
    get primary() {
        return this.status === 'primary';
    }
    get info() {
        return this.status === 'info';
    }
    get success() {
        return this.status === 'success';
    }
    get warning() {
        return this.status === 'warning';
    }
    get danger() {
        return this.status === 'danger';
    }
    /**
     * Sets all icon configurable properties via config object.
     * If passed value is a string set icon name.
     * @docs-private
     */
    get config() {
        return this._config;
    }
    set config(value) {
        if (!value) {
            return;
        }
        this._config = value;
        if (typeof value === 'string') {
            this.icon = value;
        }
        else {
            this.icon = value.icon;
            this.pack = value.pack;
            this.status = value.status;
            this.options = value.options;
        }
    }
    ngOnInit() {
        this.iconDef = this.renderIcon(this.icon, this.pack, this.options);
    }
    ngOnChanges() {
        const iconDef = this.iconLibrary.getIcon(this.icon, this.pack);
        if (iconDef) {
            this.renderIcon(this.icon, this.pack, this.options);
        }
        else {
            this.clearIcon();
        }
    }
    renderIcon(name, pack, options) {
        const iconDefinition = this.iconLibrary.getIcon(name, pack);
        if (!iconDefinition) {
            return undefined;
        }
        const content = iconDefinition.icon.getContent(options);
        if (content) {
            this.html = this.sanitizer.bypassSecurityTrustHtml(content);
        }
        this.assignClasses(iconDefinition.icon.getClasses(options));
        return iconDefinition;
    }
    clearIcon() {
        this.html = '';
        this.assignClasses([]);
    }
    assignClasses(classes) {
        this.prevClasses.forEach((className) => {
            this.renderer.removeClass(this.el.nativeElement, className);
        });
        classes.forEach((className) => {
            this.renderer.addClass(this.el.nativeElement, className);
        });
        this.prevClasses = classes;
    }
}
CjIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconComponent, deps: [{ token: i1$2.DomSanitizer }, { token: CjIconLibraries }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Component });
CjIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjIconComponent, selector: "cj-icon", inputs: { icon: "icon", pack: "pack", options: "options", status: "status", config: "config" }, host: { properties: { "innerHtml": "this.html", "class.status-primary": "this.primary", "class.status-info": "this.info", "class.status-success": "this.success", "class.status-warning": "this.warning", "class.status-danger": "this.danger" } }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [":host{display:inline-block}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-icon', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:inline-block}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }, { type: CjIconLibraries }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CjStatusService }]; }, propDecorators: { html: [{
                type: HostBinding,
                args: ['innerHtml']
            }], primary: [{
                type: HostBinding,
                args: ['class.status-primary']
            }], info: [{
                type: HostBinding,
                args: ['class.status-info']
            }], success: [{
                type: HostBinding,
                args: ['class.status-success']
            }], warning: [{
                type: HostBinding,
                args: ['class.status-warning']
            }], danger: [{
                type: HostBinding,
                args: ['class.status-danger']
            }], icon: [{
                type: Input
            }], pack: [{
                type: Input
            }], options: [{
                type: Input
            }], status: [{
                type: Input
            }], config: [{
                type: Input
            }] } });

// eslint-disable-next-line @angular-eslint/directive-class-suffix
class CjButton {
    constructor(renderer, hostElement, cd, zone, statusService) {
        this.renderer = renderer;
        this.hostElement = hostElement;
        this.cd = cd;
        this.zone = zone;
        this.statusService = statusService;
        /**
         * Button size:
         * `small`, `medium`, `large`
         */
        this.size = 'medium';
        /**
         * Button status:
         * `primary`, `info`, `success`, `warning`, `danger`
         */
        this.status = 'primary';
        /**
         * Button appearance: `filled`, `outline`
         */
        this.appearance = 'filled';
        this._fullWidth = false;
        this._disabled = false;
        this.iconLeft = false;
        this.iconRight = false;
    }
    /**
     * Sets `filled` appearance
     */
    get filled() {
        return this.appearance === 'filled';
    }
    set filled(value) {
        if (convertToBoolProperty(value)) {
            this.appearance = 'filled';
        }
    }
    /**
     * Sets `outline` appearance
     */
    get outline() {
        return this.appearance === 'outline';
    }
    set outline(value) {
        if (convertToBoolProperty(value)) {
            this.appearance = 'outline';
        }
    }
    /**
     * If set element will fill its container
     */
    get fullWidth() {
        return this._fullWidth;
    }
    set fullWidth(value) {
        this._fullWidth = convertToBoolProperty(value);
    }
    /**
     * Disables the button
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        if (this.disabled !== convertToBoolProperty(value)) {
            this._disabled = !this.disabled;
            this.renderer.setProperty(this.hostElement.nativeElement, 'disabled', this.disabled);
        }
    }
    get tabbable() {
        if (this.disabled) {
            return '-1';
        }
        if (this.tabIndex == null) {
            return '0';
        }
        return this.tabIndex.toString();
    }
    get small() {
        return this.size === 'small';
    }
    get medium() {
        return this.size === 'medium';
    }
    get large() {
        return this.size === 'large';
    }
    get additionalClasses() {
        if (this.statusService.isCustomStatus(this.status)) {
            return [this.statusService.getStatusClass(this.status)];
        }
        return [];
    }
    ngAfterContentChecked() {
        const firstNode = this.nodes[0];
        const lastNode = this.nodes[this.nodes.length - 1];
        this.iconLeft = this.isIconExist(firstNode);
        this.iconRight = this.isIconExist(lastNode);
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => setTimeout(() => {
            this.renderer.addClass(this.hostElement.nativeElement, 'cj-transition');
        }));
    }
    get iconElement() {
        const el = this.hostElement.nativeElement;
        return el.querySelector('cj-icon');
    }
    get nodes() {
        return this.cd.rootNodes.filter((child) => child.nodeType !== Node.COMMENT_NODE);
    }
    isIconExist(node) {
        return this.icons.some((item) => item.nativeElement === node);
    }
}
CjButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButton, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Directive });
CjButton.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjButton, inputs: { size: "size", status: "status", appearance: "appearance", filled: "filled", outline: "outline", fullWidth: "fullWidth", disabled: "disabled", tabIndex: "tabIndex" }, host: { properties: { "class.appearance-filled": "this.filled", "class.appearance-outline": "this.outline", "class.full-width": "this.fullWidth", "attr.aria-disabled": "this.disabled", "class.btn-disabled": "this.disabled", "attr.tabindex": "this.tabbable", "class.size-small": "this.small", "class.size-medium": "this.medium", "class.size-large": "this.large", "class.icon-start": "this.iconLeft", "class.icon-end": "this.iconRight", "class": "this.additionalClasses" } }, queries: [{ propertyName: "icons", predicate: CjIconComponent, read: ElementRef }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButton, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: CjStatusService }]; }, propDecorators: { size: [{
                type: Input
            }], status: [{
                type: Input
            }], appearance: [{
                type: Input
            }], filled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.appearance-filled']
            }], outline: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.appearance-outline']
            }], fullWidth: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.full-width']
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-disabled']
            }, {
                type: HostBinding,
                args: ['class.btn-disabled']
            }], tabIndex: [{
                type: Input
            }], tabbable: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], small: [{
                type: HostBinding,
                args: ['class.size-small']
            }], medium: [{
                type: HostBinding,
                args: ['class.size-medium']
            }], large: [{
                type: HostBinding,
                args: ['class.size-large']
            }], iconLeft: [{
                type: HostBinding,
                args: ['class.icon-start']
            }], iconRight: [{
                type: HostBinding,
                args: ['class.icon-end']
            }], additionalClasses: [{
                type: HostBinding,
                args: ['class']
            }], icons: [{
                type: ContentChildren,
                args: [CjIconComponent, { read: ElementRef }]
            }] } });

class CjButtonComponent extends CjButton {
    constructor(renderer, hostElement, cd, zone, statusService) {
        super(renderer, hostElement, cd, zone, statusService);
        this.renderer = renderer;
        this.hostElement = hostElement;
        this.cd = cd;
        this.zone = zone;
        this.statusService = statusService;
    }
    get primary() {
        return this.status === 'primary';
    }
    get info() {
        return this.status === 'info';
    }
    get success() {
        return this.status === 'success';
    }
    get warning() {
        return this.status === 'warning';
    }
    get danger() {
        return this.status === 'danger';
    }
    onClick(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }
}
CjButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Component });
CjButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjButtonComponent, selector: "button[cjButton],a[cjButton],input[type=\"button\"][cjButton],input[type=\"submit\"][cjButton]", host: { listeners: { "click": "onClick($event)" }, properties: { "class.status-primary": "this.primary", "class.status-info": "this.info", "class.status-success": "this.success", "class.status-warning": "this.warning", "class.status-danger": "this.danger" } }, providers: [
        { provide: CjButton, useExisting: CjButtonComponent },
    ], usesInheritance: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'button[cjButton],a[cjButton],input[type="button"][cjButton],input[type="submit"][cjButton]',
                    template: `
    <ng-content></ng-content>
  `,
                    providers: [
                        { provide: CjButton, useExisting: CjButtonComponent },
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: CjStatusService }]; }, propDecorators: { primary: [{
                type: HostBinding,
                args: ['class.status-primary']
            }], info: [{
                type: HostBinding,
                args: ['class.status-info']
            }], success: [{
                type: HostBinding,
                args: ['class.status-success']
            }], warning: [{
                type: HostBinding,
                args: ['class.status-warning']
            }], danger: [{
                type: HostBinding,
                args: ['class.status-danger']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const CJ_BUTTON_COMPONENTS = [
    CjButtonComponent,
];
class CjButtonModule {
}
CjButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonModule, declarations: [CjButtonComponent], imports: [CjSharedModule], exports: [CjButtonComponent] });
CjButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonModule, imports: [[
            CjSharedModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjSharedModule,
                    ],
                    declarations: [
                        ...CJ_BUTTON_COMPONENTS,
                    ],
                    exports: [
                        ...CJ_BUTTON_COMPONENTS,
                    ],
                }]
        }] });

function isUrlPathEqual(path, link) {
    const locationPath = getPathPartOfUrl(path);
    return link === locationPath;
}
function isUrlPathContain(path, link) {
    const locationPath = getPathPartOfUrl(path);
    const endOfUrlSegmentRegExp = /\/|^$/;
    return locationPath.startsWith(link) &&
        locationPath.slice(link.length).charAt(0).search(endOfUrlSegmentRegExp) !== -1;
}
function getPathPartOfUrl(url) {
    return url.match(/.*?(?=[?;#]|$)/)[0];
}
function getFragmentPartOfUrl(url) {
    const matched = url.match(/#(.+)/);
    return matched ? matched[1] : '';
}
function isFragmentEqual(path, fragment) {
    return getFragmentPartOfUrl(path) === fragment;
}
function isFragmentContain(path, fragment) {
    return getFragmentPartOfUrl(path).includes(fragment);
}

/**
 * This service determines whether we should scroll the layout back to top.
 * This occurs when the page is changed, so when current url PATH is not equal to the previous one.
 *
 *  TODO: this is most likely a temporary solutions as recently Angular introduces ViewportScroll
 *  and scroll restoration process
 */
class CjRestoreScrollTopHelper {
    constructor(router) {
        this.router = router;
    }
    shouldRestore() {
        return this.router.events
            .pipe(startWith(null), filter(event => event === null || event instanceof NavigationEnd), pairwise(), map(([prev, current]) => this.pageChanged(prev, current)), filter(res => !!res));
    }
    pageChanged(prev, current) {
        return !prev || getPathPartOfUrl(prev.url) !== getPathPartOfUrl(current.url);
    }
}
CjRestoreScrollTopHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRestoreScrollTopHelper, deps: [{ token: i6.Router }], target: i0.ɵɵFactoryTarget.Injectable });
CjRestoreScrollTopHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRestoreScrollTopHelper });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRestoreScrollTopHelper, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i6.Router }]; } });

class CjLayoutComponent {
    constructor(themeService, spinnerService, elementRef, renderer, window, document, platformId, layoutDirectionService, scrollService, rulerService, scrollTop, overlayContainer) {
        this.themeService = themeService;
        this.spinnerService = spinnerService;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.window = window;
        this.document = document;
        this.platformId = platformId;
        this.layoutDirectionService = layoutDirectionService;
        this.scrollService = scrollService;
        this.rulerService = rulerService;
        this.scrollTop = scrollTop;
        this.overlayContainer = overlayContainer;
        this.scrollBlockClass = 'cj-global-scrollblock';
        this.isScrollBlocked = false;
        this.centerValue = false;
        this.restoreScrollTopValue = true;
        this.windowModeValue = false;
        this.withScrollValue = false;
        this.afterViewInit$ = new BehaviorSubject(null);
        this.destroy$ = new Subject();
        this.registerAsOverlayContainer();
        this.themeService
            .onThemeChange()
            .pipe(takeUntil(this.destroy$))
            .subscribe((theme) => {
            const body = this.document.getElementsByTagName('body')[0];
            if (theme.previous) {
                this.renderer.removeClass(body, `cj-theme-${theme.previous}`);
            }
            this.renderer.addClass(body, `cj-theme-${theme.name}`);
        });
        this.themeService
            .onAppendLayoutClass()
            .pipe(takeUntil(this.destroy$))
            .subscribe((className) => {
            this.renderer.addClass(this.elementRef.nativeElement, className);
        });
        this.themeService
            .onRemoveLayoutClass()
            .pipe(takeUntil(this.destroy$))
            .subscribe((className) => {
            this.renderer.removeClass(this.elementRef.nativeElement, className);
        });
        this.spinnerService.registerLoader(new Promise((resolve) => {
            this.afterViewInit$.pipe(takeUntil(this.destroy$)).subscribe((_) => resolve());
        }));
        this.spinnerService.load();
        this.rulerService
            .onGetDimensions()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ listener }) => {
            listener.next(this.getDimensions());
            listener.complete();
        });
        this.scrollService
            .onGetPosition()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ listener }) => {
            listener.next(this.getScrollPosition());
            listener.complete();
        });
        this.scrollTop
            .shouldRestore()
            .pipe(filter(() => this.restoreScrollTopValue), takeUntil(this.destroy$))
            .subscribe(() => {
            this.scroll(0, 0);
        });
        this.scrollService
            .onScrollableChange()
            .pipe(filter(() => this.withScrollValue), takeUntil(this.destroy$))
            .subscribe((scrollable) => {
            /**
             * In case when CashJar Ui Layout custom scroll `withScroll` mode is enabled
             * we need to disable default CDK scroll blocker (@link CjBlockScrollStrategyAdapter) on HTML element
             * so that it won't add additional positioning.
             */
            if (scrollable) {
                this.enableScroll();
            }
            else {
                this.blockScroll();
            }
        });
        if (isPlatformBrowser(this.platformId)) {
            // trigger first time so that after the change we have the initial value
            this.themeService.changeWindowWidth(this.window.innerWidth);
        }
    }
    /**
     * Defines whether the layout columns will be centered after some width
     * @param {boolean} val
     */
    set center(val) {
        this.centerValue = convertToBoolProperty(val);
    }
    /**
     * Defines whether the layout enters a 'window' mode, when the layout content (including sidebars and fixed header)
     * becomes centered by width with a margin from the top of the screen, like a floating window.
     * Automatically enables `withScroll` mode, as in the window mode scroll must be inside the layout and cannot be on
     * window. (TODO: check this)
     * @param {boolean} val
     */
    set windowMode(val) {
        this.windowModeValue = convertToBoolProperty(val);
        this.withScroll = this.windowModeValue;
    }
    /**
     * Defines whether to move the scrollbars to layout or leave it at the body level.
     * Automatically set to true when `windowMode` is enabled.
     * @param {boolean} val
     */
    set withScroll(val) {
        this.withScrollValue = convertToBoolProperty(val);
        // TODO: is this the best way of doing it? as we don't have access to body from theme styles
        // TODO: add e2e test
        const body = this.document.getElementsByTagName('body')[0];
        if (this.withScrollValue) {
            this.renderer.setStyle(body, 'overflow', 'hidden');
        }
        else {
            this.renderer.setStyle(body, 'overflow', 'initial');
        }
    }
    /**
     * Restores scroll to the top of the page after navigation
     * @param {boolean} val
     */
    set restoreScrollTop(val) {
        this.restoreScrollTopValue = convertToBoolProperty(val);
    }
    ngAfterViewInit() {
        this.layoutDirectionService
            .onDirectionChange()
            .pipe(takeUntil(this.destroy$))
            .subscribe((direction) => (this.document.dir = direction));
        this.scrollService
            .onManualScroll()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ x, y }) => this.scroll(x, y));
        this.afterViewInit$.next(true);
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.unregisterAsOverlayContainer();
    }
    onScroll($event) {
        this.scrollService.fireScrollChange($event);
    }
    onResize(event) {
        this.themeService.changeWindowWidth(event.target.innerWidth);
    }
    /**
     * Returns scroll and client height/width
     *
     * Depending on the current scroll mode (`withScroll=true`) returns sizes from the body element
     * or from the `.scrollable-container`
     * @returns {CjLayoutDimensions}
     */
    getDimensions() {
        let clientWidth, clientHeight, scrollWidth, scrollHeight = 0;
        if (this.withScrollValue) {
            const container = this.scrollableContainerRef.nativeElement;
            clientWidth = container.clientWidth;
            clientHeight = container.clientHeight;
            scrollWidth = container.scrollWidth;
            scrollHeight = container.scrollHeight;
        }
        else {
            const { documentElement, body } = this.document;
            clientWidth = documentElement.clientWidth || body.clientWidth;
            clientHeight = documentElement.clientHeight || body.clientHeight;
            scrollWidth = documentElement.scrollWidth || body.scrollWidth;
            scrollHeight = documentElement.scrollHeight || body.scrollHeight;
        }
        return {
            clientWidth,
            clientHeight,
            scrollWidth,
            scrollHeight,
        };
    }
    /**
     * Returns scroll position of current scroll container.
     *
     * If `withScroll` = true, returns scroll position of the `.scrollable-container` element,
     * otherwise - of the scrollable element of the window (which may be different depending of a browser)
     *
     * @returns {CjScrollPosition}
     */
    getScrollPosition() {
        if (!isPlatformBrowser(this.platformId)) {
            return { x: 0, y: 0 };
        }
        if (this.withScrollValue) {
            const container = this.scrollableContainerRef.nativeElement;
            return { x: container.scrollLeft, y: container.scrollTop };
        }
        const documentRect = this.document.documentElement.getBoundingClientRect();
        const x = -documentRect.left ||
            this.document.body.scrollLeft ||
            this.window.scrollX ||
            this.document.documentElement.scrollLeft ||
            0;
        const y = -documentRect.top ||
            this.document.body.scrollTop ||
            this.window.scrollY ||
            this.document.documentElement.scrollTop ||
            0;
        return { x, y };
    }
    registerAsOverlayContainer() {
        if (this.overlayContainer.setContainer) {
            this.overlayContainer.setContainer(this.elementRef.nativeElement);
        }
    }
    unregisterAsOverlayContainer() {
        if (this.overlayContainer.clearContainer) {
            this.overlayContainer.clearContainer();
        }
    }
    scroll(x = null, y = null) {
        const { x: currentX, y: currentY } = this.getScrollPosition();
        x = x == null ? currentX : x;
        y = y == null ? currentY : y;
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (this.withScrollValue) {
            const scrollable = this.scrollableContainerRef.nativeElement;
            if (scrollable.scrollTo) {
                scrollable.scrollTo(x, y);
            }
            else {
                scrollable.scrollLeft = x;
                scrollable.scrollTop = y;
            }
        }
        else {
            this.window.scrollTo(x, y);
        }
    }
    // TODO: Extract into block scroll strategy
    blockScroll() {
        if (this.isScrollBlocked) {
            return;
        }
        this.isScrollBlocked = true;
        this.renderer.addClass(this.document.documentElement, this.scrollBlockClass);
        const scrollableContainerElement = this.scrollableContainerRef.nativeElement;
        const layoutElement = this.layoutContainerRef.nativeElement;
        const layoutWithScrollWidth = layoutElement.clientWidth;
        this.scrollableContainerOverflowOldValue = scrollableContainerElement.style.overflow;
        scrollableContainerElement.style.overflow = 'hidden';
        const layoutWithoutScrollWidth = layoutElement.clientWidth;
        const scrollWidth = layoutWithoutScrollWidth - layoutWithScrollWidth;
        if (!scrollWidth) {
            return;
        }
        this.layoutPaddingOldValue = {
            left: layoutElement.style.paddingLeft,
            right: layoutElement.style.paddingRight,
        };
        if (this.layoutDirectionService.isLtr()) {
            layoutElement.style.paddingRight = `${scrollWidth}px`;
        }
        else {
            layoutElement.style.paddingLeft = `${scrollWidth}px`;
        }
    }
    enableScroll() {
        if (this.isScrollBlocked) {
            this.isScrollBlocked = false;
            this.renderer.removeClass(this.document.documentElement, this.scrollBlockClass);
            this.scrollableContainerRef.nativeElement.style.overflow = this.scrollableContainerOverflowOldValue;
            if (this.layoutPaddingOldValue) {
                const layoutElement = this.layoutContainerRef.nativeElement;
                layoutElement.style.paddingLeft = this.layoutPaddingOldValue.left;
                layoutElement.style.paddingRight = this.layoutPaddingOldValue.right;
                this.layoutPaddingOldValue = null;
            }
        }
    }
}
CjLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutComponent, deps: [{ token: CjThemeService }, { token: CjSpinnerService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CJ_WINDOW }, { token: CJ_DOCUMENT }, { token: PLATFORM_ID }, { token: CjLayoutDirectionService }, { token: CjLayoutScrollService }, { token: CjLayoutRulerService }, { token: CjRestoreScrollTopHelper }, { token: CjOverlayContainerAdapter }], target: i0.ɵɵFactoryTarget.Component });
CjLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjLayoutComponent, selector: "cj-layout", inputs: { center: "center", windowMode: "windowMode", withScroll: "withScroll", restoreScrollTop: "restoreScrollTop" }, host: { listeners: { "window:scroll": "onScroll($event)", "window:resize": "onResize($event)" }, properties: { "class.window-mode": "this.windowModeValue", "class.with-scroll": "this.withScrollValue" } }, viewQueries: [{ propertyName: "veryTopRef", first: true, predicate: ["layoutTopDynamicArea"], descendants: true, read: ViewContainerRef }, { propertyName: "scrollableContainerRef", first: true, predicate: ["scrollableContainer"], descendants: true, read: ElementRef, static: true }, { propertyName: "layoutContainerRef", first: true, predicate: ["layoutContainer"], descendants: true, read: ElementRef }], ngImport: i0, template: `
    <div class="scrollable-container" #scrollableContainer (scroll)="onScroll($event)">
      <div class="layout" #layoutContainer>
        <div class="layout-container">
          <ng-content select="cj-sidebar"></ng-content>
          <div class="content" [class.center]="centerValue">
            <ng-content select="cj-layout-header"></ng-content>
            <div class="columns">
              <ng-content select="cj-layout-column"></ng-content>
            </div>
            <ng-content select="cj-layout-footer"></ng-content>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [":host{-webkit-font-smoothing:antialiased}[dir=ltr] :host{text-align:left}[dir=rtl] :host{text-align:right}:host .layout{display:flex;flex-direction:column}:host ::ng-deep cj-layout-header{display:block;width:100%;z-index:1040}:host ::ng-deep cj-layout-header nav{align-items:center;justify-content:flex-start;display:flex}:host ::ng-deep cj-layout-header.sticky{position:sticky;top:0;transition:all ease-in-out .2s}:host ::ng-deep cj-layout-header.sticky.sticky-scroll{-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);box-shadow:0 1px #2d4c6126}:host ::ng-deep cj-layout-header.sticky header{display:flex;align-items:center}:host .layout-container{display:flex;flex:1;-ms-flex:1 1 auto;flex-direction:row}:host .layout-container .content{display:flex;flex:1;-ms-flex:1 1 auto;flex-direction:column;min-width:0}:host .layout-container .content.center{max-width:100%;position:relative;margin-left:auto;margin-right:auto}:host .layout-container .content .columns{display:flex;flex-direction:row;width:100%}:host .layout-container .content .columns ::ng-deep cj-layout-column{order:1;flex:1 0;min-width:0}[dir=ltr] :host .layout-container .content .columns ::ng-deep cj-layout-column.left{order:0}[dir=rtl] :host .layout-container .content .columns ::ng-deep cj-layout-column.left{order:2}:host .layout-container .content .columns ::ng-deep cj-layout-column.start{order:0}:host .layout-container .content ::ng-deep cj-layout-footer{display:block;margin-top:auto}:host .layout-container .content ::ng-deep cj-layout-footer nav{justify-content:center;display:flex}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-layout', template: `
    <div class="scrollable-container" #scrollableContainer (scroll)="onScroll($event)">
      <div class="layout" #layoutContainer>
        <div class="layout-container">
          <ng-content select="cj-sidebar"></ng-content>
          <div class="content" [class.center]="centerValue">
            <ng-content select="cj-layout-header"></ng-content>
            <div class="columns">
              <ng-content select="cj-layout-column"></ng-content>
            </div>
            <ng-content select="cj-layout-footer"></ng-content>
          </div>
        </div>
      </div>
    </div>
  `, styles: [":host{-webkit-font-smoothing:antialiased}[dir=ltr] :host{text-align:left}[dir=rtl] :host{text-align:right}:host .layout{display:flex;flex-direction:column}:host ::ng-deep cj-layout-header{display:block;width:100%;z-index:1040}:host ::ng-deep cj-layout-header nav{align-items:center;justify-content:flex-start;display:flex}:host ::ng-deep cj-layout-header.sticky{position:sticky;top:0;transition:all ease-in-out .2s}:host ::ng-deep cj-layout-header.sticky.sticky-scroll{-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);box-shadow:0 1px #2d4c6126}:host ::ng-deep cj-layout-header.sticky header{display:flex;align-items:center}:host .layout-container{display:flex;flex:1;-ms-flex:1 1 auto;flex-direction:row}:host .layout-container .content{display:flex;flex:1;-ms-flex:1 1 auto;flex-direction:column;min-width:0}:host .layout-container .content.center{max-width:100%;position:relative;margin-left:auto;margin-right:auto}:host .layout-container .content .columns{display:flex;flex-direction:row;width:100%}:host .layout-container .content .columns ::ng-deep cj-layout-column{order:1;flex:1 0;min-width:0}[dir=ltr] :host .layout-container .content .columns ::ng-deep cj-layout-column.left{order:0}[dir=rtl] :host .layout-container .content .columns ::ng-deep cj-layout-column.left{order:2}:host .layout-container .content .columns ::ng-deep cj-layout-column.start{order:0}:host .layout-container .content ::ng-deep cj-layout-footer{display:block;margin-top:auto}:host .layout-container .content ::ng-deep cj-layout-footer nav{justify-content:center;display:flex}\n"] }]
        }], ctorParameters: function () { return [{ type: CjThemeService }, { type: CjSpinnerService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: CjLayoutDirectionService }, { type: CjLayoutScrollService }, { type: CjLayoutRulerService }, { type: CjRestoreScrollTopHelper }, { type: CjOverlayContainerAdapter }]; }, propDecorators: { windowModeValue: [{
                type: HostBinding,
                args: ['class.window-mode']
            }], withScrollValue: [{
                type: HostBinding,
                args: ['class.with-scroll']
            }], center: [{
                type: Input
            }], windowMode: [{
                type: Input
            }], withScroll: [{
                type: Input
            }], restoreScrollTop: [{
                type: Input
            }], veryTopRef: [{
                type: ViewChild,
                args: ['layoutTopDynamicArea', { read: ViewContainerRef }]
            }], scrollableContainerRef: [{
                type: ViewChild,
                args: ['scrollableContainer', { read: ElementRef, static: true }]
            }], layoutContainerRef: [{
                type: ViewChild,
                args: ['layoutContainer', { read: ElementRef }]
            }], onScroll: [{
                type: HostListener,
                args: ['window:scroll', ['$event']]
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });
class CjLayoutColumnComponent {
    /**
     * Move the column to the very left position in the layout.
     * @param {boolean} val
     */
    set left(val) {
        this.leftValue = convertToBoolProperty(val);
        this.startValue = false;
    }
    /**
     * Make column first in the layout.
     * @param {boolean} val
     */
    set start(val) {
        this.startValue = convertToBoolProperty(val);
        this.leftValue = false;
    }
}
CjLayoutColumnComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjLayoutColumnComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjLayoutColumnComponent, selector: "cj-layout-column", inputs: { left: "left", start: "start" }, host: { properties: { "class.left": "this.leftValue", "class.start": "this.startValue" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-layout-column',
                    template: `<ng-content></ng-content>`,
                }]
        }], propDecorators: { leftValue: [{
                type: HostBinding,
                args: ['class.left']
            }], startValue: [{
                type: HostBinding,
                args: ['class.start']
            }], left: [{
                type: Input
            }], start: [{
                type: Input
            }] } });
class CjLayoutHeaderComponent {
    constructor(scrollService, elementRef, renderer) {
        this.scrollService = scrollService;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.destroy$ = new Subject();
        this._sticky = false;
    }
    /**
     * Sets `sticky` header type
     */
    get sticky() {
        return this._sticky;
    }
    set sticky(value) {
        if (this.sticky !== convertToBoolProperty(value)) {
            this._sticky = !this.sticky;
        }
    }
    onScroll() {
        if (this.sticky) {
            this.scrollService.getPosition().subscribe((pos) => {
                if (pos.y > 10) {
                    this.renderer.addClass(this.elementRef.nativeElement, 'sticky-scroll');
                }
                else {
                    this.renderer.removeClass(this.elementRef.nativeElement, 'sticky-scroll');
                }
            });
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
CjLayoutHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutHeaderComponent, deps: [{ token: CjLayoutScrollService }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CjLayoutHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjLayoutHeaderComponent, selector: "cj-layout-header", inputs: { sticky: "sticky" }, host: { listeners: { "window:scroll": "onScroll()" }, properties: { "class.sticky": "this.sticky" } }, ngImport: i0, template: `
    <header>
      <ng-content></ng-content>
    </header>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-layout-header',
                    template: `
    <header>
      <ng-content></ng-content>
    </header>
  `,
                }]
        }], ctorParameters: function () { return [{ type: CjLayoutScrollService }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { sticky: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.sticky']
            }], onScroll: [{
                type: HostListener,
                args: ['window:scroll']
            }] } });
class CjLayoutFooterComponent {
    /**
     * Makes the footer sticky to the bottom of the window.
     * @param {boolean} val
     */
    set fixed(val) {
        this.fixedValue = convertToBoolProperty(val);
    }
}
CjLayoutFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjLayoutFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjLayoutFooterComponent, selector: "cj-layout-footer", inputs: { fixed: "fixed" }, host: { properties: { "class.fixed": "this.fixedValue" } }, ngImport: i0, template: `
    <nav [class.fixed]="fixedValue">
      <ng-content></ng-content>
    </nav>
  `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-layout-footer',
                    template: `
    <nav [class.fixed]="fixedValue">
      <ng-content></ng-content>
    </nav>
  `,
                }]
        }], propDecorators: { fixedValue: [{
                type: HostBinding,
                args: ['class.fixed']
            }], fixed: [{
                type: Input
            }] } });

class CjBaseLayoutDirectionDirective {
    constructor(templateRef, viewContainer, cd, directionService, directionToShow) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.cd = cd;
        this.directionService = directionService;
        this.directionToShow = directionToShow;
        this.destroy$ = new Subject();
    }
    ngOnInit() {
        this.directionService
            .onDirectionChange()
            .pipe(map((layoutDirection) => layoutDirection === this.directionToShow), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((shouldShow) => this.updateView(shouldShow));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    updateView(shouldShow) {
        if (shouldShow && !this.viewContainer.length) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.cd.markForCheck();
        }
        else if (!shouldShow && this.viewContainer.length) {
            this.viewContainer.clear();
        }
    }
}
CjBaseLayoutDirectionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBaseLayoutDirectionDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: CjLayoutDirectionService }, { token: CjLayoutDirection }], target: i0.ɵɵFactoryTarget.Directive });
CjBaseLayoutDirectionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjBaseLayoutDirectionDirective, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBaseLayoutDirectionDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: CjLayoutDirectionService }, { type: CjLayoutDirection }]; } });
/**
 * Apply `cjLtr` directive to the element you need to show only when layout direction is `LTR`.
 *
 * ```html
 * <div *cjLtr>This text is visible only when layout direction is LTR</div>
 * ```
 */
class CjLtrDirective extends CjBaseLayoutDirectionDirective {
    constructor(templateRef, viewContainer, cd, directionService) {
        super(templateRef, viewContainer, cd, directionService, CjLayoutDirection.LTR);
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.cd = cd;
        this.directionService = directionService;
    }
}
CjLtrDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLtrDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Directive });
CjLtrDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjLtrDirective, selector: "[cjLtr]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLtrDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjLtr]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: CjLayoutDirectionService }]; } });
/**
 * Apply `cjRtl` directive to the element you need to show only when layout direction is `RTL`.
 *
 * ```html
 * <div *cjRtl>This text is visible only when layout direction is RTL</div>
 * ```
 */
class CjRtlDirective extends CjBaseLayoutDirectionDirective {
    constructor(templateRef, viewContainer, cd, directionService) {
        super(templateRef, viewContainer, cd, directionService, CjLayoutDirection.RTL);
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.cd = cd;
        this.directionService = directionService;
    }
}
CjRtlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRtlDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Directive });
CjRtlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjRtlDirective, selector: "[cjRtl]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRtlDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjRtl]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: CjLayoutDirectionService }]; } });

const CJ_LAYOUT_COMPONENTS = [
    CjLayoutComponent,
    CjLayoutColumnComponent,
    CjLayoutFooterComponent,
    CjLayoutHeaderComponent,
];
const CJ_LAYOUT_DIRECTIVES = [CjLtrDirective, CjRtlDirective];
class CjLayoutModule {
}
CjLayoutModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjLayoutModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutModule, declarations: [CjLayoutComponent,
        CjLayoutColumnComponent,
        CjLayoutFooterComponent,
        CjLayoutHeaderComponent, CjLtrDirective, CjRtlDirective], imports: [CjSharedModule], exports: [CjLayoutComponent,
        CjLayoutColumnComponent,
        CjLayoutFooterComponent,
        CjLayoutHeaderComponent, CjLtrDirective, CjRtlDirective] });
CjLayoutModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutModule, providers: [CjRestoreScrollTopHelper], imports: [[CjSharedModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjLayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjSharedModule],
                    declarations: [...CJ_LAYOUT_COMPONENTS, ...CJ_LAYOUT_DIRECTIVES],
                    providers: [CjRestoreScrollTopHelper],
                    exports: [...CJ_LAYOUT_COMPONENTS, ...CJ_LAYOUT_DIRECTIVES],
                }]
        }] });

const getSidebarState$ = new Subject();
const getSidebarResponsiveState$ = new Subject();
class CjSidebarService {
    constructor() {
        this.toggle$ = new Subject();
        this.expand$ = new Subject();
        this.collapse$ = new Subject();
        this.compact$ = new Subject();
    }
    /**
     * Subscribe to toggle events
     *
     * @returns Observable<{ compact: boolean, tag: string }>
     */
    onToggle() {
        return this.toggle$.pipe(share());
    }
    /**
     * Subscribe to expand events
     * @returns Observable<{ tag: string }>
     */
    onExpand() {
        return this.expand$.pipe(share());
    }
    /**
     * Subscribe to collapse evens
     * @returns Observable<{ tag: string }>
     */
    onCollapse() {
        return this.collapse$.pipe(share());
    }
    /**
     * Subscribe to compact evens
     * @returns Observable<{ tag: string }>
     */
    onCompact() {
        return this.compact$.pipe(share());
    }
    /**
     * Toggle a sidebar
     * @param {boolean} compact
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar you want to control
     */
    toggle(compact = false, tag) {
        this.toggle$.next({ compact, tag });
    }
    /**
     * Expands a sidebar
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar you want to control
     */
    expand(tag) {
        this.expand$.next({ tag });
    }
    /**
     * Collapses a sidebar
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar you want to control
     */
    collapse(tag) {
        this.collapse$.next({ tag });
    }
    /**
     * Makes sidebar compact
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar you want to control
     */
    compact(tag) {
        this.compact$.next({ tag });
    }
    /**
     * Returns sidebar state observable which emits once
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar state you need
     */
    getSidebarState(tag) {
        const observer = new ReplaySubject(1);
        getSidebarState$.next({ observer, tag });
        return observer.pipe(publish(), refCount());
    }
    /**
     * Returns sidebar state observable which emits once
     * @param {string} tag If you have multiple sidebars on the page, mark them with `tag` input property and pass it here
     * to specify which sidebar responsive state you need
     */
    getSidebarResponsiveState(tag) {
        const observer = new ReplaySubject();
        getSidebarResponsiveState$.next({ observer, tag });
        return observer.pipe(publish(), refCount());
    }
}
CjSidebarService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjSidebarService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarService, decorators: [{
            type: Injectable
        }] });

class CjSidebarHeaderComponent {
}
CjSidebarHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjSidebarHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjSidebarHeaderComponent, selector: "cj-sidebar-header", ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-sidebar-header',
                    template: ` <ng-content></ng-content> `,
                }]
        }] });
class CjSidebarFooterComponent {
}
CjSidebarFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjSidebarFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjSidebarFooterComponent, selector: "cj-sidebar-footer", ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-sidebar-footer',
                    template: ` <ng-content></ng-content> `,
                }]
        }] });
class CjSidebarComponent {
    constructor(sidebarService, themeService, element, cd) {
        this.sidebarService = sidebarService;
        this.themeService = themeService;
        this.element = element;
        this.cd = cd;
        this.responsiveValueChange$ = new Subject();
        this.responsiveState = 'pc';
        this.destroy$ = new Subject();
        this.fixedValue = false;
        this._state = 'expanded';
        this._responsive = false;
        this._showCollapsible = false;
        // TODO: get width by the key and define only max width for the tablets and mobiles
        /**
         * Controls on which screen sizes sidebar should be switched to compacted state.
         * Works only when responsive mode is on.
         * Default values are `['xs', 'is', 'sm', 'md', 'lg']`.
         *
         * @type string[]
         */
        this.compactedBreakpoints = ['xs', 'is', 'sm', 'md', 'lg'];
        /**
         * Controls on which screen sizes sidebar should be switched to collapsed state.
         * Works only when responsive mode is on.
         * Default values are `['xs', 'is']`.
         *
         * @type string[]
         */
        this.collapsedBreakpoints = ['xs', 'is'];
        /**
         * Emits whenever sidebar state change.
         */
        this.stateChange = new EventEmitter();
        /**
         * Emits whenever sidebar responsive state change.
         */
        this.responsiveStateChange = new EventEmitter();
    }
    get expanded() {
        return this.state === 'expanded';
    }
    get collapsed() {
        return this.state === 'collapsed';
    }
    get compacted() {
        return this.state === 'compacted';
    }
    get mobile() {
        return this.responsiveState === 'mobile';
    }
    /**
     * Initial sidebar state, `expanded`|`collapsed`|`compacted`
     * @type {string}
     */
    get state() {
        return this._state;
    }
    set state(value) {
        this._state = value;
    }
    /**
     * Makes sidebar listen to media query events and change its behaviour
     * @type {boolean}
     */
    get responsive() {
        return this._responsive;
    }
    set responsive(value) {
        if (this.responsive !== convertToBoolProperty(value)) {
            this._responsive = !this.responsive;
            this.responsiveValueChange$.next(this.responsive);
        }
    }
    /**
     * Show collapse toggle button
     * @type {boolean}
     */
    get showCollapsible() {
        return this._showCollapsible;
    }
    set showCollapsible(value) {
        if (this.showCollapsible !== convertToBoolProperty(value)) {
            this._showCollapsible = !this.showCollapsible;
        }
    }
    ngOnInit() {
        this.sidebarService
            .onToggle()
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(({ compact }) => this.toggle(compact));
        this.sidebarService
            .onExpand()
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(() => this.expand());
        this.sidebarService
            .onCollapse()
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(() => this.collapse());
        this.sidebarService
            .onCompact()
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(() => this.compact());
        getSidebarState$
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(({ observer }) => observer.next(this.state));
        getSidebarResponsiveState$
            .pipe(filter(({ tag }) => !this.tag || this.tag === tag), takeUntil(this.destroy$))
            .subscribe(({ observer }) => observer.next(this.responsiveState));
        this.responsiveValueChange$
            .pipe(filter((responsive) => !responsive), takeUntil(this.destroy$))
            .subscribe(() => this.expand());
        this.subscribeToMediaQueryChange();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    // TODO: this is more of a workaround, should be a better way to make components communicate to each other
    onClick(event) {
        const menu = this.element.nativeElement.querySelector('cj-menu');
        if (menu && menu.contains(event.target)) {
            const link = this.getMenuLink(event.target);
            if (link && link.nextElementSibling && link.nextElementSibling.classList.contains('menu-items')) {
                this.sidebarService.expand(this.tag);
            }
        }
    }
    onCollapse() {
        if (!this.mobile) {
            this.fixedValue = !this.fixedValue;
            if (!this.fixedValue) {
                this.expand();
            }
            else {
                this.compact();
            }
        }
        else {
            if (this.collapsed) {
                this.expand();
            }
            else {
                this.collapse();
            }
        }
    }
    onHover() {
        if (this.fixedValue && !this.mobile) {
            this.expand();
        }
    }
    onBlur() {
        if (this.fixedValue && !this.mobile) {
            this.compact();
        }
    }
    /**
     * Collapses the sidebar
     */
    collapse() {
        this.updateState('collapsed');
    }
    /**
     * Expands the sidebar
     */
    expand() {
        this.updateState('expanded');
    }
    /**
     * Compacts the sidebar (minimizes)
     */
    compact() {
        this.updateState('compacted');
    }
    /**
     * Toggles sidebar state (expanded|collapsed|compacted)
     * @param {boolean} compact If true, then sidebar state will be changed between expanded & compacted,
     * otherwise - between expanded & collapsed. False by default.
     *
     * Toggle sidebar state
     *
     * ```ts
     * this.sidebar.toggle(true);
     * ```
     */
    toggle(compact = false) {
        if (this.responsive) {
            if (this.responsiveState === 'mobile') {
                compact = false;
            }
        }
        if (this.state === 'compacted' || this.state === 'collapsed') {
            this.updateState('expanded');
        }
        else {
            this.updateState(compact ? 'compacted' : 'collapsed');
        }
    }
    subscribeToMediaQueryChange() {
        combineLatest([
            this.responsiveValueChange$.pipe(startWith(this.responsive)),
            this.themeService.onMediaQueryChange(),
        ])
            .pipe(filter(([responsive]) => responsive), map(([, breakpoints]) => breakpoints), takeUntil(this.destroy$))
            .subscribe(([prev, current]) => {
            const isCollapsed = this.collapsedBreakpoints.includes(current.name);
            const isCompacted = this.compactedBreakpoints.includes(current.name);
            let newResponsiveState;
            if (isCompacted) {
                this.compact();
                newResponsiveState = 'tablet';
            }
            if (isCollapsed) {
                this.collapse();
                this.fixedValue = true;
                newResponsiveState = 'mobile';
            }
            if (!isCollapsed && !isCompacted && (!prev.width || prev.width < current.width)) {
                this.expand();
                this.fixedValue = false;
                newResponsiveState = 'pc';
            }
            if (newResponsiveState && newResponsiveState !== this.responsiveState) {
                this.responsiveState = newResponsiveState;
                this.responsiveStateChange.emit(this.responsiveState);
                this.cd.markForCheck();
            }
        });
    }
    getMenuLink(element) {
        if (!element || element.tagName.toLowerCase() === 'cj-menu') {
            return undefined;
        }
        if (element.tagName.toLowerCase() === 'a') {
            return element;
        }
        return this.getMenuLink(element.parentElement);
    }
    updateState(state) {
        if (this.state !== state) {
            this.state = state;
            this.stateChange.emit(this.state);
            this.cd.markForCheck();
        }
    }
}
CjSidebarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarComponent, deps: [{ token: CjSidebarService }, { token: CjThemeService }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CjSidebarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjSidebarComponent, selector: "cj-sidebar", inputs: { state: "state", responsive: "responsive", showCollapsible: "showCollapsible", tag: "tag", compactedBreakpoints: "compactedBreakpoints", collapsedBreakpoints: "collapsedBreakpoints" }, outputs: { stateChange: "stateChange", responsiveStateChange: "responsiveStateChange" }, host: { properties: { "class.fixed": "this.fixedValue", "class.expanded": "this.expanded", "class.collapsed": "this.collapsed", "class.compacted": "this.compacted", "class.mobile": "this.mobile" } }, ngImport: i0, template: `
    <nav (mouseenter)="onHover()" (mouseleave)="onBlur()">
      <div class="nav-container">
        <div class="nav-content-wrapper">
          <div class="nav-content">
            <ng-content select="cj-sidebar-header"></ng-content>
            <div class="scrollable" (click)="onClick($event)">
              <ng-content></ng-content>
            </div>
            <ng-content select="cj-sidebar-footer"></ng-content>
          </div>
        </div>
      </div>

      <ng-container *ngIf="showCollapsible && state === 'expanded'">
        <button cjButton status="" class="collapse-btn" (click)="collapse()">
          <cj-icon icon="close-outline"></cj-icon>
        </button>
      </ng-container>
    </nav>
  `, isInline: true, styles: [":host{position:fixed;height:100%;top:0;bottom:0;left:0;z-index:1050;order:0}:host .scrollable{overflow-y:auto;overflow-x:hidden;flex:1;height:100%}:host .nav-container{transform:translateZ(0);display:flex;flex-direction:column;overflow:hidden}:host .nav-container .nav-content{display:flex;flex-direction:column}:host .collapse-btn{position:absolute;right:1rem;top:1rem;padding:0!important;border:none!important;background-color:transparent!important}:host .collapse-btn:hover{background-color:#cbe0fb!important}:host .collapse-btn:active{background-color:#d0d7f1!important}:host .collapse-btn cj-icon{width:1.5rem!important;height:1.5rem!important}:host ::ng-deep cj-sidebar-footer{margin-top:auto;display:block;overflow-y:auto;overflow-x:hidden}:host ::ng-deep cj-sidebar-header{display:block;white-space:nowrap;overflow-y:auto;overflow-x:hidden}\n"], components: [{ type: CjButtonComponent, selector: "button[cjButton],a[cjButton],input[type=\"button\"][cjButton],input[type=\"submit\"][cjButton]" }, { type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-sidebar', template: `
    <nav (mouseenter)="onHover()" (mouseleave)="onBlur()">
      <div class="nav-container">
        <div class="nav-content-wrapper">
          <div class="nav-content">
            <ng-content select="cj-sidebar-header"></ng-content>
            <div class="scrollable" (click)="onClick($event)">
              <ng-content></ng-content>
            </div>
            <ng-content select="cj-sidebar-footer"></ng-content>
          </div>
        </div>
      </div>

      <ng-container *ngIf="showCollapsible && state === 'expanded'">
        <button cjButton status="" class="collapse-btn" (click)="collapse()">
          <cj-icon icon="close-outline"></cj-icon>
        </button>
      </ng-container>
    </nav>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{position:fixed;height:100%;top:0;bottom:0;left:0;z-index:1050;order:0}:host .scrollable{overflow-y:auto;overflow-x:hidden;flex:1;height:100%}:host .nav-container{transform:translateZ(0);display:flex;flex-direction:column;overflow:hidden}:host .nav-container .nav-content{display:flex;flex-direction:column}:host .collapse-btn{position:absolute;right:1rem;top:1rem;padding:0!important;border:none!important;background-color:transparent!important}:host .collapse-btn:hover{background-color:#cbe0fb!important}:host .collapse-btn:active{background-color:#d0d7f1!important}:host .collapse-btn cj-icon{width:1.5rem!important;height:1.5rem!important}:host ::ng-deep cj-sidebar-footer{margin-top:auto;display:block;overflow-y:auto;overflow-x:hidden}:host ::ng-deep cj-sidebar-header{display:block;white-space:nowrap;overflow-y:auto;overflow-x:hidden}\n"] }]
        }], ctorParameters: function () { return [{ type: CjSidebarService }, { type: CjThemeService }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { fixedValue: [{
                type: HostBinding,
                args: ['class.fixed']
            }], expanded: [{
                type: HostBinding,
                args: ['class.expanded']
            }], collapsed: [{
                type: HostBinding,
                args: ['class.collapsed']
            }], compacted: [{
                type: HostBinding,
                args: ['class.compacted']
            }], mobile: [{
                type: HostBinding,
                args: ['class.mobile']
            }], state: [{
                type: Input
            }], responsive: [{
                type: Input
            }], showCollapsible: [{
                type: Input
            }], tag: [{
                type: Input
            }], compactedBreakpoints: [{
                type: Input
            }], collapsedBreakpoints: [{
                type: Input
            }], stateChange: [{
                type: Output
            }], responsiveStateChange: [{
                type: Output
            }] } });

class CjIconModule {
    constructor(iconsLibrary) {
        this.iconsLibrary = iconsLibrary;
        this.essentialsPackName = 'cashjar-essentials';
        this.gradientsPackName = 'cashjar-gradients';
        this.fontAwesomeDuotonePackName = 'font-awesome-duotone';
        if (this.iconsLibrary.getPack(this.essentialsPackName) ||
            this.iconsLibrary.getPack(this.gradientsPackName) ||
            this.iconsLibrary.getPack(this.fontAwesomeDuotonePackName)) {
            return;
        }
        /* eslint-disable max-len */
        this.iconsLibrary.registerSvgPack(this.essentialsPackName, {
            'collapse-backdrop': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="26" height="43" viewBox="0 0 26 43"><path d="M0 12C0 9.79086 1.79086 8 4 8H8V0L12.4853 4.48528C14.7357 6.73572 17.788 8 20.9706 8H22C24.2091 8 26 9.79086 26 12V31C26 33.2091 24.2091 35 22 35H20.9706C17.788 35 14.7357 36.2643 12.4853 38.5147L8 43V35H4C1.79086 35 0 33.2091 0 31V12Z"/></svg>',
            'chevron-down-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="chevron-down"><rect width="24" height="24" opacity="0"/><path d="M12 15.5a1 1 0 0 1-.71-.29l-4-4a1 1 0 1 1 1.42-1.42L12 13.1l3.3-3.18a1 1 0 1 1 1.38 1.44l-4 3.86a1 1 0 0 1-.68.28z"/></g></g></svg>',
            'chevron-up-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="chevron-up"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M16 14.5a1 1 0 0 1-.71-.29L12 10.9l-3.3 3.18a1 1 0 0 1-1.41 0 1 1 0 0 1 0-1.42l4-3.86a1 1 0 0 1 1.4 0l4 4a1 1 0 0 1 0 1.42 1 1 0 0 1-.69.28z"/></g></g></svg>',
            'chevron-left-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="chevron-left"><rect width="24" height="24" transform="rotate(90 12 12)" opacity="0"/><path d="M13.36 17a1 1 0 0 1-.72-.31l-3.86-4a1 1 0 0 1 0-1.4l4-4a1 1 0 1 1 1.42 1.42L10.9 12l3.18 3.3a1 1 0 0 1 0 1.41 1 1 0 0 1-.72.29z"/></g></g></svg>',
            'chevron-right-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="chevron-right"><rect width="24" height="24" transform="rotate(-90 12 12)" opacity="0"/><path d="M10.5 17a1 1 0 0 1-.71-.29 1 1 0 0 1 0-1.42L13.1 12 9.92 8.69a1 1 0 0 1 0-1.41 1 1 0 0 1 1.42 0l3.86 4a1 1 0 0 1 0 1.4l-4 4a1 1 0 0 1-.7.32z"/></g></g></svg>',
            'checkmark-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="checkmark"><rect width="24" height="24" opacity="0"/><path d="M9.86 18a1 1 0 0 1-.73-.32l-4.86-5.17a1 1 0 1 1 1.46-1.37l4.12 4.39 8.41-9.2a1 1 0 1 1 1.48 1.34l-9.14 10a1 1 0 0 1-.73.33z"/></g></g></svg>',
            'paper-plane-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="paper-plane"><rect width="24" height="24" opacity="0"/><path d="M21 4a1.31 1.31 0 0 0-.06-.27v-.09a1 1 0 0 0-.2-.3 1 1 0 0 0-.29-.19h-.09a.86.86 0 0 0-.31-.15H20a1 1 0 0 0-.3 0l-18 6a1 1 0 0 0 0 1.9l8.53 2.84 2.84 8.53a1 1 0 0 0 1.9 0l6-18A1 1 0 0 0 21 4zm-4.7 2.29l-5.57 5.57L5.16 10zM14 18.84l-1.86-5.57 5.57-5.57z"/></g></g></svg>',
            'file-text-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="file-text"><rect width="24" height="24" opacity="0"/><path d="M15 16H9a1 1 0 0 0 0 2h6a1 1 0 0 0 0-2z"/><path d="M9 14h3a1 1 0 0 0 0-2H9a1 1 0 0 0 0 2z"/><path d="M19.74 8.33l-5.44-6a1 1 0 0 0-.74-.33h-7A2.53 2.53 0 0 0 4 4.5v15A2.53 2.53 0 0 0 6.56 22h10.88A2.53 2.53 0 0 0 20 19.5V9a1 1 0 0 0-.26-.67zM14 5l2.74 3h-2a.79.79 0 0 1-.74-.85zm3.44 15H6.56a.53.53 0 0 1-.56-.5v-15a.53.53 0 0 1 .56-.5H12v3.15A2.79 2.79 0 0 0 14.71 10H18v9.5a.53.53 0 0 1-.56.5z"/></g></g></svg>',
            'alert-triangle-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="alert-triangle"><rect width="24" height="24" transform="rotate(90 12 12)" opacity="0"/><path d="M22.56 16.3L14.89 3.58a3.43 3.43 0 0 0-5.78 0L1.44 16.3a3 3 0 0 0-.05 3A3.37 3.37 0 0 0 4.33 21h15.34a3.37 3.37 0 0 0 2.94-1.66 3 3 0 0 0-.05-3.04zm-1.7 2.05a1.31 1.31 0 0 1-1.19.65H4.33a1.31 1.31 0 0 1-1.19-.65 1 1 0 0 1 0-1l7.68-12.73a1.48 1.48 0 0 1 2.36 0l7.67 12.72a1 1 0 0 1 .01 1.01z"/><circle cx="12" cy="16" r="1"/><path d="M12 8a1 1 0 0 0-1 1v4a1 1 0 0 0 2 0V9a1 1 0 0 0-1-1z"/></g></g></svg>',
            'question-mark-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="question-mark"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M17 9A5 5 0 0 0 7 9a1 1 0 0 0 2 0 3 3 0 1 1 3 3 1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-1.1A5 5 0 0 0 17 9z"/><circle cx="12" cy="19" r="1"/></g></g></svg>',
            'email-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="email"><rect width="24" height="24" opacity="0"/><path d="M19 4H5a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3zm-.67 2L12 10.75 5.67 6zM19 18H5a1 1 0 0 1-1-1V7.25l7.4 5.55a1 1 0 0 0 .6.2 1 1 0 0 0 .6-.2L20 7.25V17a1 1 0 0 1-1 1z"/></g></g></svg>',
            'flash-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="flash"><rect width="24" height="24" opacity="0"/><path d="M11.11 23a1 1 0 0 1-.34-.06 1 1 0 0 1-.65-1.05l.77-7.09H5a1 1 0 0 1-.83-1.56l7.89-11.8a1 1 0 0 1 1.17-.38 1 1 0 0 1 .65 1l-.77 7.14H19a1 1 0 0 1 .83 1.56l-7.89 11.8a1 1 0 0 1-.83.44zM6.87 12.8H12a1 1 0 0 1 .74.33 1 1 0 0 1 .25.78l-.45 4.15 4.59-6.86H12a1 1 0 0 1-1-1.11l.45-4.15z"/></g></g></svg>',
            'search-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="search"><rect width="24" height="24" opacity="0"/><path d="M20.71 19.29l-3.4-3.39A7.92 7.92 0 0 0 19 11a8 8 0 1 0-8 8 7.92 7.92 0 0 0 4.9-1.69l3.39 3.4a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.42zM5 11a6 6 0 1 1 6 6 6 6 0 0 1-6-6z"/></g></g></svg>',
            'close-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="close"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M13.41 12l4.3-4.29a1 1 0 1 0-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 0 0-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0l4.29-4.3 4.29 4.3a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.42z"/></g></g></svg>',
            'collapse-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="collapse"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M19 9h-2.58l3.29-3.29a1 1 0 1 0-1.42-1.42L15 7.57V5a1 1 0 0 0-1-1 1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h5a1 1 0 0 0 0-2z"/><path d="M10 13H5a1 1 0 0 0 0 2h2.57l-3.28 3.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0L9 16.42V19a1 1 0 0 0 1 1 1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1z"/></g></g></svg>',
            'expand-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="expand"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M20 5a1 1 0 0 0-1-1h-5a1 1 0 0 0 0 2h2.57l-3.28 3.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0L18 7.42V10a1 1 0 0 0 1 1 1 1 0 0 0 1-1z"/><path d="M10.71 13.29a1 1 0 0 0-1.42 0L6 16.57V14a1 1 0 0 0-1-1 1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h5a1 1 0 0 0 0-2H7.42l3.29-3.29a1 1 0 0 0 0-1.42z"/></g></g></svg>',
            'minus-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="minus"><rect width="24" height="24" transform="rotate(180 12 12)" opacity="0"/><path d="M19 13H5a1 1 0 0 1 0-2h14a1 1 0 0 1 0 2z"/></g></g></svg>',
            'minus-bold-outline': '<svg xmlns:xlink="http://www.w3.org/1999/xlink" fill="currentColor" viewBox="0 0 8 2" width="100%" height="100%"><defs><rect id="nb-mbo" y="3" width="8" height="2" rx="1"/></defs><use xlink:href="#nb-mbo" transform="translate(0 -3)" fill-rule="evenodd"/></svg>',
            'checkmark-bold-outline': '<svg xmlns:xlink="http://www.w3.org/1999/xlink" fill="currentColor" viewBox="0 0 8 7" width="100%" height="100%"><defs><path id="nb-cbo" d="M6.039 1.43a1.11 1.11 0 0 1 1.517-.228c.483.342.588.998.234 1.466L4.431 7.1a1 1 0 0 1-1.492.115L.317 4.677a1.023 1.023 0 0 1 .002-1.483 1.113 1.113 0 0 1 1.535.002l1.641 1.59L6.04 1.428z"/></defs><use xlink:href="#nb-cbo" transform="translate(0 -1)" fill-rule="evenodd"/></svg>',
            'arrow-back': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24"><g data-name="Layer 2"><g data-name="arrow-back"><rect width="24" height="24" transform="rotate(90 12 12)" opacity="0"/><path d="M19 11H7.14l3.63-4.36a1 1 0 1 0-1.54-1.28l-5 6a1.19 1.19 0 0 0-.09.15c0 .05 0 .08-.07.13A1 1 0 0 0 4 12a1 1 0 0 0 .07.36c0 .05 0 .08.07.13a1.19 1.19 0 0 0 .09.15l5 6A1 1 0 0 0 10 19a1 1 0 0 0 .64-.23 1 1 0 0 0 .13-1.41L7.14 13H19a1 1 0 0 0 0-2z"/></g></g></svg>',
            'checkmark': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 16 16"><path d="M5.43431 13.7316L0.234305 8.53157C-0.0781015 8.21917 -0.0781015 7.71264 0.234305 7.40024L1.36565 6.26885C1.67805 5.95641 2.18462 5.95641 2.49703 6.26885L6 9.77177L13.503 2.26885C13.8154 1.95644 14.3219 1.95644 14.6343 2.26885L15.7657 3.40022C16.0781 3.71263 16.0781 4.21916 15.7657 4.5316L6.56569 13.7316C6.25325 14.044 5.74671 14.044 5.43431 13.7316Z"/></svg>',
            'plus-outline': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 18 18"><path d="M9 0.818176L9 17.1818" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M17.1819 9L0.818248 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
        });
        this.iconsLibrary.registerSvgPack(this.gradientsPackName, {
            'house': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M22.6484 11.4844C23.0391 11.8359 23.0781 12.4219 22.7656 12.8125C22.4141 13.2031 21.8281 13.2422 21.4375 12.9297L20.5 12.1094V19.375C20.5 21.1328 19.0938 22.5 17.375 22.5H6.125C4.36719 22.5 3 21.1328 3 19.375V12.1094L2.02344 12.9297C1.63281 13.2422 1.04688 13.2031 0.695312 12.8125C0.382812 12.4219 0.421875 11.8359 0.8125 11.4844L11.125 2.73438C11.4766 2.46094 11.9844 2.46094 12.3359 2.73438L22.6484 11.4844ZM6.125 20.625H8V14.6875C8 13.8281 8.66406 13.125 9.5625 13.125H13.9375C14.7969 13.125 15.5 13.8281 15.5 14.6875V20.625H17.375C18.0391 20.625 18.625 20.0781 18.625 19.375V10.5078L11.75 4.6875L4.875 10.5078V19.375C4.875 20.0781 5.42188 20.625 6.125 20.625ZM9.875 20.625H13.625V15H9.875V20.625Z"/></svg>',
            'capital': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M12.7812 11.5234C13.0156 11.5625 13.4062 11.6406 13.6016 11.6797C13.9922 11.7969 14.2656 12.2266 14.1484 12.6562C14.0312 13.0469 13.6016 13.3203 13.1719 13.2031C13.0547 13.1641 12.5078 13.0469 12.3516 13.0078C11.8438 12.9688 11.4531 13.0078 11.2188 13.125C10.9844 13.2422 10.9062 13.3594 10.9062 13.4375C10.8672 13.5938 10.9062 13.6328 10.9062 13.6328C10.9062 13.6719 10.9453 13.7109 11.0625 13.7891C11.2969 13.9453 11.6484 14.0625 12.1953 14.2188H12.2344C12.7031 14.375 13.3672 14.5703 13.8359 14.9219C14.0703 15.1172 14.3438 15.3516 14.5 15.7031C14.6562 16.0547 14.6562 16.4453 14.6172 16.875C14.4609 17.6172 13.9922 18.125 13.3672 18.3594C13.1719 18.4375 12.9766 18.5156 12.7812 18.5547V18.75C12.7812 19.2188 12.4297 19.5703 11.9609 19.5703C11.5312 19.5703 11.1797 19.2188 11.1797 18.75V18.5156C10.8281 18.4375 10.3203 18.2812 10.0469 18.1641C9.96875 18.125 9.89062 18.125 9.85156 18.0859C9.46094 17.9688 9.22656 17.5391 9.38281 17.1094C9.5 16.7188 9.92969 16.4844 10.3594 16.6016C10.4375 16.6406 10.5156 16.6797 10.5938 16.7188C10.9844 16.8359 11.375 16.9531 11.6094 16.9922C12.1172 17.0703 12.5078 17.0312 12.7422 16.9531C12.9766 16.8359 13.0547 16.7188 13.0547 16.6016C13.0938 16.4453 13.0547 16.3672 13.0547 16.3281C13.0547 16.2891 13.0156 16.25 12.8984 16.1719C12.6641 16.0156 12.3125 15.8984 11.7656 15.7031H11.6875C11.2188 15.5469 10.6328 15.3906 10.1641 15.0781C9.92969 14.8828 9.65625 14.6484 9.5 14.2969C9.34375 13.9453 9.30469 13.5547 9.34375 13.1641C9.5 12.4609 10.0078 11.9531 10.5938 11.7188C10.75 11.6016 10.9844 11.5625 11.1797 11.4844V11.25C11.1797 10.8203 11.5312 10.4688 12 10.4688C12.4297 10.4688 12.7812 10.8203 12.7812 11.25V11.5234ZM8.99219 7.22656L6.72656 3.98438C6.29688 3.35938 6.76562 2.5 7.50781 2.5H16.4531C17.1953 2.5 17.6641 3.35938 17.2344 3.98438L14.9688 7.22656C15.3203 7.42188 15.6719 7.69531 16.1016 7.96875C18.4844 9.76562 22 13.2422 22 18.75C22 20.8203 20.3203 22.5 18.25 22.5H5.75C3.64062 22.5 2 20.8203 2 18.75C2 13.2422 5.47656 9.76562 7.85938 7.96875C8.28906 7.69531 8.64062 7.42188 8.99219 7.22656ZM9.96875 8.78906C7.85938 10.1562 3.875 13.3203 3.875 18.75C3.875 19.8047 4.69531 20.625 5.75 20.625H18.25C19.2656 20.625 20.125 19.8047 20.125 18.75C20.125 13.3203 16.1016 10.1562 13.9922 8.78906L13.4453 8.4375H10.5156L9.96875 8.78906ZM13.1328 6.5625L14.6562 4.375H9.30469L10.8281 6.5625H13.1328Z"/></svg>',
            'customers': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M17 2.5H7C5.59375 2.5 4.5 3.63281 4.5 5V20C4.5 21.4062 5.59375 22.5 7 22.5H17C18.3672 22.5 19.5 21.4062 19.5 20V5C19.5 3.63281 18.3672 2.5 17 2.5ZM17.625 20C17.625 20.3516 17.3125 20.625 17 20.625H7C6.64844 20.625 6.375 20.3516 6.375 20V5C6.375 4.6875 6.64844 4.375 7 4.375H9.5V5C9.5 5.70312 10.0469 6.25 10.75 6.25H13.25C13.9141 6.25 14.5 5.70312 14.5 5V4.375H17C17.3125 4.375 17.625 4.6875 17.625 5V20ZM12 13.75C13.3672 13.75 14.5 12.6562 14.5 11.25C14.5 9.88281 13.3672 8.75 12 8.75C10.5938 8.75 9.5 9.88281 9.5 11.25C9.5 12.6562 10.5938 13.75 12 13.75ZM13.25 15H10.75C8.99219 15 7.625 16.4062 7.625 18.125C7.625 18.4766 7.89844 18.75 8.25 18.75H15.75C16.0625 18.75 16.375 18.4766 16.375 18.125C16.375 16.4062 14.9688 15 13.25 15Z"/></svg>',
            'metric': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M3.875 18.4375C3.875 18.9844 4.26562 19.375 4.8125 19.375H21.0625C21.5703 19.375 22 19.8047 22 20.3125C22 20.8594 21.5703 21.25 21.0625 21.25H4.8125C3.25 21.25 2 20 2 18.4375V4.6875C2 4.17969 2.39062 3.75 2.9375 3.75C3.44531 3.75 3.875 4.17969 3.875 4.6875V18.4375ZM15.125 14.4141C14.7734 14.8047 14.1875 14.8047 13.8359 14.4141L10.7109 11.3281L7.625 14.4141C7.27344 14.8047 6.6875 14.8047 6.33594 14.4141C5.94531 14.0625 5.94531 13.4766 6.33594 13.0859L10.0859 9.33594C10.2422 9.17969 10.4766 9.0625 10.7109 9.0625C10.9844 9.0625 11.2188 9.17969 11.375 9.33594L14.5 12.4609L19.1484 7.77344C19.5 7.42188 20.0859 7.42188 20.4375 7.77344C20.8281 8.16406 20.8281 8.75 20.4375 9.10156L15.125 14.4141Z"/></svg>',
            'payroll': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M22.0234 14.7266C20.7734 13.6719 19.2891 14.0625 18.5469 14.6484L16.2031 16.4062H16.0469C16.0859 14.9219 14.9141 13.75 13.3906 13.75H7.76562C6.67188 13.75 5.57812 14.1406 4.71875 14.8047L3.58594 15.6641H1.4375C0.890625 15.6641 0.5 16.0547 0.5 16.6016C0.5 17.1094 0.890625 17.5391 1.4375 17.5391H4.25L5.85156 16.2891C6.39844 15.8594 7.0625 15.6641 7.76562 15.6641H13.3906C14.4844 15.6641 14.5234 17.2266 13.3516 17.2266H11.0078C10.7344 17.2266 10.5 17.4609 10.5 17.7734C10.5 18.0469 10.7344 18.2812 11.0078 18.2812H16.2812C16.6719 18.2812 17.0234 18.1641 17.3359 17.9297L19.7188 16.1328C20.0312 15.8984 20.5391 15.8984 20.8125 16.1328C21.2422 16.4844 21.2031 17.0312 20.8125 17.3438L16.8672 20.3125C16.5547 20.5078 16.2031 20.6641 15.8125 20.6641H1.4375C0.890625 20.625 0.5 21.0547 0.5 21.5625C0.5 22.1094 0.890625 22.5 1.4375 22.5H15.8125C16.5938 22.5 17.375 22.2656 18 21.7969L21.9062 18.8281C22.5703 18.3594 22.9609 17.6172 22.9609 16.7969C23 16.0156 22.6484 15.2344 22.0234 14.7266ZM11.3984 9.33594C11.0859 9.29688 10.6953 9.17969 10.3438 9.0625L10.1875 8.98438C9.67969 8.78906 9.13281 9.0625 8.97656 9.53125C8.78125 10.0391 9.05469 10.5859 9.52344 10.7422L9.67969 10.8203C10.0312 10.9375 10.3828 11.0547 10.7734 11.1328V11.5625C10.7734 12.1094 11.1641 12.5 11.7109 12.5C12.2188 12.5 12.6484 12.1094 12.6484 11.5625V11.1719C13.625 10.9375 14.3281 10.3125 14.4453 9.375C14.7578 7.42188 12.9219 6.91406 11.9062 6.60156L11.75 6.5625C10.7734 6.25 10.7734 6.13281 10.8125 5.97656C10.8516 5.70312 11.3984 5.58594 12.0625 5.66406C12.2578 5.70312 12.4922 5.78125 12.8438 5.89844C13.3125 6.05469 13.8594 5.82031 14.0156 5.3125C14.1719 4.80469 13.9375 4.29688 13.4297 4.10156C13.1562 4.02344 12.8828 3.94531 12.6875 3.90625V3.4375C12.6875 2.92969 12.2578 2.5 11.75 2.5C11.2031 2.5 10.8125 2.92969 10.8125 3.4375V3.86719C9.79688 4.10156 9.13281 4.72656 8.9375 5.66406C8.625 7.57812 10.5 8.125 11.2031 8.35938L11.4766 8.4375C12.7266 8.78906 12.6875 8.86719 12.6484 9.0625C12.6094 9.33594 12.0625 9.45312 11.3984 9.33594Z"/></svg>',
            'pmplus': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M20.925 4.38125L20.0625 3.51875C19.6875 3.18125 19.6875 2.61875 20.0625 2.28125C20.4 1.90625 20.9625 1.90625 21.3 2.28125L23.7 4.68125C23.8875 4.83125 24 5.05625 24 5.28125C24 5.54375 23.8875 5.76875 23.7 5.91875L21.3 8.31875C20.9625 8.69375 20.4 8.69375 20.0625 8.31875C19.6875 7.98125 19.6875 7.41875 20.0625 7.04375L20.925 6.18125H6V6.78125C6 8.13125 4.9125 9.18125 3.6 9.18125H3V12.7063L1.2 14.5063V6.78125C1.2 5.46875 2.25 4.38125 3.6 4.38125H14.3625H14.4H20.925ZM21 13.9813V10.4938L22.8 8.69375V16.3813C22.8 17.7313 21.7125 18.7813 20.4 18.7813H3.0375L3.9 19.6438C4.275 20.0188 4.275 20.5813 3.9 20.9188C3.5625 21.2938 3 21.2938 2.6625 20.9188L0.2625 18.5188C0.075 18.3688 0 18.1438 0 17.8813C0 17.6563 0.075 17.4313 0.2625 17.2438L2.6625 14.8438C3 14.5063 3.5625 14.5063 3.9 14.8438C4.275 15.2188 4.275 15.7813 3.9 16.1188L3.0375 16.9813H9.5625C9.6 16.9813 9.6 16.9813 9.6 16.9813H18V16.3813C18 15.0688 19.05 13.9813 20.4 13.9813H21ZM8.4 11.5813C8.4 9.59375 9.975 7.98125 12 7.98125C13.9875 7.98125 15.6 9.59375 15.6 11.5813C15.6 13.5688 13.9875 15.1813 12 15.1813C9.975 15.1813 8.4 13.5688 8.4 11.5813Z"/></svg>',
            'pos': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M7.0625 15.625C7.57031 15.625 8 16.0547 8 16.5625C8 17.1094 7.57031 17.5 7.0625 17.5H5.1875C4.64062 17.5 4.25 17.1094 4.25 16.5625C4.25 16.0547 4.64062 15.625 5.1875 15.625H7.0625ZM14.5625 15.625C15.0703 15.625 15.5 16.0547 15.5 16.5625C15.5 17.1094 15.0703 17.5 14.5625 17.5H10.1875C9.64062 17.5 9.25 17.1094 9.25 16.5625C9.25 16.0547 9.64062 15.625 10.1875 15.625H14.5625ZM20.5 3.75C21.8672 3.75 23 4.88281 23 6.25V18.75C23 20.1562 21.8672 21.25 20.5 21.25H3C1.59375 21.25 0.5 20.1562 0.5 18.75V6.25C0.5 4.88281 1.59375 3.75 3 3.75H20.5ZM20.5 5.625H3C2.64844 5.625 2.375 5.9375 2.375 6.25V7.5H21.125V6.25C21.125 5.9375 20.8125 5.625 20.5 5.625ZM21.125 11.25H2.375V18.75C2.375 19.1016 2.64844 19.375 3 19.375H20.5C20.8125 19.375 21.125 19.1016 21.125 18.75V11.25Z"/></svg>',
            'products': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M15.5 4.375C15.8125 4.375 16.125 4.6875 16.125 5V10H18V5C18 3.63281 16.8672 2.5 15.5 2.5H8C6.59375 2.5 5.5 3.63281 5.5 5V10H7.375V5C7.375 4.6875 7.64844 4.375 8 4.375H10.1875V6.875C10.1875 7.22656 10.4609 7.5 10.8125 7.5H12.6875C13 7.5 13.3125 7.22656 13.3125 6.875V4.375H15.5ZM13.2734 22.5H20.5C21.8672 22.5 23 21.4062 23 20V13.75C23 12.3828 21.8672 11.25 20.5 11.25H13.2734C13.7422 11.7969 14.0547 12.4219 14.1719 13.125H15.1875V15.625C15.1875 15.9766 15.4609 16.25 15.8125 16.25H17.6875C18 16.25 18.3125 15.9766 18.3125 15.625V13.125H20.5C20.8125 13.125 21.125 13.4375 21.125 13.75V20C21.125 20.3516 20.8125 20.625 20.5 20.625H14.1719C14.0547 21.3672 13.7422 21.9922 13.2734 22.5ZM10.5 13.125C10.8125 13.125 11.125 13.4375 11.125 13.75V20C11.125 20.3516 10.8125 20.625 10.5 20.625H3C2.64844 20.625 2.375 20.3516 2.375 20V13.75C2.375 13.4375 2.64844 13.125 3 13.125H5.1875V15.625C5.1875 15.9766 5.46094 16.25 5.8125 16.25H7.6875C8 16.25 8.3125 15.9766 8.3125 15.625V13.125H10.5ZM3 11.25C1.59375 11.25 0.5 12.3828 0.5 13.75V20C0.5 21.4062 1.59375 22.5 3 22.5H10.5C11.8672 22.5 13 21.4062 13 20V13.75C13 12.3828 11.8672 11.25 10.5 11.25H3Z"/></svg>',
            'reports': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M15.0859 11.0547C14.7344 11.3281 14.2266 11.3281 13.875 11.0547L9.42188 7.14843L3.44531 11.0937C3.01562 11.4062 2.42969 11.2891 2.15625 10.8594C1.84375 10.4297 1.96094 9.84375 2.39062 9.53125L8.95312 5.19531C9.30469 4.96093 9.77344 4.96093 10.0859 5.23437L14.5 9.10156L20.4375 3.98437C20.8281 3.67187 21.4141 3.71093 21.7656 4.10156C22.0781 4.49218 22.0391 5.07812 21.6484 5.42968L15.0859 11.0547ZM11.375 13.125V19.375C11.375 20.4297 10.5156 21.25 9.5 21.25C8.44531 21.25 7.625 20.4297 7.625 19.375V13.125C7.625 12.1094 8.44531 11.25 9.5 11.25C10.5156 11.25 11.375 12.1094 11.375 13.125ZM9.8125 19.375V13.125C9.8125 12.9687 9.65625 12.8125 9.5 12.8125C9.30469 12.8125 9.1875 12.9687 9.1875 13.125V19.375C9.1875 19.5703 9.30469 19.6875 9.5 19.6875C9.65625 19.6875 9.8125 19.5703 9.8125 19.375ZM6.375 16.875V19.375C6.375 20.4297 5.51562 21.25 4.5 21.25C3.44531 21.25 2.625 20.4297 2.625 19.375V16.875C2.625 15.8594 3.44531 15 4.5 15C5.51562 15 6.375 15.8594 6.375 16.875ZM4.8125 19.375V16.875C4.8125 16.7187 4.65625 16.5625 4.5 16.5625C4.30469 16.5625 4.1875 16.7187 4.1875 16.875V19.375C4.1875 19.5703 4.30469 19.6875 4.5 19.6875C4.65625 19.6875 4.8125 19.5703 4.8125 19.375ZM12.625 15.625C12.625 14.6094 13.4453 13.75 14.5 13.75C15.5156 13.75 16.375 14.6094 16.375 15.625V19.375C16.375 20.4297 15.5156 21.25 14.5 21.25C13.4453 21.25 12.625 20.4297 12.625 19.375V15.625ZM14.5 15.3125C14.3047 15.3125 14.1875 15.4687 14.1875 15.625V19.375C14.1875 19.5703 14.3047 19.6875 14.5 19.6875C14.6562 19.6875 14.8125 19.5703 14.8125 19.375V15.625C14.8125 15.4687 14.6562 15.3125 14.5 15.3125ZM21.375 13.125V19.375C21.375 20.4297 20.5156 21.25 19.5 21.25C18.4453 21.25 17.625 20.4297 17.625 19.375V13.125C17.625 12.1094 18.4453 11.25 19.5 11.25C20.5156 11.25 21.375 12.1094 21.375 13.125ZM19.8125 19.375V13.125C19.8125 12.9687 19.6562 12.8125 19.5 12.8125C19.3047 12.8125 19.1875 12.9687 19.1875 13.125V19.375C19.1875 19.5703 19.3047 19.6875 19.5 19.6875C19.6562 19.6875 19.8125 19.5703 19.8125 19.375Z"/></svg>',
            'virtual-sell': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M7.3125 12.5C7.3125 13.0469 6.88281 13.4375 6.375 13.4375C5.82812 13.4375 5.4375 13.0469 5.4375 12.5C5.4375 11.9922 5.82812 11.5625 6.375 11.5625C6.88281 11.5625 7.3125 11.9922 7.3125 12.5ZM9.1875 12.5C9.1875 11.9922 9.57812 11.5625 10.125 11.5625C10.6328 11.5625 11.0625 11.9922 11.0625 12.5C11.0625 13.0469 10.6328 13.4375 10.125 13.4375C9.57812 13.4375 9.1875 13.0469 9.1875 12.5ZM9.1875 15.625C9.1875 16.1719 8.75781 16.5625 8.25 16.5625C7.70312 16.5625 7.3125 16.1719 7.3125 15.625C7.3125 15.1172 7.70312 14.6875 8.25 14.6875C8.75781 14.6875 9.1875 15.1172 9.1875 15.625ZM12.9375 12.5C12.9375 11.9922 13.3281 11.5625 13.875 11.5625C14.3828 11.5625 14.8125 11.9922 14.8125 12.5C14.8125 13.0469 14.3828 13.4375 13.875 13.4375C13.3281 13.4375 12.9375 13.0469 12.9375 12.5ZM12.9375 15.625C12.9375 16.1719 12.5078 16.5625 12 16.5625C11.4531 16.5625 11.0625 16.1719 11.0625 15.625C11.0625 15.1172 11.4531 14.6875 12 14.6875C12.5078 14.6875 12.9375 15.1172 12.9375 15.625ZM16.6875 12.5C16.6875 11.9922 17.0781 11.5625 17.625 11.5625C18.1328 11.5625 18.5625 11.9922 18.5625 12.5C18.5625 13.0469 18.1328 13.4375 17.625 13.4375C17.0781 13.4375 16.6875 13.0469 16.6875 12.5ZM16.6875 15.625C16.6875 16.1719 16.2578 16.5625 15.75 16.5625C15.2031 16.5625 14.8125 16.1719 14.8125 15.625C14.8125 15.1172 15.2031 14.6875 15.75 14.6875C16.2578 14.6875 16.6875 15.1172 16.6875 15.625ZM13.25 2.5C13.9141 2.5 14.5 3.08594 14.5 3.75V6.25C14.5 6.95312 13.9141 7.5 13.25 7.5H10.125V8.75H18.5625C19.8125 8.75 20.8672 9.6875 21.0234 10.8984L21.9609 17.3438C21.9609 17.4609 22 17.5781 22 17.6953V20C22 21.4062 20.8672 22.5 19.5 22.5H4.5C3.09375 22.5 2 21.4062 2 20V17.6953C2 17.5781 2 17.4609 2 17.3438L2.9375 10.8984C3.09375 9.6875 4.14844 8.75 5.39844 8.75H7.625V7.5H4.5C3.79688 7.5 3.25 6.95312 3.25 6.25V3.75C3.25 3.08594 3.79688 2.5 4.5 2.5H13.25ZM5.75 4.375C5.39844 4.375 5.125 4.6875 5.125 5C5.125 5.35156 5.39844 5.625 5.75 5.625H12C12.3125 5.625 12.625 5.35156 12.625 5C12.625 4.6875 12.3125 4.375 12 4.375H5.75ZM4.5 20.625H19.5C19.8125 20.625 20.125 20.3516 20.125 20V19.375H3.875V20C3.875 20.3516 4.14844 20.625 4.5 20.625ZM18.5625 10.625H5.39844C5.08594 10.625 4.8125 10.8594 4.77344 11.1719L3.875 17.5H20.0859L19.1875 11.1719C19.1484 10.8594 18.875 10.625 18.5625 10.625Z"/></svg>',
            'settings': '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="menu-gradient"><defs><linearGradient id="menu-gradient" x1="-1.41667" y1="7" x2="19.2409" y2="27.3625" gradientUnits="userSpaceOnUse"><stop stop-color="#806BFF"/><stop offset="0.346816" stop-color="#1362E2"/><stop offset="0.690621" stop-color="#00BBFF"/><stop offset="0.99463" stop-color="#A0E9A7"/></linearGradient></defs><path d="M7.93744 11.5C7.93744 9.4998 9.49974 7.9375 11.4999 7.9375C13.4667 7.9375 15.0624 9.4998 15.0624 11.5C15.0624 13.4668 13.4667 15.0625 11.4999 15.0625C9.49974 15.0625 7.93744 13.4668 7.93744 11.5ZM11.4999 9.71875C10.5165 9.71875 9.71869 10.5166 9.71869 11.5C9.71869 12.4834 10.5165 13.2812 11.4999 13.2812C12.4833 13.2812 13.2812 12.4834 13.2812 11.5C13.2812 10.5166 12.4833 9.71875 11.4999 9.71875ZM12.8767 2.00001C13.7005 2.00001 14.4167 2.56369 14.6097 3.3634L14.8992 4.56834C15.2146 4.72234 15.5152 4.89713 15.8009 5.09047L16.9921 4.73979C17.7826 4.50711 18.6286 4.84518 19.0406 5.55768L20.4173 7.94121C20.8255 8.65371 20.6956 9.55547 20.1019 10.1232L19.2001 10.9471C19.2113 11.1512 19.2187 11.3256 19.2187 11.5C19.2187 11.6744 19.2113 11.8488 19.2001 12.0195L20.1019 12.8768C20.6956 13.4445 20.8255 14.3463 20.4173 15.0588L19.0406 17.4412C18.6286 18.1537 17.7826 18.4914 16.9921 18.2613L15.8009 17.9088C15.5152 18.1018 15.2146 18.2762 14.8992 18.432L14.6097 19.6381C14.4167 20.4359 13.7005 21 12.8767 21H10.1232C9.29935 21 8.58314 20.4359 8.39017 19.6381L8.10072 18.432C7.78529 18.2762 7.4847 18.1018 7.19896 17.9088L5.97435 18.2613C5.21843 18.4914 4.37271 18.1537 3.96117 17.4412L2.58441 15.0588C2.17287 14.3463 2.30305 13.4445 2.8991 12.8768L3.79863 12.0195C3.78712 11.8488 3.78119 11.6744 3.78119 11.4666C3.78119 11.3256 3.78712 11.1512 3.79863 10.9471L2.8991 10.1232C2.30301 9.55547 2.17287 8.65371 2.58441 7.94121L3.96117 5.55768C4.37271 4.84518 5.21843 4.50711 5.97435 4.73979L7.19896 5.09047C7.4847 4.89713 7.78529 4.72234 8.10072 4.56834L8.39017 3.3634C8.58314 2.56332 9.29935 2 10.1232 2L12.8767 2.00001ZM9.62591 5.84453L9.21029 6.01895C8.739 6.21562 8.30111 6.47168 7.90404 6.77598L7.54408 7.05059L5.5038 6.44941L4.12667 8.83184L5.66671 10.2977L5.60994 10.743C5.57876 10.9916 5.56244 11.2439 5.56244 11.5C5.56244 11.7561 5.57876 12.0084 5.60994 12.257L5.66671 12.7023L4.12705 14.1682L5.5038 16.5506L7.54408 15.9494L7.90404 16.224C8.30111 16.5283 8.739 16.7844 9.21029 16.9811L9.62591 17.1555L10.1232 19.2188H12.8767L13.374 17.1555L13.7896 16.9811C14.2609 16.7844 14.6988 16.5283 15.0958 16.224L15.4558 15.9494L17.4968 16.5506L18.8736 14.1682L17.3335 12.7023L17.3892 12.257C17.4226 12.0084 17.4374 11.7561 17.4374 11.5C17.4374 11.2439 17.4226 10.9916 17.3892 10.743L17.3335 10.2977L18.8736 8.83184L17.4968 6.44941L15.4558 7.05059L15.0958 6.77598C14.6988 6.47168 14.2609 6.21562 13.7896 6.01895L13.374 5.84453L12.8767 3.78125H10.1232L9.62591 5.84453Z"/></svg>',
        });
        this.iconsLibrary.registerSvgPack(this.fontAwesomeDuotonePackName, {
            'house': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M11.23,2.54a1.16,1.16,0,0,1,1.54,0l9.33,8.2a1.17,1.17,0,0,1-1.54,1.77L12,5,3.43,12.51A1.17,1.17,0,0,1,1.9,10.74Z"/><path opacity="0.4" d="M12,5l8.17,7.18v7.37A1.45,1.45,0,0,1,18.73,21H15.79a1.47,1.47,0,0,1-1.46-1.46V16.3a1.16,1.16,0,0,0-1.16-1.17H10.83A1.16,1.16,0,0,0,9.67,16.3v3.24A1.47,1.47,0,0,1,8.21,21H5.3a1.44,1.44,0,0,1-1.46-1.46V12.16Z"/></svg>',
            'user-group': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path opacity="0.4" d="M8.1375 11.625C10.7059 11.625 12.7875 9.52625 12.7875 6.9375C12.7875 4.34875 10.7059 2.25 8.1375 2.25C5.5691 2.25 3.4875 4.34875 3.4875 6.9375C3.4875 9.52625 5.5691 11.625 8.1375 11.625ZM9.97934 13.3828H6.29566C2.81797 13.3828 0 16.2246 0 19.7292C0 20.4324 0.563813 21 1.25913 21H15.0144C15.7119 21 16.275 20.4324 16.275 19.7292C16.275 16.2246 13.4559 13.3828 9.97934 13.3828Z"/><path d="M17.4047 13.9688H14.7218C16.3911 15.3494 17.4374 17.4148 17.4374 19.7292C17.4374 20.198 17.2993 20.6301 17.0741 21H22.0874C22.7304 21 23.2499 20.4727 23.2499 19.7952C23.2499 16.5908 20.6488 13.9688 17.4047 13.9688ZM15.6936 11.625C17.9423 11.625 19.7624 9.79028 19.7624 7.52344C19.7624 5.25659 17.9423 3.42188 15.6936 3.42188C14.7814 3.42188 13.9484 3.73517 13.2698 4.24622C13.6883 5.05627 13.9499 5.96338 13.9499 6.9375C13.9499 8.23828 13.5165 9.43286 12.8022 10.4059C13.5393 11.1562 14.5602 11.625 15.6936 11.625Z"/></svg>',
            'cart-flatbed-boxes': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M23.25 17.4514C23.25 18.0959 22.7269 18.6233 22.0875 18.5903H3.4875C2.84812 18.6562 2.325 18.1289 2.325 17.4514V5.17969C2.325 4.85596 2.06489 4.59375 1.74375 4.59375H1.1298C0.523125 4.59375 0 4.06641 0 3.42188C0 2.77734 0.523125 2.25 1.1298 2.25H2.325C3.60375 2.25 4.65 3.30469 4.65 4.59375V16.3125H22.0875C22.7269 16.3125 23.25 16.8398 23.25 17.4514Z"/><path opacity="0.4" d="M8.1375 13.9688H12.7875C13.4269 13.9688 13.95 13.4414 13.95 12.7969V4.59375C13.95 3.94922 13.4269 3.42188 12.7875 3.42188H8.1375C7.49813 3.42188 6.975 3.94922 6.975 4.59375V12.7969C6.975 13.4414 7.49813 13.9688 8.1375 13.9688ZM16.275 8.10938H18.6C19.2394 8.10938 19.7625 7.58203 19.7625 6.9375V4.59375C19.7625 3.94922 19.2394 3.42188 18.6 3.42188H16.275C15.6356 3.42188 15.1125 3.94922 15.1125 4.59375V6.9375C15.1125 7.58203 15.6356 8.10938 16.275 8.10938ZM16.275 13.9688H20.925C21.5644 13.9688 22.0875 13.4414 22.0875 12.7969V10.4531C22.0875 9.80859 21.5644 9.28125 20.925 9.28125H16.275C15.6356 9.28125 15.1125 9.80859 15.1125 10.4531V12.7969C15.1125 13.4414 15.6356 13.9688 16.275 13.9688ZM5.91785 18.6233C5.85609 18.8064 5.8125 19.0371 5.8125 19.2422C5.8125 20.2126 6.59355 21 7.55625 21C8.51895 21 9.3 20.2126 9.3 19.2422C9.3 19.0362 9.25306 18.8394 9.19403 18.6562H5.91785V18.6233ZM16.3513 18.6233C16.2877 18.8064 16.2449 19.0034 16.2449 19.2092C16.2449 20.1797 17.0256 20.967 17.9883 20.967C18.951 20.967 19.7324 20.1797 19.7324 19.2092C19.7324 19.0032 19.6859 18.8064 19.6268 18.6233H16.3513Z"/></svg>',
            'receipt': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M8.0625 8.10938C8.0625 7.78711 8.31422 7.52344 8.625 7.52344H15.375C15.6844 7.52344 15.9375 7.78711 15.9375 8.10938C15.9375 8.43164 15.6844 8.69531 15.375 8.69531H8.625C8.31422 8.69531 8.0625 8.43164 8.0625 8.10938ZM8.0625 15.1406C8.0625 14.8184 8.31422 14.5547 8.625 14.5547H15.375C15.6844 14.5547 15.9375 14.8184 15.9375 15.1406C15.9375 15.4629 15.6844 15.7266 15.375 15.7266H8.625C8.31422 15.7266 8.0625 15.4629 8.0625 15.1406ZM15.375 11.0391C15.6844 11.0391 15.9375 11.3027 15.9375 11.625C15.9375 11.9473 15.6844 12.2109 15.375 12.2109H8.625C8.31422 12.2109 8.0625 11.9473 8.0625 11.625C8.0625 11.3027 8.31422 11.0391 8.625 11.0391H15.375Z"/><path opacity="0.4" d="M5.74113 2.33042C6.04066 2.18701 6.39258 2.23823 6.64289 2.4616L8.0625 3.72913L9.48281 2.4616C9.79922 2.17947 10.2633 2.17947 10.5797 2.4616L12 3.72913L13.4203 2.4616C13.7367 2.17947 14.2008 2.17947 14.5172 2.4616L15.9375 3.72913L17.3578 2.4616C17.6074 2.23823 17.959 2.18701 18.2578 2.33042C18.5566 2.47386 18.75 2.78577 18.75 3.12891V20.1211C18.75 20.4653 18.5566 20.7766 18.2578 20.9194C17.959 21.0623 17.6074 21.011 17.3578 20.7876L15.9375 19.5205L14.5172 20.7876C14.2008 21.0696 13.7367 21.0696 13.4203 20.7876L12 19.5205L10.5797 20.7876C10.2633 21.0696 9.79922 21.0696 9.48281 20.7876L8.0625 19.5205L6.64289 20.7876C6.39258 21.011 6.04066 21.0623 5.74113 20.9194C5.44181 20.7766 5.25 20.4653 5.25 20.1211V3.12891C5.25 2.78577 5.44181 2.47383 5.74113 2.33042ZM8.625 7.52344C8.31422 7.52344 8.0625 7.78711 8.0625 8.10938C8.0625 8.43164 8.31422 8.69531 8.625 8.69531H15.375C15.6844 8.69531 15.9375 8.43164 15.9375 8.10938C15.9375 7.78711 15.6844 7.52344 15.375 7.52344H8.625ZM8.625 15.7266H15.375C15.6844 15.7266 15.9375 15.4629 15.9375 15.1406C15.9375 14.8184 15.6844 14.5547 15.375 14.5547H8.625C8.31422 14.5547 8.0625 14.8184 8.0625 15.1406C8.0625 15.4629 8.31422 15.7266 8.625 15.7266ZM8.625 11.0391C8.31422 11.0391 8.0625 11.3027 8.0625 11.625C8.0625 11.9473 8.31422 12.2109 8.625 12.2109H15.375C15.6844 12.2109 15.9375 11.9473 15.9375 11.625C15.9375 11.3027 15.6844 11.0391 15.375 11.0391H8.625Z"/></svg>',
            'hand-holding-dollar': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M11.6829 9.15234C11.4058 9.11367 11.0412 8.98711 10.7204 8.87461L10.5417 8.81484C10.086 8.62852 9.58651 8.88867 9.42609 9.29648C9.26567 9.7043 9.50342 10.2176 9.95802 10.3737L10.1277 10.4319C10.4371 10.5395 10.7796 10.6462 11.1263 10.7241V11.1562C11.1263 11.6221 11.5175 12 12.0006 12C12.4837 12 12.8756 11.6221 12.8756 11.1562V10.7906C13.7969 10.5894 14.4389 10.0225 14.5563 9.18328C14.8731 7.42371 13.1385 6.94523 12.2077 6.68895L12.0146 6.62461C11.0558 6.36234 11.1105 6.24914 11.136 6.10465C11.1781 5.86678 11.6938 5.75098 12.3103 5.84481C12.4903 5.87228 12.7248 5.93435 13.0267 6.03543C13.4835 6.18593 13.9823 5.95414 14.1394 5.51371C14.2966 5.07327 14.0552 4.59296 13.5984 4.44039C13.3212 4.34803 13.0909 4.28718 12.8758 4.23926V3.84375C12.8758 3.37793 12.4838 3 12.0008 3C11.5177 3 11.1265 3.37793 11.1265 3.84375V4.21359C10.2063 4.41434 9.56099 4.97754 9.37869 5.81707C9.09796 7.55625 10.8589 8.05547 11.5224 8.24531L11.7593 8.31123C12.9136 8.62982 12.895 8.72924 12.8655 8.89623C12.824 9.13477 12.3099 9.25078 11.6829 9.15234Z"/><path opacity="0.4" d="M22.2156 14.8231C21.7373 14.1969 20.8251 14.0616 20.1765 14.5253L15.8124 17.625H11.4156C11.0966 17.625 10.8651 17.3702 10.8651 17.0629C10.8651 16.7553 11.1294 16.5004 11.4156 16.5004H14.2684C14.819 16.5004 15.3895 16.1179 15.4851 15.5645C15.6035 14.8614 15.0432 14.2504 14.3322 14.2504H8.46719C7.51562 14.2504 6.53052 14.5756 5.7649 15.1732L4.06958 16.5L2.05052 16.4684C1.76436 16.4684 1.5 16.7566 1.5 17.0625V20.4368C1.5 20.7469 1.76436 21 2.05052 21H14.6687C15.4719 21 16.27 20.7473 16.9182 20.2874L21.8437 16.7886C22.5547 16.3277 22.6932 15.4488 22.2156 14.8231Z"/></svg>',
            'file-invoice-dollar': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M14.2499 2.25V6.9375H18.7499L14.2499 2.25ZM12.1933 13.8516L11.9683 13.793C11.1667 13.5366 11.1843 13.3865 11.2124 13.2546C11.2608 12.9686 11.7957 12.8987 12.2766 12.9754C12.4733 13.0063 12.689 13.0784 12.895 13.1528C13.2603 13.2844 13.6625 13.0818 13.7897 12.7009C13.9172 12.3186 13.7233 11.9011 13.3563 11.7682C13.1025 11.6772 12.8954 11.6188 12.7038 11.575V11.332C12.7038 10.9281 12.389 10.5996 12.0006 10.5996C11.6123 10.5996 11.2975 10.9281 11.2975 11.332V11.5515C10.5093 11.7275 9.95385 12.2402 9.82729 13.0056C9.56692 14.5745 10.9941 15.0106 11.6034 15.197L11.8077 15.2588C12.8371 15.5655 12.8202 15.6686 12.7893 15.855C12.7409 16.1411 12.206 16.2121 11.7237 16.1343C11.4798 16.0965 11.1679 15.9798 10.8919 15.8768L10.7348 15.8173C10.3688 15.6857 9.96732 15.8836 9.83759 16.2648C9.7085 16.646 9.9002 17.0646 10.2665 17.1997L10.4165 17.2547C10.6892 17.3568 10.9923 17.4594 11.2975 17.5297V17.7773C11.2975 18.1813 11.6122 18.5098 12.0006 18.5098C12.389 18.5098 12.7037 18.1813 12.7037 17.7773V17.5483C13.4898 17.3728 14.0471 16.8697 14.1743 16.1043C14.4363 14.5181 12.9773 14.0823 12.1933 13.8516Z"/><path opacity="0.4" d="M14.25 6.9375V2.25H6.9375C6.00551 2.25 5.25 3.03699 5.25 4.00781V19.2422C5.25 20.2126 6.00551 21 6.9375 21H17.0625C17.9945 21 18.75 20.213 18.75 19.2422V6.9375H14.25ZM7.5 4.88672C7.5 4.72668 7.62762 4.59375 7.78125 4.59375H10.5938C10.7484 4.59375 10.875 4.72668 10.875 4.88672V5.47266C10.875 5.63306 10.7484 5.76562 10.5938 5.76562H7.78125C7.62762 5.76562 7.5 5.63306 7.5 5.47266V4.88672ZM7.5 7.81641V7.23047C7.5 7.06934 7.62762 6.9375 7.78125 6.9375H10.5938C10.7484 6.9375 10.875 7.06934 10.875 7.23047V7.81641C10.875 7.97754 10.7484 8.10938 10.5938 8.10938H7.78125C7.62762 8.10938 7.5 7.97754 7.5 7.81641ZM14.1727 16.1038C14.0453 16.8695 13.4882 17.3723 12.7021 17.5477V17.7773C12.7021 18.1813 12.3873 18.5098 11.9989 18.5098C11.6106 18.5098 11.2958 18.1813 11.2958 17.7773V17.5294C10.9906 17.459 10.6876 17.3565 10.4148 17.2543L10.2648 17.1994C9.8985 17.0643 9.70655 16.6457 9.83592 16.2645C9.96558 15.8832 10.3671 15.6855 10.7331 15.8169L10.8902 15.8765C11.166 15.9794 11.478 16.0962 11.722 16.1339C12.2044 16.2118 12.7394 16.1408 12.7876 15.8547C12.8184 15.6682 12.8354 15.5652 11.806 15.2585L11.6017 15.1967C10.9925 15.0101 9.56546 14.5741 9.82562 13.0053C9.95391 12.2402 10.5094 11.7275 11.2969 11.5188V11.332C11.2969 10.9281 11.6116 10.5996 12 10.5996C12.3884 10.5996 12.7031 10.9281 12.7031 11.332V11.5749C12.8948 11.6187 13.1018 11.6771 13.3556 11.768C13.7227 11.9008 13.9164 12.3185 13.7891 12.7008C13.6617 13.082 13.2596 13.2845 12.8944 13.1527C12.6884 13.0783 12.4725 13.0062 12.276 12.9753C11.7961 12.8994 11.2301 12.969 11.2125 13.2546C11.1844 13.3865 11.1668 13.5366 11.9684 13.793L12.1634 13.8513C12.9773 14.0823 14.4363 14.5181 14.1727 16.1038Z"/></svg>',
            'basket-shopping': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M6.96521 11.9839C6.76834 12.427 6.25063 12.6248 5.80949 12.427C5.36834 12.2292 5.17146 11.7092 5.36834 11.2661L9.16001 2.76929C9.35688 2.32643 9.87459 2.12831 10.3157 2.32687C10.7569 2.52543 10.9538 3.04541 10.7569 3.48853L6.96521 11.9839ZM13.2433 3.48853C13.0465 3.04541 13.2433 2.52543 13.6845 2.32687C14.1256 2.12831 14.6433 2.32643 14.8402 2.76929L18.6319 11.2661C18.8288 11.7092 18.6319 12.2292 18.1907 12.427C17.7496 12.6248 17.2319 12.427 17.035 11.9839L13.2433 3.48853Z"/><path opacity="0.4" d="M15.8281 9.28125L17.0349 11.9839C17.2318 12.427 17.7495 12.6248 18.1906 12.427C18.6318 12.2292 18.8286 11.7092 18.6318 11.2661L17.7458 9.28125H21.3333C21.9786 9.28125 22.5 9.80493 22.5 10.4531C22.5 11.1013 21.9786 11.625 21.3333 11.625L19.4411 19.2239C19.1495 20.2676 18.249 21 17.1771 21H6.7901C5.75104 21 4.81771 20.2676 4.55812 19.2239L2.66667 11.625C2.02245 11.625 1.5 11.1013 1.5 10.4531C1.5 9.80493 2.02245 9.28125 2.66667 9.28125H6.25417L5.36823 11.2661C5.17135 11.7092 5.36823 12.2292 5.80937 12.427C6.25052 12.6248 6.76823 12.427 6.9651 11.9839L8.13906 9.28125H15.8281ZM8.5 13.9358C8.5 13.6428 8.2375 13.3499 7.91667 13.3499C7.59583 13.3499 7.33333 13.6428 7.33333 13.9358V17.4514C7.33333 17.8066 7.59583 18.0374 7.91667 18.0374C8.2375 18.0374 8.5 17.8066 8.5 17.4514V13.9358ZM11.4167 13.9358V17.4514C11.4167 17.8066 11.6792 18.0374 12 18.0374C12.3208 18.0374 12.5833 17.8066 12.5833 17.4514V13.9358C12.5833 13.6428 12.3208 13.3499 12 13.3499C11.6792 13.3499 11.4167 13.6428 11.4167 13.9358ZM16.6667 13.9358C16.6667 13.6428 16.4042 13.3499 16.0833 13.3499C15.7625 13.3499 15.5 13.6428 15.5 13.9358V17.4514C15.5 17.8066 15.7625 18.0374 16.0833 18.0374C16.4042 18.0374 16.6667 17.8066 16.6667 17.4514V13.9358Z"/></svg>',
            'file-chart-column': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M16.0535 12.7969H14.7C14.475 12.7969 14.25 13.0312 14.25 13.2656V18.1912C14.25 18.4219 14.475 18.6562 14.7 18.6562H16.0535C16.275 18.6562 16.5 18.4219 16.5 18.1912V13.2656C16.5 13.0312 16.275 12.7969 16.0535 12.7969ZM9.30352 15.1406H7.95C7.7243 15.1406 7.5 15.375 7.5 15.6094V18.1912C7.5 18.4219 7.7243 18.6562 7.94824 18.6562H9.30176C9.525 18.6562 9.75 18.4219 9.75 18.1912V15.6094C9.75 15.375 9.525 15.1406 9.30352 15.1406ZM12.6785 10.4531H11.325C11.1 10.4531 10.875 10.6875 10.875 10.9219V18.1912C10.875 18.4219 11.1 18.6562 11.325 18.6562H12.6785C12.9 18.6562 13.125 18.4219 13.125 18.1912V10.9219C13.125 10.6875 12.9 10.4531 12.6785 10.4531ZM14.25 2.25V6.9375H18.75L14.25 2.25Z"/><path opacity="0.4" d="M14.25 6.9375V2.25H6.9375C6.00551 2.25 5.25 3.03699 5.25 4.00781V19.2422C5.25 20.2126 6.00551 21 6.9375 21H17.0625C17.9945 21 18.75 20.213 18.75 19.2422V6.9375H14.25ZM9.75 18.1912C9.75 18.4219 9.525 18.6562 9.30352 18.6562H7.95C7.7243 18.6562 7.5 18.4219 7.5 18.1912V15.6094C7.5 15.375 7.7243 15.1406 7.94824 15.1406H9.30176C9.525 15.1406 9.75 15.375 9.75 15.6094V18.1912ZM13.125 18.1912C13.125 18.4219 12.9 18.6562 12.6785 18.6562H11.325C11.1 18.6562 10.875 18.4219 10.875 18.1912V10.9219C10.875 10.6875 11.1 10.4531 11.325 10.4531H12.6785C12.9 10.4531 13.125 10.6875 13.125 10.9219V18.1912ZM16.5 18.1912C16.5 18.4219 16.275 18.6562 16.0535 18.6562H14.7C14.475 18.6562 14.25 18.4219 14.25 18.1912V13.2656C14.25 13.0312 14.475 12.7969 14.7 12.7969H16.0535C16.275 12.7969 16.5 13.0312 16.5 13.2656V18.1912Z"/></svg>',
            'gears': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M11.4325 6.28931C11.567 6.60791 11.4616 6.94116 11.2073 7.20483L10.4008 7.92993C10.4408 8.17896 10.4626 8.4353 10.4626 8.66235C10.4626 8.95532 10.4408 9.21167 10.4008 9.46069L11.2073 10.1858C11.4616 10.4165 11.567 10.7498 11.4325 11.1013C11.349 11.2625 11.2582 11.4858 11.1564 11.6689L11.0438 11.8667C10.9021 12.0498 10.815 12.2256 10.6878 12.3977C10.4807 12.676 10.1175 12.7676 9.79051 12.6577L8.76242 12.3171C8.37371 12.6431 7.93051 12.9031 7.44734 13.0862L7.19668 14.1482C7.15672 14.4888 6.89515 14.7598 6.55367 14.8037C6.31027 14.833 6.06324 14.8477 5.81258 14.8477C5.56191 14.8477 5.31488 14.833 5.07148 14.8037C4.73 14.7598 4.43574 14.4888 4.39578 14.1482L4.14512 13.0862C3.69465 12.9031 3.25144 12.6431 2.86092 12.3171L1.83574 12.6577C1.50915 12.7676 1.14514 12.676 0.938069 12.3977C0.809467 12.2256 0.689584 12.0461 0.579147 11.863L0.469436 11.6689C0.36808 11.4858 0.27588 11.2954 0.193487 11.1013C0.0589279 10.7498 0.162027 10.4165 0.418576 10.1858L1.22433 9.46069C1.18365 9.21167 1.12988 8.95532 1.12988 8.66235C1.12988 8.4353 1.18365 8.17896 1.22433 7.92993L0.418576 7.20483C0.162027 6.94116 0.0589279 6.60791 0.193487 6.28931C0.27588 6.06226 0.36808 5.90442 0.469072 5.72021L0.57951 5.52795C0.689584 5.34302 0.809467 5.16431 0.938069 4.99255C1.14514 4.71606 1.50915 4.62415 1.83574 4.73254L2.86092 5.07312C3.25144 4.74939 3.69465 4.48865 4.14512 4.30627L4.39578 3.2417C4.43574 2.90186 4.73 2.62976 5.07148 2.58805C5.31488 2.55828 5.56191 2.54297 5.81258 2.54297C6.06324 2.54297 6.31027 2.55828 6.55367 2.58801C6.89515 2.62976 7.15672 2.90186 7.19668 3.2417L7.44734 4.30627C7.93051 4.48865 8.37371 4.74939 8.76242 5.07312L9.79051 4.73254C10.1175 4.62415 10.4807 4.71606 10.6878 4.99255C10.815 5.16357 10.9021 5.34155 11.0438 5.52539L11.1564 5.72278C11.2582 5.90625 11.349 6.09521 11.4325 6.28931ZM5.81258 10.4202C6.77527 10.4202 7.55633 9.66577 7.55633 8.66235C7.55633 7.72485 6.77527 6.90454 5.81258 6.90454C4.84988 6.90454 4.06883 7.72485 4.06883 8.66235C4.06883 9.66577 4.84988 10.4202 5.81258 10.4202Z"/><path opacity="0.4" d="M19.2428 20.8059C18.8941 20.9414 18.5635 20.8352 18.3346 20.5788L17.6153 19.7658C17.3683 19.8061 17.114 19.8281 16.8561 19.8281C16.5981 19.8281 16.3438 19.8061 16.0968 19.7658L15.3775 20.5788C15.1159 20.8352 14.7854 20.9414 14.4693 20.8059C14.2768 20.7216 14.0879 20.6301 13.9062 20.5275L13.71 20.414C13.5284 20.2712 13.354 20.1833 13.1833 20.0551C12.9072 19.8464 12.8164 19.4802 12.9254 19.1506L13.2632 18.1142C12.9399 17.7224 12.682 17.2756 12.5003 16.7885L11.4468 16.5359C11.1089 16.4956 10.8401 16.2319 10.7965 15.8877C10.7675 15.6423 10.7529 15.3933 10.7529 15.1406C10.7529 14.8879 10.7675 14.6389 10.7965 14.3935C10.8401 14.0493 11.1089 13.7526 11.4468 13.7124L12.5003 13.4597C12.682 13.0056 12.9399 12.5588 13.2632 12.167L12.9254 11.1306C12.8164 10.801 12.9072 10.4348 13.1833 10.226C13.354 10.0979 13.532 9.94405 13.7137 9.86349L13.9062 9.75362C14.0879 9.65109 14.2441 9.55953 14.4693 9.4753C14.7854 9.33981 15.1159 9.44601 15.3775 9.70236L16.0968 10.5153C16.3438 10.4751 16.5981 10.4531 16.8561 10.4531C17.114 10.4531 17.3683 10.4751 17.6153 10.5153L18.3346 9.70236C18.5635 9.44601 18.8941 9.33981 19.2428 9.4753C19.4354 9.55953 19.6243 9.65109 19.8059 9.75362L19.9984 9.86349C20.1801 9.94405 20.3581 10.0979 20.5288 10.226C20.8049 10.4348 20.8957 10.801 20.7868 11.1306L20.4489 12.167C20.7722 12.5588 21.0302 13.0056 21.2118 13.4597L22.2653 13.7124C22.6032 13.7526 22.872 14.0493 22.9156 14.3935C22.9446 14.6389 22.9592 14.8879 22.9592 15.1406C22.9592 15.3933 22.9446 15.6423 22.9156 15.8877C22.872 16.2319 22.6032 16.4956 22.2653 16.5359L21.2118 16.7885C21.0302 17.2756 20.7722 17.7224 20.4489 18.1142L20.7868 19.1506C20.8957 19.4802 20.8049 19.8464 20.5288 20.0551C20.3581 20.1833 20.1837 20.2712 20.0021 20.414L19.8059 20.5275C19.6243 20.6301 19.4027 20.7216 19.2428 20.8059ZM15.1123 15.1406C15.1123 16.111 15.8934 16.8984 16.8561 16.8984C17.8187 16.8984 18.5998 16.111 18.5998 15.1406C18.5998 14.1701 17.8187 13.3828 16.8561 13.3828C15.8934 13.3828 15.1123 14.1701 15.1123 15.1406Z"/></svg>',
            'clock-rotate-left': '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="100%" height="100%" viewBox="0 0 24 24"><path d="M11.625 6.9375C12.1121 6.9375 12.5039 7.32935 12.5039 7.81641V11.2625L14.8513 13.6392C15.2249 13.9834 15.2249 14.54 14.8513 14.8513C14.54 15.2249 13.9834 15.2249 13.6392 14.8513L11.0024 12.2146C10.8376 12.0828 10.7461 11.8594 10.7461 11.625V7.81641C10.7461 7.32935 11.1379 6.9375 11.625 6.9375Z"/><path opacity="0.4" d="M7.77979 7.77979C8.33276 8.33276 7.94092 9.28125 7.16089 9.28125H3.12891C2.64368 9.28125 2.25 8.8894 2.25 8.40234V4.37183C2.25 3.58887 3.19666 3.19666 3.75037 3.75037L4.99585 4.99585C6.69214 3.30066 9.03589 2.25 11.592 2.25C16.8032 2.25 21 6.44678 21 11.625C21 16.8032 16.8032 21 11.592 21C9.63647 21 7.78711 20.3774 6.26733 19.3191C5.73633 18.9163 5.60596 18.2168 5.97803 17.6858C6.3479 17.1548 7.07666 17.0266 7.60767 17.3965C8.74658 18.1912 10.1309 18.6562 11.592 18.6562C15.5068 18.6562 18.6562 15.5068 18.6562 11.625C18.6562 7.71021 15.5068 4.59375 11.592 4.59375C9.65112 4.59375 7.92627 5.37964 6.65186 6.65186L7.77979 7.77979Z"/></svg>',
        });
        /* eslint-enable max-len */
        iconsLibrary.setDefaultPack(this.essentialsPackName);
    }
}
CjIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconModule, deps: [{ token: CjIconLibraries }], target: i0.ɵɵFactoryTarget.NgModule });
CjIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconModule, declarations: [CjIconComponent], imports: [CommonModule], exports: [CjIconComponent] });
CjIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        CjIconComponent,
                    ],
                    exports: [
                        CjIconComponent,
                    ]
                }]
        }], ctorParameters: function () { return [{ type: CjIconLibraries }]; } });

const CJ_SIDEBAR_COMPONENTS = [
    CjSidebarComponent,
    CjSidebarFooterComponent,
    CjSidebarHeaderComponent,
];
const CJ_SIDEBAR_PROVIDERS = [
    CjSidebarService,
];
class CjSidebarModule {
    static forRoot() {
        return {
            ngModule: CjSidebarModule,
            providers: [
                ...CJ_SIDEBAR_PROVIDERS,
            ],
        };
    }
}
CjSidebarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjSidebarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarModule, declarations: [CjSidebarComponent,
        CjSidebarFooterComponent,
        CjSidebarHeaderComponent], imports: [CjSharedModule,
        CjIconModule,
        CjButtonModule], exports: [CjSidebarComponent,
        CjSidebarFooterComponent,
        CjSidebarHeaderComponent] });
CjSidebarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarModule, imports: [[
            CjSharedModule,
            CjIconModule,
            CjButtonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSidebarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjSharedModule,
                        CjIconModule,
                        CjButtonModule,
                    ],
                    declarations: [
                        ...CJ_SIDEBAR_COMPONENTS,
                    ],
                    exports: [
                        ...CJ_SIDEBAR_COMPONENTS,
                    ],
                }]
        }] });

;
const itemClick$ = new Subject();
const addItems$ = new ReplaySubject(1);
const navigateHome$ = new ReplaySubject(1);
const getSelectedItem$ = new ReplaySubject(1);
const itemSelect$ = new ReplaySubject(1);
const itemHover$ = new ReplaySubject(1);
const submenuToggle$ = new ReplaySubject(1);
const collapseAll$ = new ReplaySubject(1);
class CjMenuItem {
    constructor() {
        /**
         * Item is selected when partly or fully equal to the current url
         * @type {string}
         */
        this.pathMatch = 'full';
        this.level = 0;
    }
    /**
     * @returns item parents in top-down order
     */
    static getParents(item) {
        const parents = [];
        let parent = item.parent;
        while (parent) {
            parents.unshift(parent);
            parent = parent.parent;
        }
        return parents;
    }
    static isParent(item, possibleChild) {
        return possibleChild.parent
            ? possibleChild.parent === item || this.isParent(item, possibleChild.parent)
            : false;
    }
}
class CjMenuService {
    /**
     * Add items to the end of the menu items list
     * @param {List<CjMenuItem>} items
     * @param {string} tag
     */
    addItems(items, tag) {
        addItems$.next({ tag, items });
    }
    /**
     * Collapses all menu items
     * @param {string} tag
     */
    collapseAll(tag) {
        collapseAll$.next({ tag });
    }
    /**
     * Navigate to the home menu item
     * @param {string} tag
     */
    navigateHome(tag) {
        navigateHome$.next({ tag });
    }
    /**
     * Returns currently selected item. Won't subscribe to the future events.
     * @param {string} tag
     * @returns {Observable<{tag: string; item: CjMenuItem}>}
     */
    getSelectedItem(tag) {
        const listener = new BehaviorSubject(null);
        getSelectedItem$.next({ tag, listener });
        return listener.asObservable();
    }
    onItemClick() {
        return itemClick$.pipe(share());
    }
    onItemSelect() {
        return itemSelect$.pipe(share());
    }
    onItemHover() {
        return itemHover$.pipe(share());
    }
    onSubmenuToggle() {
        return submenuToggle$.pipe(share());
    }
}
CjMenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjMenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuService, decorators: [{
            type: Injectable
        }] });
class CjMenuInternalService {
    constructor(location) {
        this.location = location;
    }
    prepareItems(items) {
        const defaultItem = new CjMenuItem();
        items.forEach(i => {
            this.applyDefaults(i, defaultItem);
            this.setParent(i);
            this.setLevel(i);
        });
    }
    selectFromUrl(items, tag, collapseOther = false) {
        const selectedItem = this.findItemByUrl(items);
        if (selectedItem) {
            this.selectItem(selectedItem, items, collapseOther, tag);
        }
    }
    selectItem(item, items, collapseOther = false, tag, isAction = false) {
        const unselectedItems = this.resetSelection(items);
        const collapsedItems = collapseOther ? this.collapseItems(items) : [];
        for (const parent of CjMenuItem.getParents(item)) {
            parent.selected = true;
            // emit event only for items that weren't selected before ('unselectedItems' contains items that were selected)
            if (!unselectedItems.includes(parent)) {
                this.itemSelect(parent, tag);
            }
            const wasNotExpanded = !parent.expanded;
            parent.expanded = true;
            const i = collapsedItems.indexOf(parent);
            // emit event only for items that weren't expanded before.
            // 'collapsedItems' contains items that were expanded, so no need to emit event.
            // in case 'collapseOther' is false, 'collapsedItems' will be empty,
            // so also check if item isn't expanded already ('wasNotExpanded').
            if (i === -1 && wasNotExpanded) {
                this.submenuToggle(parent, tag);
            }
            else {
                collapsedItems.splice(i, 1);
            }
        }
        if (isAction && item.action) {
            item.action.selected = true;
        }
        item.selected = true;
        // emit event only for items that weren't selected before ('unselectedItems' contains items that were selected)
        if (!unselectedItems.includes(item)) {
            this.itemSelect(item, tag);
        }
        // remaining items which wasn't expanded back after expanding all currently selected items
        for (const collapsedItem of collapsedItems) {
            this.submenuToggle(collapsedItem, tag);
        }
    }
    collapseAll(items, tag, except) {
        const collapsedItems = this.collapseItems(items, except);
        for (const item of collapsedItems) {
            this.submenuToggle(item, tag);
        }
    }
    onAddItem() {
        return addItems$.pipe(share());
    }
    onNavigateHome() {
        return navigateHome$.pipe(share());
    }
    onCollapseAll() {
        return collapseAll$.pipe(share());
    }
    onGetSelectedItem() {
        return getSelectedItem$.pipe(share());
    }
    itemHover(item, tag) {
        itemHover$.next({ tag, item });
    }
    submenuToggle(item, tag) {
        submenuToggle$.next({ tag, item });
    }
    itemSelect(item, tag) {
        itemSelect$.next({ tag, item });
    }
    itemClick(item, tag) {
        itemClick$.next({ tag, item });
    }
    /**
     * Unselect all given items deeply.
     * @param items array of items to unselect.
     * @returns items which selected value was changed.
     */
    resetSelection(items) {
        const unselectedItems = [];
        for (const item of items) {
            if (item.selected) {
                unselectedItems.push(item);
            }
            item.selected = false;
            if (item.action) {
                item.action.selected = false;
            }
            if (item.children) {
                unselectedItems.push(...this.resetSelection(item.children));
            }
        }
        return unselectedItems;
    }
    /**
     * Collapse all given items deeply.
     * @param items array of items to collapse.
     * @param except menu item which shouldn't be collapsed, also disables collapsing for parents of this item.
     * @returns items which expanded value was changed.
     */
    collapseItems(items, except) {
        const collapsedItems = [];
        for (const item of items) {
            if (except && (item === except || CjMenuItem.isParent(item, except))) {
                continue;
            }
            if (item.expanded) {
                collapsedItems.push(item);
            }
            item.expanded = false;
            if (item.children) {
                collapsedItems.push(...this.collapseItems(item.children));
            }
        }
        return collapsedItems;
    }
    applyDefaults(item, defaultItem) {
        const menuItem = { ...item };
        Object.assign(item, defaultItem, menuItem);
        item.children && item.children.forEach(child => {
            this.applyDefaults(child, defaultItem);
        });
    }
    setParent(item) {
        if (item.action) {
            item.action.parent = item;
        }
        item.children && item.children.forEach(child => {
            child.parent = item;
            this.setParent(child);
        });
    }
    setLevel(item, level = 0) {
        item.children && item.children.forEach(child => {
            child.level = level + 1;
            this.setLevel(child, child.level);
        });
    }
    /**
     * Find deepest item which link matches current URL path.
     * @param items array of items to search in.
     * @returns found item of undefined.
     */
    findItemByUrl(items) {
        let selectedItem;
        items.some(item => {
            if (item.children) {
                selectedItem = this.findItemByUrl(item.children);
            }
            if (!selectedItem && this.isSelectedInUrl(item)) {
                selectedItem = item;
            }
            return selectedItem;
        });
        return selectedItem;
    }
    isSelectedInUrl(item) {
        const exact = item.pathMatch === 'full';
        const link = item.route;
        const isSelectedInPath = exact
            ? isUrlPathEqual(this.location.path(), link)
            : isUrlPathContain(this.location.path(), link);
        if (isSelectedInPath && item.fragment != null) {
            return exact
                ? isFragmentEqual(this.location.path(true), item.fragment)
                : isFragmentContain(this.location.path(true), item.fragment);
        }
        return isSelectedInPath;
    }
}
CjMenuInternalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuInternalService, deps: [{ token: i1$1.Location }], target: i0.ɵɵFactoryTarget.Injectable });
CjMenuInternalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuInternalService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuInternalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Location }]; } });

class CjDynamicOverlay {
    constructor(overlay, componentFactoryResolver, zone, overlayContainer) {
        this.overlay = overlay;
        this.componentFactoryResolver = componentFactoryResolver;
        this.zone = zone;
        this.overlayContainer = overlayContainer;
        this.context = {};
        this.overlayConfig = {};
        this.disabled = false;
        this.positionStrategyChange$ = new Subject();
        this.isShown$ = new BehaviorSubject(false);
        this.destroy$ = new Subject();
        this.overlayDestroy$ = new Subject();
    }
    get isAttached() {
        return this.ref && this.ref.hasAttached();
    }
    get isShown() {
        return this.isShown$.pipe(distinctUntilChanged());
    }
    create(componentType, content, context, positionStrategy, overlayConfig = {}, disabled = false) {
        this.setContentAndContext(content, context);
        this.setComponent(componentType);
        this.setPositionStrategy(positionStrategy);
        this.setOverlayConfig(overlayConfig);
        this.setDisabled(disabled);
        return this;
    }
    setContent(content) {
        this.content = content;
        if (this.container) {
            this.updateContext();
        }
        this.updatePosition();
    }
    setContext(context) {
        this.context = context;
        if (this.container) {
            this.updateContext();
        }
        this.updatePosition();
    }
    setContentAndContext(content, context) {
        this.content = content;
        this.context = context;
        if (this.container) {
            this.updateContext();
        }
        this.updatePosition();
    }
    setComponent(componentType) {
        this.componentType = componentType;
        // in case the component is shown we recreate it and show it back
        const wasAttached = this.isAttached;
        this.disposeOverlayRef();
        if (wasAttached) {
            this.show();
        }
    }
    setPositionStrategy(positionStrategy) {
        this.positionStrategyChange$.next();
        this.positionStrategy = positionStrategy;
        this.positionStrategy.positionChange
            .pipe(filter(() => !!this.container), takeUntil(merge(this.positionStrategyChange$, this.destroy$)))
            .subscribe((position) => {
            this.lastAppliedPosition = position;
            patch(this.container, { position });
        });
        if (this.ref) {
            this.ref.updatePositionStrategy(this.positionStrategy);
        }
    }
    setOverlayConfig(overlayConfig) {
        this.overlayConfig = overlayConfig;
        const wasAttached = this.isAttached;
        this.disposeOverlayRef();
        if (wasAttached) {
            this.show();
        }
    }
    setDisabled(disabled) {
        if (disabled && this.isShown$.value) {
            this.hide();
        }
        this.disabled = disabled;
    }
    show() {
        if (this.disabled) {
            return;
        }
        if (!this.ref) {
            this.createOverlay();
        }
        this.renderContainer();
        if (!this.hasOverlayInContainer()) {
            // Dispose overlay ref as it refers to the old overlay container and create new by calling `show`
            this.disposeOverlayRef();
            return this.show();
        }
        this.isShown$.next(true);
    }
    hide() {
        if (!this.ref) {
            return;
        }
        this.ref.detach();
        this.container = null;
        this.isShown$.next(false);
    }
    toggle() {
        if (this.isAttached) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    dispose() {
        this.destroy$.next();
        this.destroy$.complete();
        this.hide();
        this.disposeOverlayRef();
        this.isShown$.complete();
        this.positionStrategyChange$.complete();
        this.overlayDestroy$.complete();
    }
    getContainer() {
        return this.container;
    }
    createOverlay() {
        this.ref = this.overlay.create({
            positionStrategy: this.positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            ...this.overlayConfig,
        });
        this.updatePositionWhenStable(this.ref);
    }
    renderContainer() {
        const containerContext = this.createContainerContext();
        if (!this.container) {
            this.container = createContainer(this.ref, this.componentType, containerContext, this.componentFactoryResolver);
        }
        this.container.instance.renderContent();
    }
    updateContext() {
        const containerContext = this.createContainerContext();
        Object.assign(this.container.instance, containerContext);
        this.container.instance.renderContent();
        this.container.changeDetectorRef.detectChanges();
    }
    createContainerContext() {
        return {
            content: this.content,
            context: this.context,
            cfr: this.componentFactoryResolver,
            position: this.lastAppliedPosition,
        };
    }
    /**
     * Dimensions of the container may change after content update. So we listen to zone.stable event to
     * reposition the container.
     */
    updatePositionWhenStable(overlay) {
        const overlayDestroy$ = this.overlayDestroy$.pipe(filter((destroyedOverlay) => destroyedOverlay === overlay));
        this.zone.onStable.pipe(take(1), takeUntil(merge(this.destroy$, overlayDestroy$))).subscribe(() => this.updatePosition());
    }
    updatePosition() {
        if (this.ref) {
            this.ref.updatePosition();
        }
    }
    hasOverlayInContainer() {
        return this.overlayContainer.getContainerElement().contains(this.ref.hostElement);
    }
    disposeOverlayRef() {
        if (this.ref) {
            this.ref.dispose();
            this.overlayDestroy$.next(this.ref);
            this.ref = null;
            this.container = null;
        }
    }
}
CjDynamicOverlay.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlay, deps: [{ token: CjOverlayService }, { token: i0.ComponentFactoryResolver }, { token: i0.NgZone }, { token: CjOverlayContainer }], target: i0.ɵɵFactoryTarget.Injectable });
CjDynamicOverlay.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlay });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlay, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjOverlayService }, { type: i0.ComponentFactoryResolver }, { type: i0.NgZone }, { type: CjOverlayContainer }]; } });

class CjDynamicOverlayChange extends SimpleChange {
    constructor(previousValue, currentValue, firstChange = false) {
        super(previousValue, currentValue, firstChange);
    }
    isChanged() {
        return this.currentValue !== this.previousValue;
    }
}
class CjDynamicOverlayHandler {
    constructor(positionBuilder, triggerStrategyBuilder, dynamicOverlayService, directionService) {
        this.positionBuilder = positionBuilder;
        this.triggerStrategyBuilder = triggerStrategyBuilder;
        this.dynamicOverlayService = dynamicOverlayService;
        this.directionService = directionService;
        this._context = {};
        this._trigger = CjTrigger.NOOP;
        this._disabled = false;
        this._position = CjPosition.TOP;
        this._adjustment = CjAdjustment.NOOP;
        this._offset = 8;
        this._overlayConfig = {};
        this.changes = {};
        this.destroy$ = new Subject();
    }
    host(host) {
        this.changes.host = new CjDynamicOverlayChange(this._host, host);
        this._host = host;
        return this;
    }
    trigger(trigger) {
        this.changes.trigger = new CjDynamicOverlayChange(this._trigger, trigger);
        this._trigger = trigger;
        return this;
    }
    disabled(disabled) {
        this.changes.disabled = new CjDynamicOverlayChange(this._disabled, disabled);
        this._disabled = disabled;
        return this;
    }
    position(position) {
        this.changes.position = new CjDynamicOverlayChange(this._position, position);
        this._position = position;
        return this;
    }
    adjustment(adjustment) {
        this.changes.adjustment = new CjDynamicOverlayChange(this._adjustment, adjustment);
        this._adjustment = adjustment;
        return this;
    }
    componentType(componentType) {
        this.changes.componentType = new CjDynamicOverlayChange(this._componentType, componentType);
        this._componentType = componentType;
        return this;
    }
    content(content) {
        this.changes.content = new CjDynamicOverlayChange(this._content, content);
        this._content = content;
        return this;
    }
    context(context) {
        this.changes.context = new CjDynamicOverlayChange(this._context, context);
        this._context = context;
        return this;
    }
    offset(offset) {
        this.changes.offset = new CjDynamicOverlayChange(this._offset, offset);
        this._offset = offset;
        return this;
    }
    overlayConfig(overlayConfig) {
        this.changes.overlayConfig = new CjDynamicOverlayChange(this._overlayConfig, overlayConfig);
        this._overlayConfig = overlayConfig;
        return this;
    }
    build() {
        if (!this._componentType || !this._host) {
            throw Error(`CjDynamicOverlayHandler: at least 'componentType' and 'host' should be
      passed before building a dynamic overlay.`);
        }
        this.dynamicOverlay = this.dynamicOverlayService.create(this._componentType, this._content, this._context, this.createPositionStrategy(), this._overlayConfig, this._disabled);
        this.connect();
        this.clearChanges();
        return this.dynamicOverlay;
    }
    rebuild() {
        /**
         * we should not throw here
         * as we use rebuilt in lifecycle hooks
         * which it could be called before the build
         * so we just ignore this call
         */
        if (!this.dynamicOverlay) {
            return undefined;
        }
        if (this.isPositionStrategyUpdateRequired()) {
            this.dynamicOverlay.setPositionStrategy(this.createPositionStrategy());
        }
        if (this.isTriggerStrategyUpdateRequired()) {
            this.connect();
        }
        if (this.isContainerRerenderRequired()) {
            this.dynamicOverlay.setContentAndContext(this._content, this._context);
        }
        if (this.isComponentTypeUpdateRequired()) {
            this.dynamicOverlay.setComponent(this._componentType);
        }
        if (this.isOverlayConfigUpdateRequired()) {
            this.dynamicOverlay.setOverlayConfig(this._overlayConfig);
        }
        if (this.isDisabledUpdated()) {
            this.dynamicOverlay.setDisabled(this._disabled);
        }
        this.clearChanges();
        return this.dynamicOverlay;
    }
    connect() {
        if (!this.dynamicOverlay) {
            throw new Error(`CjDynamicOverlayHandler: cannot connect to DynamicOverlay
      as it is not created yet. Call build() first`);
        }
        this.disconnect();
        this.subscribeOnTriggers(this.dynamicOverlay);
        this.subscribeOnDirectionChange();
    }
    disconnect() {
        if (this.triggerStrategy) {
            this.triggerStrategy.destroy();
        }
    }
    destroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.disconnect();
        this.clearChanges();
        if (this.dynamicOverlay) {
            this.dynamicOverlay.dispose();
        }
    }
    createPositionStrategy() {
        return this.positionBuilder
            .connectedTo(this._host)
            .position(this._position)
            .adjustment(this._adjustment)
            .offset(this._offset)
            .direction(this.directionService.getDirection());
    }
    subscribeOnTriggers(dynamicOverlay) {
        this.triggerStrategy = this.triggerStrategyBuilder
            .trigger(this._trigger)
            .host(this._host.nativeElement)
            .container(() => dynamicOverlay.getContainer())
            .build();
        this.triggerStrategy.show$.subscribe(() => dynamicOverlay.show());
        this.triggerStrategy.hide$.subscribe(() => dynamicOverlay.hide());
    }
    subscribeOnDirectionChange() {
        this.directionService
            .onDirectionChange()
            .pipe(skip(1), takeUntil(this.destroy$))
            .subscribe(() => {
            this.dynamicOverlay.setPositionStrategy(this.createPositionStrategy());
        });
    }
    isContainerRerenderRequired() {
        return this.isContentUpdated() || this.isContextUpdated() || this.isPositionStrategyUpdateRequired();
    }
    isPositionStrategyUpdateRequired() {
        return this.isAdjustmentUpdated() || this.isPositionUpdated() || this.isOffsetUpdated() || this.isHostUpdated();
    }
    isTriggerStrategyUpdateRequired() {
        return this.isTriggerUpdated() || this.isHostUpdated();
    }
    isComponentTypeUpdateRequired() {
        return this.isComponentTypeUpdated();
    }
    isOverlayConfigUpdateRequired() {
        return this.isOverlayConfigUpdated();
    }
    isComponentTypeUpdated() {
        return this.changes.componentType && this.changes.componentType.isChanged();
    }
    isContentUpdated() {
        return this.changes.content && this.changes.content.isChanged();
    }
    isContextUpdated() {
        return this.changes.context && this.changes.context.isChanged();
    }
    isAdjustmentUpdated() {
        return this.changes.adjustment && this.changes.adjustment.isChanged();
    }
    isPositionUpdated() {
        return this.changes.position && this.changes.position.isChanged();
    }
    isHostUpdated() {
        return this.changes.host && this.changes.host.isChanged();
    }
    isTriggerUpdated() {
        return this.changes.trigger && this.changes.trigger.isChanged();
    }
    isOffsetUpdated() {
        return this.changes.offset && this.changes.offset.isChanged();
    }
    isOverlayConfigUpdated() {
        return this.changes.overlayConfig && this.changes.overlayConfig.isChanged();
    }
    isDisabledUpdated() {
        return this.changes.disabled && this.changes.disabled.isChanged();
    }
    clearChanges() {
        this.changes = {};
    }
}
CjDynamicOverlayHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlayHandler, deps: [{ token: CjPositionBuilderService }, { token: CjTriggerStrategyBuilderService }, { token: CjDynamicOverlay }, { token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Injectable });
CjDynamicOverlayHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlayHandler });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDynamicOverlayHandler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CjPositionBuilderService }, { type: CjTriggerStrategyBuilderService }, { type: CjDynamicOverlay }, { type: CjLayoutDirectionService }]; } });

class CjTooltipComponent {
    constructor() {
        /**
         * Popover position relatively host element.
         * */
        this.position = CjPosition.TOP;
        this.context = {};
    }
    get binding() {
        return `${this.position}`;
    }
    get show() {
        return true;
    }
    /**
     * The method is empty since we don't need to do anything additionally
     * render is handled by change detection
     */
    renderContent() { }
}
CjTooltipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjTooltipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjTooltipComponent, selector: "cj-tooltip", inputs: { content: "content", position: "position", context: "context" }, host: { properties: { "class": "this.binding", "@showTooltip": "this.show" } }, ngImport: i0, template: `
    <span class="arrow"></span>
    <div class="content">
      <cj-icon *ngIf="context?.icon" [config]="context.icon"></cj-icon>
      <span *ngIf="content">{{ content }}</span>
    </div>
  `, isInline: true, styles: [":host{z-index:10000}:host .content{display:flex;align-items:center}:host.right .content{flex-direction:row-reverse}:host .arrow{position:absolute;width:0;height:0}:host cj-icon+span,:host svg+span{margin-left:.5rem}:host.right cj-icon+span,:host.right svg+span{margin-right:.5rem}:host .arrow{border-left:5px solid transparent;border-right:5px solid transparent}:host(.bottom) .arrow{top:-4px;left:calc(50% - 4px)}:host(.bottom-start) .arrow{top:-4px}[dir=ltr] :host(.bottom-start) .arrow{right:4px}[dir=rtl] :host(.bottom-start) .arrow{left:4px}:host(.bottom-end) .arrow{top:-4px}[dir=ltr] :host(.bottom-end) .arrow{left:4px}[dir=rtl] :host(.bottom-end) .arrow{right:4px}:host(.left) .arrow,:host(.start) .arrow{top:calc(50% - 1.6px)}[dir=ltr] :host(.left) .arrow,[dir=ltr] :host(.start) .arrow{right:-6px;transform:rotate(90deg)}[dir=rtl] :host(.left) .arrow,[dir=rtl] :host(.start) .arrow{left:-6px;transform:rotate(270deg)}:host(.start-top) .arrow{right:-6px;bottom:4px;transform:rotate(90deg)}:host(.start-bottom) .arrow{right:-6px;top:4px;transform:rotate(90deg)}:host(.top) .arrow{bottom:-4px;left:calc(50% - 4px);transform:rotate(180deg)}:host(.top-start) .arrow{bottom:-3px;transform:rotate(180deg)}[dir=ltr] :host(.top-start) .arrow{right:4px}[dir=rtl] :host(.top-start) .arrow{left:4px}:host(.top-end) .arrow{bottom:-3px;transform:rotate(180deg)}[dir=ltr] :host(.top-end) .arrow{left:4px}[dir=rtl] :host(.top-end) .arrow{right:4px}:host(.right) .arrow,:host(.end) .arrow{top:calc(50% - 1.6px)}[dir=ltr] :host(.right) .arrow,[dir=ltr] :host(.end) .arrow{left:-6px;transform:rotate(270deg)}[dir=rtl] :host(.right) .arrow,[dir=rtl] :host(.end) .arrow{right:-6px;transform:rotate(90deg)}:host(.end-top) .arrow{left:-5.6px;bottom:4px;transform:rotate(270deg)}:host(.end-bottom) .arrow{left:-5.6px;top:4px;transform:rotate(270deg)}\n"], components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [
        trigger('showTooltip', [
            state('in', style({ opacity: 1 })),
            transition('void => *', [
                style({ opacity: 0 }),
                animate(100),
            ]),
            transition('* => void', [
                animate(100, style({ opacity: 0 })),
            ]),
        ]),
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-tooltip', template: `
    <span class="arrow"></span>
    <div class="content">
      <cj-icon *ngIf="context?.icon" [config]="context.icon"></cj-icon>
      <span *ngIf="content">{{ content }}</span>
    </div>
  `, animations: [
                        trigger('showTooltip', [
                            state('in', style({ opacity: 1 })),
                            transition('void => *', [
                                style({ opacity: 0 }),
                                animate(100),
                            ]),
                            transition('* => void', [
                                animate(100, style({ opacity: 0 })),
                            ]),
                        ]),
                    ], styles: [":host{z-index:10000}:host .content{display:flex;align-items:center}:host.right .content{flex-direction:row-reverse}:host .arrow{position:absolute;width:0;height:0}:host cj-icon+span,:host svg+span{margin-left:.5rem}:host.right cj-icon+span,:host.right svg+span{margin-right:.5rem}:host .arrow{border-left:5px solid transparent;border-right:5px solid transparent}:host(.bottom) .arrow{top:-4px;left:calc(50% - 4px)}:host(.bottom-start) .arrow{top:-4px}[dir=ltr] :host(.bottom-start) .arrow{right:4px}[dir=rtl] :host(.bottom-start) .arrow{left:4px}:host(.bottom-end) .arrow{top:-4px}[dir=ltr] :host(.bottom-end) .arrow{left:4px}[dir=rtl] :host(.bottom-end) .arrow{right:4px}:host(.left) .arrow,:host(.start) .arrow{top:calc(50% - 1.6px)}[dir=ltr] :host(.left) .arrow,[dir=ltr] :host(.start) .arrow{right:-6px;transform:rotate(90deg)}[dir=rtl] :host(.left) .arrow,[dir=rtl] :host(.start) .arrow{left:-6px;transform:rotate(270deg)}:host(.start-top) .arrow{right:-6px;bottom:4px;transform:rotate(90deg)}:host(.start-bottom) .arrow{right:-6px;top:4px;transform:rotate(90deg)}:host(.top) .arrow{bottom:-4px;left:calc(50% - 4px);transform:rotate(180deg)}:host(.top-start) .arrow{bottom:-3px;transform:rotate(180deg)}[dir=ltr] :host(.top-start) .arrow{right:4px}[dir=rtl] :host(.top-start) .arrow{left:4px}:host(.top-end) .arrow{bottom:-3px;transform:rotate(180deg)}[dir=ltr] :host(.top-end) .arrow{left:4px}[dir=rtl] :host(.top-end) .arrow{right:4px}:host(.right) .arrow,:host(.end) .arrow{top:calc(50% - 1.6px)}[dir=ltr] :host(.right) .arrow,[dir=ltr] :host(.end) .arrow{left:-6px;transform:rotate(270deg)}[dir=rtl] :host(.right) .arrow,[dir=rtl] :host(.end) .arrow{right:-6px;transform:rotate(90deg)}:host(.end-top) .arrow{left:-5.6px;bottom:4px;transform:rotate(270deg)}:host(.end-bottom) .arrow{left:-5.6px;top:4px;transform:rotate(270deg)}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { content: [{
                type: Input
            }], position: [{
                type: Input
            }], binding: [{
                type: HostBinding,
                args: ['class']
            }], show: [{
                type: HostBinding,
                args: ['@showTooltip']
            }], context: [{
                type: Input
            }] } });

class CjTooltipDirective {
    constructor(hostRef, dynamicOverlayHandler) {
        this.hostRef = hostRef;
        this.dynamicOverlayHandler = dynamicOverlayHandler;
        this.destroy$ = new Subject();
        this.tooltipComponent = CjTooltipComponent;
        this.context = {};
        /**
         * Position will be calculated relatively host element based on the position.
         * Can be top, right, bottom, left, start or end.
         */
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.position = CjPosition.TOP;
        this._adjustment = CjAdjustment.CLOCKWISE;
        this._tooltipClass = '';
        /**
         * Describes when the container will be shown.
         * Available options: `click`, `hover`, `hint`, `focus` and `noop`
         * */
        this.trigger = CjTrigger.HINT;
        /**
         * Determines tooltip overlay offset (in pixels).
         **/
        this.offset = 8;
        /** Disables the display of the tooltip. */
        this.disabled = false;
        this.cjTooltipShowStateChange = new EventEmitter();
        this.overlayConfig = { panelClass: this.tooltipClass };
    }
    /**
     * Container position will change automatically based on this strategy if container can't fit view port.
     * Set this property to `noop` value if you want to disable automatic adjustment.
     * Available values: `clockwise` (default), `counterclockwise`, `vertical`, `horizontal`, `noop`.
     */
    get adjustment() {
        return this._adjustment;
    }
    set adjustment(value) {
        this._adjustment = value;
    }
    get tooltipClass() {
        return this._tooltipClass;
    }
    set tooltipClass(value) {
        if (value !== this.tooltipClass) {
            this._tooltipClass = value;
            this.overlayConfig = { panelClass: this.tooltipClass };
        }
    }
    /**
     * Accepts icon name or icon config object
     * @param {string | CjIconConfig} icon name or config object
     */
    set icon(icon) {
        this.context = Object.assign(this.context, { icon });
    }
    get isShown() {
        return !!(this.dynamicOverlay && this.dynamicOverlay.isAttached);
    }
    ngOnInit() {
        this.dynamicOverlayHandler.host(this.hostRef).componentType(this.tooltipComponent).offset(this.offset);
    }
    ngOnChanges() {
        this.rebuild();
    }
    ngAfterViewInit() {
        this.dynamicOverlay = this.configureDynamicOverlay().build();
        this.dynamicOverlay.isShown
            .pipe(skip(1), takeUntil(this.destroy$))
            .subscribe((isShown) => this.cjTooltipShowStateChange.emit({ isShown }));
    }
    rebuild() {
        this.dynamicOverlay = this.configureDynamicOverlay().rebuild();
    }
    show() {
        this.dynamicOverlay.show();
    }
    hide() {
        this.dynamicOverlay.hide();
    }
    toggle() {
        this.dynamicOverlay.toggle();
    }
    ngOnDestroy() {
        this.dynamicOverlayHandler.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }
    configureDynamicOverlay() {
        return this.dynamicOverlayHandler
            .position(this.position)
            .trigger(this.trigger)
            .disabled(this.disabled)
            .adjustment(this.adjustment)
            .content(this.content)
            .context(this.context)
            .overlayConfig(this.overlayConfig);
    }
}
CjTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipDirective, deps: [{ token: i0.ElementRef }, { token: CjDynamicOverlayHandler }], target: i0.ɵɵFactoryTarget.Directive });
CjTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjTooltipDirective, selector: "[cjTooltip]", inputs: { content: ["cjTooltip", "content"], position: ["cjTooltipPlacement", "position"], adjustment: ["cjTooltipAdjustment", "adjustment"], tooltipClass: ["cjTooltipClass", "tooltipClass"], icon: ["cjTooltipIcon", "icon"], trigger: ["cjTooltipTrigger", "trigger"], offset: ["cjTooltipOffset", "offset"], disabled: ["cjTooltipDisabled", "disabled"] }, outputs: { cjTooltipShowStateChange: "cjTooltipShowStateChange" }, providers: [CjDynamicOverlayHandler, CjDynamicOverlay], exportAs: ["cjTooltip"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjTooltip]',
                    exportAs: 'cjTooltip',
                    providers: [CjDynamicOverlayHandler, CjDynamicOverlay],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CjDynamicOverlayHandler }]; }, propDecorators: { content: [{
                type: Input,
                args: ['cjTooltip']
            }], position: [{
                type: Input,
                args: ['cjTooltipPlacement']
            }], adjustment: [{
                type: Input,
                args: ['cjTooltipAdjustment']
            }], tooltipClass: [{
                type: Input,
                args: ['cjTooltipClass']
            }], icon: [{
                type: Input,
                args: ['cjTooltipIcon']
            }], trigger: [{
                type: Input,
                args: ['cjTooltipTrigger']
            }], offset: [{
                type: Input,
                args: ['cjTooltipOffset']
            }], disabled: [{
                type: Input,
                args: ['cjTooltipDisabled']
            }], cjTooltipShowStateChange: [{
                type: Output
            }] } });

var CjToggleStates;
(function (CjToggleStates) {
    CjToggleStates["Expanded"] = "expanded";
    CjToggleStates["Collapsed"] = "collapsed";
    CjToggleStates["Left"] = "left";
    CjToggleStates["Right"] = "right";
})(CjToggleStates || (CjToggleStates = {}));
class CjMenuItemComponent {
    constructor(menuService, directionService) {
        this.menuService = menuService;
        this.directionService = directionService;
        this.menuItem = null;
        this.hoverItem = new EventEmitter();
        this.toggleSubMenu = new EventEmitter();
        this.toggleExtendedMenu = new EventEmitter();
        this.selectItem = new EventEmitter();
        this.itemClick = new EventEmitter();
        this.actionClick = new EventEmitter();
        this.destroy$ = new Subject();
    }
    ngDoCheck() {
        this.toggleState = this.menuItem.expanded ? CjToggleStates.Expanded : CjToggleStates.Collapsed;
    }
    ngAfterViewInit() {
        this.menuService.onSubmenuToggle()
            .pipe(filter(({ item }) => item === this.menuItem), map(({ item }) => item.expanded), takeUntil(this.destroy$))
            .subscribe(isExpanded => this.toggleState = isExpanded ? CjToggleStates.Expanded : CjToggleStates.Collapsed);
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    onToggleSubMenu(item) {
        this.toggleSubMenu.emit(item);
    }
    onToggleExtendedMenu(item) {
        this.toggleExtendedMenu.emit(item);
    }
    onHoverItem(item) {
        this.hoverItem.emit(item);
    }
    onSelectItem(item) {
        this.selectItem.emit(item);
    }
    onItemClick(item) {
        this.itemClick.emit(item);
    }
    onActionClick(item) {
        this.actionClick.emit(item);
    }
    onActionExecute(item) {
        this.onActionClick(item);
        item.action.click();
    }
}
CjMenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuItemComponent, deps: [{ token: CjMenuService }, { token: CjLayoutDirectionService }], target: i0.ɵɵFactoryTarget.Component });
CjMenuItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjMenuItemComponent, selector: "[cjMenuItem]", inputs: { menuItem: "menuItem", badge: "badge" }, outputs: { hoverItem: "hoverItem", toggleSubMenu: "toggleSubMenu", toggleExtendedMenu: "toggleExtendedMenu", selectItem: "selectItem", itemClick: "itemClick", actionClick: "actionClick" }, ngImport: i0, template: "<span *ngIf=\"menuItem.group\">\n  <div class=\"menu-item-label\">\n    <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n      <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n    </div>\n    {{ menuItem.title }}\n  </div>\n</span>\n\n<div\n  *ngIf=\"menuItem.route && !menuItem.url && !menuItem.children && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [routerLink]=\"menuItem.route\"\n    [queryParams]=\"menuItem.queryParams\"\n    [fragment]=\"menuItem.fragment\"\n    [queryParamsHandling]=\"menuItem.queryParamsHandling\"\n    [preserveFragment]=\"menuItem.preserveFragment\"\n    [skipLocationChange]=\"menuItem.skipLocationChange\"\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"onItemClick(menuItem);\">\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<div\n  *ngIf=\"menuItem.url && !menuItem.children && !menuItem.route && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [attr.href]=\"menuItem.url\"\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"onSelectItem(menuItem)\">\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<div\n  *ngIf=\"!menuItem.children && !menuItem.route && !menuItem.url && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"$event.preventDefault(); onItemClick(menuItem);\"\n  >\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<ng-container *ngIf=\"menuItem.children\">\n  <div\n    class=\"menu-item-container\"\n    [class.active]=\"menuItem.selected\"\n    [class.with-icon]=\"menuItem.icon\"\n    [class.expanded]=\"menuItem.expanded\">\n    <a\n      (click)=\"$event.preventDefault(); onToggleSubMenu(menuItem);\"\n      [attr.target]=\"menuItem.target\"\n      (mouseenter)=\"onHoverItem(menuItem)\"\n      href=\"#\">\n      <div class=\"menu-item-label\">\n        <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n          <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n        </div>\n        <span class=\"menu-title\">{{ menuItem.title }}</span>\n        <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n      </div>\n\n      <cj-icon\n        *ngIf=\"menuItem.level === 0\"\n        [class.expanded]=\"menuItem.expanded\"\n        class=\"expand-state\"\n        icon=\"chevron-right-outline\"\n      ></cj-icon>\n\n      <cj-icon *ngIf=\"menuItem.level === 1\" class=\"next-state\" icon=\"arrow-back\"></cj-icon>\n    </a>\n  </div>\n  <ul\n    *ngIf=\"menuItem.level === 0\"\n    [class.collapsed]=\"!(menuItem.children && menuItem.expanded)\"\n    [class.expanded]=\"menuItem.expanded\"\n    [@toggle]=\"toggleState\"\n    class=\"menu-submenu-items\">\n    <ng-container *ngFor=\"let item of menuItem.children\">\n      <li cjMenuItem *ngIf=\"!item.hidden\"\n        [attr.id]=\"item.id || false\"\n        [menuItem]=\"item\"\n        [class.menu-group]=\"item.group\"\n        (hoverItem)=\"onHoverItem($event)\"\n        (toggleSubMenu)=\"onToggleExtendedMenu($event)\"\n        (selectItem)=\"onSelectItem($event)\"\n        (itemClick)=\"onItemClick($event)\"\n        (actionClick)=\"onActionClick($event)\"\n        class=\"menu-item\">\n    </li>\n  </ng-container>\n</ul>\n</ng-container>\n\n<!-- <ng-template #badgeTemplate>\n  <cj-badge [text]=\"badge.text\" [dotMode]=\"badge.dotMode\" [status]=\"badge.status\">\n  </cj-badge>\n</ng-template> -->\n\n<ng-template #actionTemplate>\n  <button\n    cjButton\n    class=\"menu-item-action\"\n    [cjTooltip]=\"menuItem.action.tooltip\"\n    [cjTooltipTrigger]=\"menuItem.action.tooltip ? 'hint' : 'noop'\"\n    [class.active]=\"menuItem.action.selected\"\n    (click)=\"menuItem.action.click ? onActionExecute(menuItem) : null\">\n    <cj-icon [icon]=\"menuItem.action.icon\" [pack]=\"menuItem.action.pack\"></cj-icon>\n  </button>\n</ng-template>\n", components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }, { type: CjMenuItemComponent, selector: "[cjMenuItem]", inputs: ["menuItem", "badge"], outputs: ["hoverItem", "toggleSubMenu", "toggleExtendedMenu", "selectItem", "itemClick", "actionClick"] }, { type: CjButtonComponent, selector: "button[cjButton],a[cjButton],input[type=\"button\"][cjButton],input[type=\"submit\"][cjButton]" }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i6.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo", "routerLink"] }, { type: CjTooltipDirective, selector: "[cjTooltip]", inputs: ["cjTooltip", "cjTooltipPlacement", "cjTooltipAdjustment", "cjTooltipClass", "cjTooltipIcon", "cjTooltipTrigger", "cjTooltipOffset", "cjTooltipDisabled"], outputs: ["cjTooltipShowStateChange"], exportAs: ["cjTooltip"] }, { type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], animations: [
        trigger('toggle', [
            state(CjToggleStates.Collapsed, style({ height: '0', margin: '0' })),
            state(CjToggleStates.Expanded, style({ height: '*' })),
            transition(`${CjToggleStates.Collapsed} <=> ${CjToggleStates.Expanded}`, animate('0.2s ease-in-out')),
        ]),
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: '[cjMenuItem]', animations: [
                        trigger('toggle', [
                            state(CjToggleStates.Collapsed, style({ height: '0', margin: '0' })),
                            state(CjToggleStates.Expanded, style({ height: '*' })),
                            transition(`${CjToggleStates.Collapsed} <=> ${CjToggleStates.Expanded}`, animate('0.2s ease-in-out')),
                        ]),
                    ], template: "<span *ngIf=\"menuItem.group\">\n  <div class=\"menu-item-label\">\n    <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n      <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n    </div>\n    {{ menuItem.title }}\n  </div>\n</span>\n\n<div\n  *ngIf=\"menuItem.route && !menuItem.url && !menuItem.children && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [routerLink]=\"menuItem.route\"\n    [queryParams]=\"menuItem.queryParams\"\n    [fragment]=\"menuItem.fragment\"\n    [queryParamsHandling]=\"menuItem.queryParamsHandling\"\n    [preserveFragment]=\"menuItem.preserveFragment\"\n    [skipLocationChange]=\"menuItem.skipLocationChange\"\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"onItemClick(menuItem);\">\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<div\n  *ngIf=\"menuItem.url && !menuItem.children && !menuItem.route && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [attr.href]=\"menuItem.url\"\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"onSelectItem(menuItem)\">\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<div\n  *ngIf=\"!menuItem.children && !menuItem.route && !menuItem.url && !menuItem.group\"\n  class=\"menu-item-container\"\n  [class.active]=\"menuItem.selected\"\n  [class.with-icon]=\"menuItem.icon\"\n  [class.with-action]=\"menuItem.action\">\n  <a\n    [attr.target]=\"menuItem.target\"\n    (mouseenter)=\"onHoverItem(menuItem)\"\n    (click)=\"$event.preventDefault(); onItemClick(menuItem);\"\n  >\n    <div class=\"menu-item-label\">\n      <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n        <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n      </div>\n      <span class=\"menu-title\">{{ menuItem.title }}</span>\n      <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n    </div>\n    <cj-icon\n      *ngIf=\"menuItem.level !== 0 && menuItem.action\"\n      class=\"action-state\"\n      icon=\"arrow-back\"\n      [cjTooltip]=\"menuItem.tooltip\"\n      [cjTooltipTrigger]=\"menuItem.tooltip ? 'hint' : 'noop'\"\n    ></cj-icon>\n  </a>\n  <ng-container *ngIf=\"menuItem.action\" [ngTemplateOutlet]=\"actionTemplate\"></ng-container>\n</div>\n\n<ng-container *ngIf=\"menuItem.children\">\n  <div\n    class=\"menu-item-container\"\n    [class.active]=\"menuItem.selected\"\n    [class.with-icon]=\"menuItem.icon\"\n    [class.expanded]=\"menuItem.expanded\">\n    <a\n      (click)=\"$event.preventDefault(); onToggleSubMenu(menuItem);\"\n      [attr.target]=\"menuItem.target\"\n      (mouseenter)=\"onHoverItem(menuItem)\"\n      href=\"#\">\n      <div class=\"menu-item-label\">\n        <div class=\"menu-icon-container\" *ngIf=\"menuItem.icon\">\n          <cj-icon class=\"menu-icon\" [config]=\"menuItem.icon\"></cj-icon>\n        </div>\n        <span class=\"menu-title\">{{ menuItem.title }}</span>\n        <!-- <ng-container *ngIf=\"badge\" [ngTemplateOutlet]=\"badgeTemplate\"></ng-container> -->\n      </div>\n\n      <cj-icon\n        *ngIf=\"menuItem.level === 0\"\n        [class.expanded]=\"menuItem.expanded\"\n        class=\"expand-state\"\n        icon=\"chevron-right-outline\"\n      ></cj-icon>\n\n      <cj-icon *ngIf=\"menuItem.level === 1\" class=\"next-state\" icon=\"arrow-back\"></cj-icon>\n    </a>\n  </div>\n  <ul\n    *ngIf=\"menuItem.level === 0\"\n    [class.collapsed]=\"!(menuItem.children && menuItem.expanded)\"\n    [class.expanded]=\"menuItem.expanded\"\n    [@toggle]=\"toggleState\"\n    class=\"menu-submenu-items\">\n    <ng-container *ngFor=\"let item of menuItem.children\">\n      <li cjMenuItem *ngIf=\"!item.hidden\"\n        [attr.id]=\"item.id || false\"\n        [menuItem]=\"item\"\n        [class.menu-group]=\"item.group\"\n        (hoverItem)=\"onHoverItem($event)\"\n        (toggleSubMenu)=\"onToggleExtendedMenu($event)\"\n        (selectItem)=\"onSelectItem($event)\"\n        (itemClick)=\"onItemClick($event)\"\n        (actionClick)=\"onActionClick($event)\"\n        class=\"menu-item\">\n    </li>\n  </ng-container>\n</ul>\n</ng-container>\n\n<!-- <ng-template #badgeTemplate>\n  <cj-badge [text]=\"badge.text\" [dotMode]=\"badge.dotMode\" [status]=\"badge.status\">\n  </cj-badge>\n</ng-template> -->\n\n<ng-template #actionTemplate>\n  <button\n    cjButton\n    class=\"menu-item-action\"\n    [cjTooltip]=\"menuItem.action.tooltip\"\n    [cjTooltipTrigger]=\"menuItem.action.tooltip ? 'hint' : 'noop'\"\n    [class.active]=\"menuItem.action.selected\"\n    (click)=\"menuItem.action.click ? onActionExecute(menuItem) : null\">\n    <cj-icon [icon]=\"menuItem.action.icon\" [pack]=\"menuItem.action.pack\"></cj-icon>\n  </button>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: CjMenuService }, { type: CjLayoutDirectionService }]; }, propDecorators: { menuItem: [{
                type: Input
            }], badge: [{
                type: Input
            }], hoverItem: [{
                type: Output
            }], toggleSubMenu: [{
                type: Output
            }], toggleExtendedMenu: [{
                type: Output
            }], selectItem: [{
                type: Output
            }], itemClick: [{
                type: Output
            }], actionClick: [{
                type: Output
            }] } });
class CjMenuComponent {
    constructor(window, platformId, menuInternalService, router) {
        this.window = window;
        this.platformId = platformId;
        this.menuInternalService = menuInternalService;
        this.router = router;
        this._autoCollapse = false;
        this.destroy$ = new Subject();
        this.showLevelTwo = false;
    }
    /**
     * Collapse all opened submenus on the toggle event
     * Default value is "false"
     * @type boolean
     */
    get autoCollapse() {
        return this._autoCollapse;
    }
    set autoCollapse(value) {
        this._autoCollapse = convertToBoolProperty(value);
    }
    ngOnInit() {
        this.menuInternalService.prepareItems(this.items);
        this.menuInternalService
            .onAddItem()
            .pipe(filter((data) => this.compareTag(data.tag)), takeUntil(this.destroy$))
            .subscribe(data => this.onAddItem(data));
        this.menuInternalService
            .onNavigateHome()
            .pipe(filter((data) => this.compareTag(data.tag)), takeUntil(this.destroy$))
            .subscribe(() => this.navigateHome());
        this.menuInternalService
            .onGetSelectedItem()
            .pipe(filter((data) => this.compareTag(data.tag)), takeUntil(this.destroy$))
            .subscribe((data) => {
            data.listener.next({ tag: this.tag, item: this.getSelectedItem(this.items) });
        });
        this.menuInternalService
            .onCollapseAll()
            .pipe(filter((data) => this.compareTag(data.tag)), takeUntil(this.destroy$))
            .subscribe(() => this.collapseAll());
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
            .subscribe(() => {
            this.menuInternalService.selectFromUrl(this.items, this.tag, this.autoCollapse);
        });
    }
    ngAfterViewInit() {
        setTimeout(() => this.menuInternalService.selectFromUrl(this.items, this.tag, this.autoCollapse));
    }
    onAddItem(data) {
        this.items.push(...data.items);
        this.menuInternalService.prepareItems(this.items);
        this.menuInternalService.selectFromUrl(this.items, this.tag, this.autoCollapse);
    }
    onHoverItem(item) {
        this.menuInternalService.itemHover(item, this.tag);
    }
    onToggleSubMenu(item) {
        if (this.autoCollapse) {
            this.menuInternalService.collapseAll(this.items, this.tag, item);
        }
        item.expanded = !item.expanded;
        this.menuInternalService.submenuToggle(item, this.tag);
    }
    onToggleExtendedMenu(item) {
        if (!this.showLevelTwo) {
            this.levelTwoItems = [...item.children];
            this.levelTwoItems.unshift({ title: item.title, group: true });
            this.menuInternalService.prepareItems(this.levelTwoItems);
        }
        this.showLevelTwo = !this.showLevelTwo;
    }
    // TODO: is not fired on page reload
    onSelectItem(item, isAction = false) {
        this.menuInternalService.selectItem(item, this.items, this.autoCollapse, this.tag, isAction);
    }
    onItemClick(item) {
        this.menuInternalService.itemClick(item, this.tag);
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    navigateHome() {
        const homeItem = this.getHomeItem(this.items);
        if (homeItem) {
            if (homeItem.route) {
                const extras = {
                    queryParams: homeItem.queryParams,
                    queryParamsHandling: homeItem.queryParamsHandling,
                    fragment: homeItem.fragment,
                    preserveFragment: homeItem.preserveFragment,
                };
                this.router.navigate([homeItem.route], extras);
            }
            if (homeItem.url && isPlatformBrowser(this.platformId)) {
                this.window.location.href = homeItem.url;
            }
        }
    }
    collapseAll() {
        this.menuInternalService.collapseAll(this.items, this.tag);
    }
    getHomeItem(items) {
        for (const item of items) {
            if (item.home) {
                return item;
            }
            const homeItem = item.children && this.getHomeItem(item.children);
            if (homeItem) {
                return homeItem;
            }
        }
        return undefined;
    }
    compareTag(tag) {
        return !tag || tag === this.tag;
    }
    getSelectedItem(items) {
        let selected = null;
        items.forEach((item) => {
            if (item.selected) {
                selected = item;
            }
            if (item.selected && item.children && item.children.length > 0) {
                selected = this.getSelectedItem(item.children);
            }
        });
        return selected;
    }
}
CjMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuComponent, deps: [{ token: CJ_WINDOW }, { token: PLATFORM_ID }, { token: CjMenuInternalService }, { token: i6.Router }], target: i0.ɵɵFactoryTarget.Component });
CjMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjMenuComponent, selector: "cj-menu", inputs: { tag: "tag", items: "items", autoCollapse: "autoCollapse" }, ngImport: i0, template: `
    <div class="menu-items-container" [@slide]="showLevelTwo ? 'right' : 'left'">
      <ul class="menu-items">
        <ng-container *ngFor="let item of items">
          <li cjMenuItem *ngIf="!item.hidden"
            [attr.id]="item.id || false"
            [menuItem]="item"
            [badge]="item.badge"
            [class.menu-group]="item.group"
            (hoverItem)="onHoverItem($event)"
            (toggleSubMenu)="onToggleSubMenu($event)"
            (toggleExtendedMenu)="onToggleExtendedMenu($event)"
            (selectItem)="onSelectItem($event)"
            (itemClick)="onItemClick($event)"
            (actionClick)="onSelectItem($event, true)"
            class="menu-item">
          </li>
        </ng-container>
      </ul>

      <ul class="menu-items menu-items-extended">
        <li class="menu-item item-previous">
          <div class="menu-item-container">
            <a (click)="$event.preventDefault(); onToggleExtendedMenu();">
              <div class="menu-item-label">
                <div class="menu-icon-container">
                  <cj-icon class="menu-icon previous-state" icon="chevron-left" pack="font-awesome-solid"></cj-icon>
                </div>
                <span class="menu-title">All Products</span>
              </div>
            </a>
          </div>
        </li>
        <ng-container *ngFor="let item of levelTwoItems">
          <li cjMenuItem *ngIf="!item.hidden"
            [attr.id]="item.id || false"
            [menuItem]="item"
            [badge]="item.badge"
            [class.menu-group]="item.group"
            (hoverItem)="onHoverItem($event)"
            (toggleExtendedMenu)="onToggleExtendedMenu($event)"
            (selectItem)="onSelectItem($event)"
            (itemClick)="onItemClick($event)"
            (actionClick)="onSelectItem($event, true)"
            class="menu-item">
          </li>
        </ng-container>
      </ul>
    </div>
  `, isInline: true, styles: [":host ::ng-deep{display:block}:host ::ng-deep .menu-items-container{display:flex;width:200%}:host ::ng-deep .menu-items-container .menu-items{flex:1}:host ::ng-deep .menu-items,:host ::ng-deep .menu-item>.menu-submenu-items{list-style-type:none;overflow:hidden}:host ::ng-deep .menu-items,:host ::ng-deep .menu-submenu-items{display:flex;flex-direction:column}:host ::ng-deep .menu-item:not(.item-previous):first-child{margin-top:1rem}:host ::ng-deep .menu-item.item-previous{margin-top:.5rem;padding-bottom:.5rem}:host ::ng-deep .menu-item.item-previous .previous-state{width:.5rem!important}:host ::ng-deep .menu-item .menu-item-container{display:flex;align-items:center;padding:4px 0}:host ::ng-deep .menu-item a{display:flex;text-decoration:none;align-items:flex-start;justify-content:space-between;flex-grow:1}:host ::ng-deep .menu-item a .menu-item-label{display:flex;text-decoration:none;align-items:flex-start;gap:.625rem}:host ::ng-deep .menu-item a .menu-item-label .menu-title{flex-grow:1;line-height:1.5rem;align-self:center}[dir=rtl] :host ::ng-deep .menu-item a .menu-item-label .menu-title{text-align:right}:host ::ng-deep .menu-item a .menu-item-label .menu-icon-container{display:flex;align-items:center;justify-content:center;flex-shrink:0;text-align:center}:host ::ng-deep .menu-item a .menu-item-label .menu-icon-container .menu-icon{width:1.5rem;height:1.5rem;font-size:1rem}:host ::ng-deep .menu-item a .expand-state{font-size:1.5rem;flex-shrink:0;transition:all .2s ease-in-out}:host ::ng-deep .menu-item a .expand-state.expanded{transform:rotate(90deg)}:host ::ng-deep .menu-item a .action-state,:host ::ng-deep .menu-item a .next-state{font-size:1.25rem;flex-shrink:0;transform:rotate(180deg);margin-top:2px}:host ::ng-deep .menu-item a .next-state{margin-top:4px;margin-right:2px}:host ::ng-deep .menu-item a .action-state{position:relative;left:-8px;transition:left .15s ease-out;opacity:0}:host ::ng-deep .menu-item button{margin:0 .375rem}:host ::ng-deep .menu-item button:focus{box-shadow:none!important}:host ::ng-deep .menu-submenu-items .menu-item:first-child{margin-top:0}:host ::ng-deep .menu-item cj-badge{position:static}:host ::ng-deep .menu-group:not(:first-child){margin-top:1rem}:host ::ng-deep .menu-group span{display:flex}\n"], components: [{ type: CjMenuItemComponent, selector: "[cjMenuItem]", inputs: ["menuItem", "badge"], outputs: ["hoverItem", "toggleSubMenu", "toggleExtendedMenu", "selectItem", "itemClick", "actionClick"] }, { type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [
        trigger('slide', [
            state(CjToggleStates.Left, style({ transform: 'translateX(0)' })),
            state(CjToggleStates.Right, style({ transform: 'translateX(-50%)' })),
            transition(`${CjToggleStates.Left} <=> ${CjToggleStates.Right}`, animate('0.2s ease-in-out')),
        ]),
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-menu', template: `
    <div class="menu-items-container" [@slide]="showLevelTwo ? 'right' : 'left'">
      <ul class="menu-items">
        <ng-container *ngFor="let item of items">
          <li cjMenuItem *ngIf="!item.hidden"
            [attr.id]="item.id || false"
            [menuItem]="item"
            [badge]="item.badge"
            [class.menu-group]="item.group"
            (hoverItem)="onHoverItem($event)"
            (toggleSubMenu)="onToggleSubMenu($event)"
            (toggleExtendedMenu)="onToggleExtendedMenu($event)"
            (selectItem)="onSelectItem($event)"
            (itemClick)="onItemClick($event)"
            (actionClick)="onSelectItem($event, true)"
            class="menu-item">
          </li>
        </ng-container>
      </ul>

      <ul class="menu-items menu-items-extended">
        <li class="menu-item item-previous">
          <div class="menu-item-container">
            <a (click)="$event.preventDefault(); onToggleExtendedMenu();">
              <div class="menu-item-label">
                <div class="menu-icon-container">
                  <cj-icon class="menu-icon previous-state" icon="chevron-left" pack="font-awesome-solid"></cj-icon>
                </div>
                <span class="menu-title">All Products</span>
              </div>
            </a>
          </div>
        </li>
        <ng-container *ngFor="let item of levelTwoItems">
          <li cjMenuItem *ngIf="!item.hidden"
            [attr.id]="item.id || false"
            [menuItem]="item"
            [badge]="item.badge"
            [class.menu-group]="item.group"
            (hoverItem)="onHoverItem($event)"
            (toggleExtendedMenu)="onToggleExtendedMenu($event)"
            (selectItem)="onSelectItem($event)"
            (itemClick)="onItemClick($event)"
            (actionClick)="onSelectItem($event, true)"
            class="menu-item">
          </li>
        </ng-container>
      </ul>
    </div>
  `, animations: [
                        trigger('slide', [
                            state(CjToggleStates.Left, style({ transform: 'translateX(0)' })),
                            state(CjToggleStates.Right, style({ transform: 'translateX(-50%)' })),
                            transition(`${CjToggleStates.Left} <=> ${CjToggleStates.Right}`, animate('0.2s ease-in-out')),
                        ]),
                    ], styles: [":host ::ng-deep{display:block}:host ::ng-deep .menu-items-container{display:flex;width:200%}:host ::ng-deep .menu-items-container .menu-items{flex:1}:host ::ng-deep .menu-items,:host ::ng-deep .menu-item>.menu-submenu-items{list-style-type:none;overflow:hidden}:host ::ng-deep .menu-items,:host ::ng-deep .menu-submenu-items{display:flex;flex-direction:column}:host ::ng-deep .menu-item:not(.item-previous):first-child{margin-top:1rem}:host ::ng-deep .menu-item.item-previous{margin-top:.5rem;padding-bottom:.5rem}:host ::ng-deep .menu-item.item-previous .previous-state{width:.5rem!important}:host ::ng-deep .menu-item .menu-item-container{display:flex;align-items:center;padding:4px 0}:host ::ng-deep .menu-item a{display:flex;text-decoration:none;align-items:flex-start;justify-content:space-between;flex-grow:1}:host ::ng-deep .menu-item a .menu-item-label{display:flex;text-decoration:none;align-items:flex-start;gap:.625rem}:host ::ng-deep .menu-item a .menu-item-label .menu-title{flex-grow:1;line-height:1.5rem;align-self:center}[dir=rtl] :host ::ng-deep .menu-item a .menu-item-label .menu-title{text-align:right}:host ::ng-deep .menu-item a .menu-item-label .menu-icon-container{display:flex;align-items:center;justify-content:center;flex-shrink:0;text-align:center}:host ::ng-deep .menu-item a .menu-item-label .menu-icon-container .menu-icon{width:1.5rem;height:1.5rem;font-size:1rem}:host ::ng-deep .menu-item a .expand-state{font-size:1.5rem;flex-shrink:0;transition:all .2s ease-in-out}:host ::ng-deep .menu-item a .expand-state.expanded{transform:rotate(90deg)}:host ::ng-deep .menu-item a .action-state,:host ::ng-deep .menu-item a .next-state{font-size:1.25rem;flex-shrink:0;transform:rotate(180deg);margin-top:2px}:host ::ng-deep .menu-item a .next-state{margin-top:4px;margin-right:2px}:host ::ng-deep .menu-item a .action-state{position:relative;left:-8px;transition:left .15s ease-out;opacity:0}:host ::ng-deep .menu-item button{margin:0 .375rem}:host ::ng-deep .menu-item button:focus{box-shadow:none!important}:host ::ng-deep .menu-submenu-items .menu-item:first-child{margin-top:0}:host ::ng-deep .menu-item cj-badge{position:static}:host ::ng-deep .menu-group:not(:first-child){margin-top:1rem}:host ::ng-deep .menu-group span{display:flex}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: CjMenuInternalService }, { type: i6.Router }]; }, propDecorators: { tag: [{
                type: Input
            }], items: [{
                type: Input
            }], autoCollapse: [{
                type: Input
            }] } });

class CjTooltipModule {
}
CjTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipModule, declarations: [CjTooltipComponent, CjTooltipDirective], imports: [CjSharedModule, CjOverlayModule, CjIconModule], exports: [CjTooltipDirective] });
CjTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipModule, imports: [[CjSharedModule, CjOverlayModule, CjIconModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjSharedModule, CjOverlayModule, CjIconModule],
                    declarations: [CjTooltipComponent, CjTooltipDirective],
                    exports: [CjTooltipDirective],
                    entryComponents: [CjTooltipComponent],
                }]
        }] });

const cjMenuComponents = [CjMenuComponent, CjMenuItemComponent];
const CJ_MENU_PROVIDERS = [CjMenuService, CjMenuInternalService];
class CjMenuModule {
    static forRoot() {
        return {
            ngModule: CjMenuModule,
            providers: [
                ...CJ_MENU_PROVIDERS,
            ],
        };
    }
}
CjMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuModule, declarations: [CjMenuComponent, CjMenuItemComponent], imports: [CjSharedModule,
        CjIconModule,
        CjButtonModule,
        CjTooltipModule], exports: [CjMenuComponent, CjMenuItemComponent] });
CjMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuModule, imports: [[
            CjSharedModule,
            CjIconModule,
            CjButtonModule,
            CjTooltipModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjSharedModule,
                        CjIconModule,
                        CjButtonModule,
                        CjTooltipModule,
                    ],
                    declarations: [...cjMenuComponents],
                    exports: [...cjMenuComponents],
                }]
        }] });

class CjCardComponent {
    constructor() {
        this._shadow = false;
    }
    /**
     * If set, element will show shadow
     */
    get shadow() {
        return this._shadow;
    }
    set shadow(value) {
        this._shadow = convertToBoolProperty(value);
    }
}
CjCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjCardComponent, selector: "cj-card", inputs: { shadow: "shadow" }, host: { properties: { "class.shadow": "this.shadow" } }, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, styles: [":host{display:block;position:relative;overflow:auto}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-card', template: `
    <ng-content></ng-content>
  `, styles: [":host{display:block;position:relative;overflow:auto}\n"] }]
        }], propDecorators: { shadow: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.shadow']
            }] } });

class CjCardModule {
}
CjCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardModule, declarations: [CjCardComponent], imports: [CjSharedModule], exports: [CjCardComponent] });
CjCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardModule, imports: [[
            CjSharedModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjSharedModule,
                    ],
                    declarations: [
                        CjCardComponent,
                    ],
                    exports: [
                        CjCardComponent,
                    ],
                }]
        }] });

/*
 * Class used as injection token to provide form element.
 **/
class CjFormFieldControl {
}
CjFormFieldControl.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControl, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjFormFieldControl.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControl });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControl, decorators: [{
            type: Injectable
        }] });
/*
 * Optional config to be provided on CjFormFieldControl to alter default settings.
 **/
class CjFormFieldControlConfig {
    constructor() {
        this.supportsPrefix = true;
        this.supportsSuffix = true;
    }
}
CjFormFieldControlConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControlConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CjFormFieldControlConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControlConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldControlConfig, decorators: [{
            type: Injectable
        }] });

class CjInputDirective {
    constructor(elementRef, focusMonitor, renderer, zone, statusService) {
        this.elementRef = elementRef;
        this.focusMonitor = focusMonitor;
        this.renderer = renderer;
        this.zone = zone;
        this.statusService = statusService;
        this.destroy$ = new Subject();
        /**
         * Field size modifications:
         * `small`, `medium` (default), `large`.
         */
        this.size = 'medium';
        /**
         * Field status:
         * `primary`, `info`, `success`, `warning`, `danger`
         */
        this.status = '';
        this._fullWidth = false;
        this.status$ = new BehaviorSubject(this.status);
        this.size$ = new BehaviorSubject(this.size);
        this.focused$ = new BehaviorSubject(false);
        this.disabled$ = new BehaviorSubject(false);
        this.fullWidth$ = new BehaviorSubject(this.fullWidth);
    }
    /**
     * If set element will fill container. `false` by default.
     */
    get fullWidth() {
        return this._fullWidth;
    }
    set fullWidth(value) {
        this._fullWidth = convertToBoolProperty(value);
    }
    get additionalClasses() {
        if (this.statusService.isCustomStatus(this.status)) {
            return [this.statusService.getStatusClass(this.status)];
        }
        return [];
    }
    ngDoCheck() {
        const isDisabled = this.elementRef.nativeElement.disabled;
        if (isDisabled !== this.disabled$.value) {
            this.disabled$.next(isDisabled);
        }
    }
    ngOnChanges({ status, size, fullWidth }) {
        if (status) {
            this.status$.next(this.status);
        }
        if (size) {
            this.size$.next(this.size);
        }
        if (fullWidth) {
            this.fullWidth$.next(this.fullWidth);
        }
    }
    ngOnInit() {
        this.focusMonitor.monitor(this.elementRef)
            .pipe(map(origin => !!origin), finalize(() => this.focusMonitor.stopMonitoring(this.elementRef)), takeUntil(this.destroy$))
            .subscribe(this.focused$);
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => setTimeout(() => {
            this.renderer.addClass(this.elementRef.nativeElement, 'cj-transition');
        }));
    }
    ngOnDestroy() {
        this.destroy$.next();
    }
    get small() {
        return this.size === 'small';
    }
    get medium() {
        return this.size === 'medium';
    }
    get large() {
        return this.size === 'large';
    }
    get primary() {
        return this.status === 'primary';
    }
    get info() {
        return this.status === 'info';
    }
    get success() {
        return this.status === 'success';
    }
    get warning() {
        return this.status === 'warning';
    }
    get danger() {
        return this.status === 'danger';
    }
}
CjInputDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputDirective, deps: [{ token: i0.ElementRef }, { token: CjFocusMonitor }, { token: i0.Renderer2 }, { token: i0.NgZone }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Directive });
CjInputDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjInputDirective, selector: "input[cjInput],textarea[cjInput]", inputs: { size: "size", status: "status", fullWidth: "fullWidth" }, host: { properties: { "class.input-full-width": "this.fullWidth", "class": "this.additionalClasses", "class.size-small": "this.small", "class.size-medium": "this.medium", "class.size-large": "this.large", "class.status-primary": "this.primary", "class.status-info": "this.info", "class.status-success": "this.success", "class.status-warning": "this.warning", "class.status-danger": "this.danger" } }, providers: [
        { provide: CjFormFieldControl, useExisting: CjInputDirective },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cjInput],textarea[cjInput]',
                    providers: [
                        { provide: CjFormFieldControl, useExisting: CjInputDirective },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CjFocusMonitor }, { type: i0.Renderer2 }, { type: i0.NgZone }, { type: CjStatusService }]; }, propDecorators: { size: [{
                type: Input
            }], status: [{
                type: Input
            }], fullWidth: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.input-full-width']
            }], additionalClasses: [{
                type: HostBinding,
                args: ['class']
            }], small: [{
                type: HostBinding,
                args: ['class.size-small']
            }], medium: [{
                type: HostBinding,
                args: ['class.size-medium']
            }], large: [{
                type: HostBinding,
                args: ['class.size-large']
            }], primary: [{
                type: HostBinding,
                args: ['class.status-primary']
            }], info: [{
                type: HostBinding,
                args: ['class.status-info']
            }], success: [{
                type: HostBinding,
                args: ['class.status-success']
            }], warning: [{
                type: HostBinding,
                args: ['class.status-warning']
            }], danger: [{
                type: HostBinding,
                args: ['class.status-danger']
            }] } });

const CJ_INPUT_COMPONENTS = [
    CjInputDirective,
];
class CjInputModule {
}
CjInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputModule, declarations: [CjInputDirective], imports: [CjSharedModule, CjIconModule], exports: [CjInputDirective] });
CjInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputModule, imports: [[CjSharedModule, CjIconModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjSharedModule, CjIconModule],
                    declarations: CJ_INPUT_COMPONENTS,
                    exports: CJ_INPUT_COMPONENTS,
                }]
        }] });

class CjPrefixDirective {
}
CjPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CjPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjPrefixDirective, selector: "[cjPrefix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjPrefix]',
                }]
        }] });

class CjSuffixDirective {
}
CjSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CjSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjSuffixDirective, selector: "[cjSuffix]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjSuffix]',
                }]
        }] });

function throwFormControlElementNotFound() {
    throw new Error(`CjFormFieldComponent must contain [cjInput]`);
}
class CjFormLabelComponent {
}
CjFormLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjFormLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjFormLabelComponent, selector: "cj-form-label", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-form-label',
                    template: `<ng-content></ng-content>`,
                }]
        }] });
class CjFormHintComponent {
}
CjFormHintComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormHintComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjFormHintComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjFormHintComponent, selector: "cj-form-hint", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormHintComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-form-hint',
                    template: `<ng-content></ng-content>`,
                }]
        }] });
class CjFormErrorComponent {
}
CjFormErrorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjFormErrorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjFormErrorComponent, selector: "cj-form-error", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormErrorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-form-error',
                    template: `<ng-content></ng-content>`,
                }]
        }] });
class CjFormFieldComponent {
    constructor(cd, zone, elementRef, renderer) {
        this.cd = cd;
        this.zone = zone;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.destroy$ = new Subject();
        this.formControlState$ = new ReplaySubject(1);
        this.prefixClasses$ = this.formControlState$.pipe(map(s => this.getAddonClasses('prefix', s)));
        this.suffixClasses$ = this.formControlState$.pipe(map(s => this.getAddonClasses('suffix', s)));
        this._withClear = false;
    }
    /**
     * If set element will show a clear icon in the input. `false` by default.
     */
    get withClear() {
        return this._withClear;
    }
    set withClear(value) {
        this._withClear = convertToBoolProperty(value);
    }
    /**
     * Input value.
     */
    set inputValue(value) {
        this._inputValue = value;
    }
    get inputValue() {
        return this._inputValue;
    }
    /**
     * Input required.
     */
    get required() {
        return this.inputElement.last.elementRef.nativeElement.required;
    }
    onKeyup() {
        this.inputValue = this.inputElement.last.elementRef.nativeElement.value;
    }
    ngAfterContentChecked() {
        if (!this.formControl) {
            throwFormControlElementNotFound();
        }
    }
    ngAfterContentInit() {
        this.subscribeToFormControlStateChange();
        this.subscribeToAddonChange();
        this.inputValue = this.inputElement.last.elementRef.nativeElement.value;
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => setTimeout(() => {
            this.renderer.addClass(this.elementRef.nativeElement, 'cj-transition');
        }));
    }
    ngOnDestroy() {
        this.destroy$.next();
    }
    shouldShowPrefix() {
        return this.getFormControlConfig().supportsPrefix && !!this.prefix.length;
    }
    shouldShowSuffix() {
        if (this.withClear && this._inputValue) {
            return false;
        }
        return this.getFormControlConfig().supportsSuffix && !!this.suffix.length;
    }
    shouldShowError() {
        return !!this.error.length;
    }
    shouldShowHint() {
        return !!this.hint.length;
    }
    clear() {
        this.inputElement.last.elementRef.nativeElement.value = '';
        this.inputValue = '';
    }
    subscribeToFormControlStateChange() {
        const { disabled$, focused$, size$, status$, fullWidth$ } = this.formControl;
        combineLatest([disabled$, focused$, size$, status$, fullWidth$])
            .pipe(map(([disabled, focused, size, status, fullWidth]) => ({ disabled, focused, size, status, fullWidth })), distinctUntilChanged((oldState, state) => this.isStatesEqual(oldState, state)), tap(({ size, fullWidth, disabled }) => {
            const formFieldClasses = [`cj-form-field-size-${size}`];
            if (!fullWidth) {
                formFieldClasses.push('cj-form-field-limited-width');
            }
            if (disabled) {
                formFieldClasses.push('cj-form-field-disabled');
            }
            this.formFieldClasses = formFieldClasses.join(' ');
        }), takeUntil(this.destroy$))
            .subscribe(this.formControlState$);
    }
    subscribeToAddonChange() {
        merge(this.prefix.changes, this.suffix.changes)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cd.markForCheck());
    }
    getAddonClasses(addon, state) {
        const classes = [
            'cj-form-field-addon',
            `cj-form-field-${addon}-${state.size}`,
        ];
        if (state.disabled) {
            classes.push(`cj-form-field-addon-disabled`);
        }
        else if (state.focused) {
            classes.push(`cj-form-field-addon-${state.status}-focused`);
        }
        else {
            classes.push(`cj-form-field-addon-${state.status}`);
        }
        return classes;
    }
    getFormControlConfig() {
        return this.formControlConfig || new CjFormFieldControlConfig();
    }
    isStatesEqual(oldState, state) {
        return oldState.status === state.status &&
            oldState.disabled === state.disabled &&
            oldState.focused === state.focused &&
            oldState.fullWidth === state.fullWidth &&
            oldState.size === state.size;
    }
}
CjFormFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
CjFormFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjFormFieldComponent, selector: "cj-form-field", inputs: { withClear: "withClear" }, host: { listeners: { "window:keyup": "onKeyup($event)" }, properties: { "class": "this.formFieldClasses", "class.required": "this.required" } }, queries: [{ propertyName: "formControl", first: true, predicate: CjFormFieldControl, descendants: true }, { propertyName: "formControlConfig", first: true, predicate: CjFormFieldControlConfig, descendants: true }, { propertyName: "prefix", predicate: CjPrefixDirective, descendants: true }, { propertyName: "suffix", predicate: CjSuffixDirective, descendants: true }, { propertyName: "hint", predicate: CjFormHintComponent, descendants: true }, { propertyName: "error", predicate: CjFormErrorComponent, descendants: true }, { propertyName: "inputElement", predicate: CjInputDirective, descendants: true }], ngImport: i0, template: "<label>\n  <ng-content select=\"cj-form-label\"></ng-content>\n\n  <div class=\"cj-form-control-input\">\n    <div *ngIf=\"shouldShowPrefix()\" [ngClass]=\"prefixClasses$ | async\">\n      <ng-content select=\"[cjPrefix]\"></ng-content>\n    </div>\n\n    <div class=\"cj-form-control-container\"\n        [class.cj-form-field-control-with-prefix]=\"shouldShowPrefix()\"\n        [class.cj-form-field-control-with-suffix]=\"shouldShowSuffix()\"\n        [class.cj-form-field-control-with-clear]=\"withClear\">\n      <ng-content></ng-content>\n    </div>\n\n    <div *ngIf=\"shouldShowSuffix()\" [ngClass]=\"suffixClasses$ | async\">\n      <ng-content select=\"[cjSuffix]\"></ng-content>\n    </div>\n\n    <div\n      *ngIf=\"withClear\"\n      class=\"cj-form-field-addon cj-form-control-clear-icon\"\n      [class.hidden]=\"!inputValue\"\n      (click)=\"clear()\"\n    >\n      <cj-icon icon=\"close-outline\"></cj-icon>\n    </div>\n  </div>\n</label>\n\n<div *ngIf=\"shouldShowError()\" class=\"cj-form-control-error\">\n  <ng-content select=\"cj-form-error\"></ng-content>\n</div>\n\n<div *ngIf=\"shouldShowHint()\" class=\"cj-form-control-hint\">\n  <ng-content select=\"cj-form-hint\"></ng-content>\n</div>", styles: [":host{display:flex;align-items:flex-start;flex-direction:column}:host label,:host .cj-form-control-container,:host .cj-form-control-input{width:100%}:host .cj-form-control-input{display:flex;align-items:center}:host .cj-form-control-error,:host .cj-form-control-hint{font-size:.875rem;line-height:1.125rem;margin-top:.75rem;padding:0 .5rem}:host .cj-form-control-clear-icon{cursor:pointer}:host.cj-form-field-disabled .cj-form-control-clear-icon,:host .cj-form-control-clear-icon.hidden{display:none}:host .cj-form-field-addon ::ng-deep [cjButton]{background-color:transparent;background-color:initial;border:none;box-shadow:none;border-radius:0;border-radius:initial;padding:0}:host.required ::ng-deep cj-form-label:after{content:\" *\"}:host ::ng-deep cj-form-label{display:block;font-weight:500;line-height:1.375rem;margin-bottom:.5rem}\n"], components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], pipes: { "async": i1$1.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-form-field', changeDetection: ChangeDetectionStrategy.OnPush, template: "<label>\n  <ng-content select=\"cj-form-label\"></ng-content>\n\n  <div class=\"cj-form-control-input\">\n    <div *ngIf=\"shouldShowPrefix()\" [ngClass]=\"prefixClasses$ | async\">\n      <ng-content select=\"[cjPrefix]\"></ng-content>\n    </div>\n\n    <div class=\"cj-form-control-container\"\n        [class.cj-form-field-control-with-prefix]=\"shouldShowPrefix()\"\n        [class.cj-form-field-control-with-suffix]=\"shouldShowSuffix()\"\n        [class.cj-form-field-control-with-clear]=\"withClear\">\n      <ng-content></ng-content>\n    </div>\n\n    <div *ngIf=\"shouldShowSuffix()\" [ngClass]=\"suffixClasses$ | async\">\n      <ng-content select=\"[cjSuffix]\"></ng-content>\n    </div>\n\n    <div\n      *ngIf=\"withClear\"\n      class=\"cj-form-field-addon cj-form-control-clear-icon\"\n      [class.hidden]=\"!inputValue\"\n      (click)=\"clear()\"\n    >\n      <cj-icon icon=\"close-outline\"></cj-icon>\n    </div>\n  </div>\n</label>\n\n<div *ngIf=\"shouldShowError()\" class=\"cj-form-control-error\">\n  <ng-content select=\"cj-form-error\"></ng-content>\n</div>\n\n<div *ngIf=\"shouldShowHint()\" class=\"cj-form-control-hint\">\n  <ng-content select=\"cj-form-hint\"></ng-content>\n</div>", styles: [":host{display:flex;align-items:flex-start;flex-direction:column}:host label,:host .cj-form-control-container,:host .cj-form-control-input{width:100%}:host .cj-form-control-input{display:flex;align-items:center}:host .cj-form-control-error,:host .cj-form-control-hint{font-size:.875rem;line-height:1.125rem;margin-top:.75rem;padding:0 .5rem}:host .cj-form-control-clear-icon{cursor:pointer}:host.cj-form-field-disabled .cj-form-control-clear-icon,:host .cj-form-control-clear-icon.hidden{display:none}:host .cj-form-field-addon ::ng-deep [cjButton]{background-color:transparent;background-color:initial;border:none;box-shadow:none;border-radius:0;border-radius:initial;padding:0}:host.required ::ng-deep cj-form-label:after{content:\" *\"}:host ::ng-deep cj-form-label{display:block;font-weight:500;line-height:1.375rem;margin-bottom:.5rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { prefix: [{
                type: ContentChildren,
                args: [CjPrefixDirective, { descendants: true }]
            }], suffix: [{
                type: ContentChildren,
                args: [CjSuffixDirective, { descendants: true }]
            }], hint: [{
                type: ContentChildren,
                args: [CjFormHintComponent, { descendants: true }]
            }], error: [{
                type: ContentChildren,
                args: [CjFormErrorComponent, { descendants: true }]
            }], inputElement: [{
                type: ContentChildren,
                args: [CjInputDirective, { descendants: true }]
            }], formControl: [{
                type: ContentChild,
                args: [CjFormFieldControl, { static: false }]
            }], formControlConfig: [{
                type: ContentChild,
                args: [CjFormFieldControlConfig, { static: false }]
            }], formFieldClasses: [{
                type: HostBinding,
                args: ['class']
            }], withClear: [{
                type: Input
            }], required: [{
                type: HostBinding,
                args: ['class.required']
            }], onKeyup: [{
                type: HostListener,
                args: ['window:keyup', ['$event']]
            }] } });

const COMPONENTS = [
    CjFormFieldComponent,
    CjFormLabelComponent,
    CjFormHintComponent,
    CjFormErrorComponent,
    CjPrefixDirective,
    CjSuffixDirective,
];
class CjFormFieldModule {
}
CjFormFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjFormFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldModule, declarations: [CjFormFieldComponent,
        CjFormLabelComponent,
        CjFormHintComponent,
        CjFormErrorComponent,
        CjPrefixDirective,
        CjSuffixDirective], imports: [CommonModule, CjIconModule], exports: [CjFormFieldComponent,
        CjFormLabelComponent,
        CjFormHintComponent,
        CjFormErrorComponent,
        CjPrefixDirective,
        CjSuffixDirective] });
CjFormFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldModule, imports: [[CommonModule, CjIconModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjFormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CjIconModule],
                    declarations: [...COMPONENTS],
                    exports: [...COMPONENTS],
                }]
        }] });

class CjCheckboxComponent {
    constructor(changeDetector, renderer, hostElement, zone, statusService) {
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.hostElement = hostElement;
        this.zone = zone;
        this.statusService = statusService;
        this.onChange = () => { };
        this.onTouched = () => { };
        /**
         * Checkbox status:
         * `primary`, `success`, `warning`, `danger`, `info`.
         */
        this.status = 'primary';
        /**
         * Checkbox size:
         * `small`, `medium`, `large`
         */
        this.size = 'medium';
        this._checked = false;
        this._disabled = false;
        this._indeterminate = false;
        /**
         * Output when checked state is changed by a user
         * @type EventEmitter<boolean>
         */
        this.checkedChange = new EventEmitter();
    }
    /**
     * Controls checkbox checked state
     */
    get checked() {
        return this._checked;
    }
    set checked(value) {
        this._checked = convertToBoolProperty(value);
    }
    /**
     * Controls input disabled state
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = convertToBoolProperty(value);
    }
    /**
     * Controls checkbox indeterminate state
     */
    get indeterminate() {
        return this._indeterminate;
    }
    set indeterminate(value) {
        this._indeterminate = convertToBoolProperty(value);
    }
    get small() {
        return this.size === 'small';
    }
    get medium() {
        return this.size === 'medium';
    }
    get large() {
        return this.size === 'large';
    }
    get primary() {
        return this.status === 'primary';
    }
    get success() {
        return this.status === 'success';
    }
    get warning() {
        return this.status === 'warning';
    }
    get danger() {
        return this.status === 'danger';
    }
    get info() {
        return this.status === 'info';
    }
    get additionalClasses() {
        if (this.statusService.isCustomStatus(this.status)) {
            return [this.statusService.getStatusClass(this.status)];
        }
        return [];
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => setTimeout(() => {
            this.renderer.addClass(this.hostElement.nativeElement, 'cj-transition');
        }));
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(val) {
        this._checked = val;
        this.changeDetector.markForCheck();
    }
    setDisabledState(val) {
        this.disabled = convertToBoolProperty(val);
        this.changeDetector.markForCheck();
    }
    setTouched() {
        this.onTouched();
    }
    updateValueAndIndeterminate(event) {
        const input = event.target;
        this.checked = input.checked;
        this.checkedChange.emit(this.checked);
        this.onChange(this.checked);
        this.indeterminate = input.indeterminate;
    }
}
CjCheckboxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Component });
CjCheckboxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjCheckboxComponent, selector: "cj-checkbox", inputs: { status: "status", size: "size", checked: "checked", disabled: "disabled", indeterminate: "indeterminate" }, outputs: { checkedChange: "checkedChange" }, host: { properties: { "class.size-small": "this.small", "class.size-medium": "this.medium", "class.size-large": "this.large", "class.status-primary": "this.primary", "class.status-success": "this.success", "class.status-warning": "this.warning", "class.status-danger": "this.danger", "class.status-info": "this.info", "class": "this.additionalClasses" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CjCheckboxComponent),
            multi: true,
        }, {
            provide: CjFormFieldControl,
            useExisting: CjCheckboxComponent
        }], ngImport: i0, template: `
    <label class="label">
      <input type="checkbox" class="native-input visually-hidden"
        [disabled]="disabled"
        [checked]="checked"
        (change)="updateValueAndIndeterminate($event)"
        (blur)="setTouched()"
        (click)="$event.stopPropagation()"
        [indeterminate]="indeterminate"
      >
      <span [class.indeterminate]="indeterminate" [class.checked]="checked" class="custom-checkbox">
        <cj-icon *ngIf="indeterminate" icon="minus-bold-outline" class="custom-checkbox-icon"></cj-icon>
        <cj-icon *ngIf="checked && !indeterminate" icon="checkmark" class="custom-checkbox-icon"></cj-icon>
      </span>
      <span class="text">
        <ng-content></ng-content>
      </span>
    </label>
  `, isInline: true, styles: [":host .label{position:relative;display:inline-flex;align-items:flex-start;margin:0;min-height:inherit}:host .custom-checkbox{flex-shrink:0}:host(.cj-transition) .custom-checkbox{transition-duration:.15s;transition-property:background-color,border,box-shadow;transition-timing-function:ease-in}:host(.cj-transition) .text{transition-duration:.15s;transition-property:color;transition-timing-function:ease-in}\n"], components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-checkbox', template: `
    <label class="label">
      <input type="checkbox" class="native-input visually-hidden"
        [disabled]="disabled"
        [checked]="checked"
        (change)="updateValueAndIndeterminate($event)"
        (blur)="setTouched()"
        (click)="$event.stopPropagation()"
        [indeterminate]="indeterminate"
      >
      <span [class.indeterminate]="indeterminate" [class.checked]="checked" class="custom-checkbox">
        <cj-icon *ngIf="indeterminate" icon="minus-bold-outline" class="custom-checkbox-icon"></cj-icon>
        <cj-icon *ngIf="checked && !indeterminate" icon="checkmark" class="custom-checkbox-icon"></cj-icon>
      </span>
      <span class="text">
        <ng-content></ng-content>
      </span>
    </label>
  `, providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => CjCheckboxComponent),
                            multi: true,
                        }, {
                            provide: CjFormFieldControl,
                            useExisting: CjCheckboxComponent
                        }], changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host .label{position:relative;display:inline-flex;align-items:flex-start;margin:0;min-height:inherit}:host .custom-checkbox{flex-shrink:0}:host(.cj-transition) .custom-checkbox{transition-duration:.15s;transition-property:background-color,border,box-shadow;transition-timing-function:ease-in}:host(.cj-transition) .text{transition-duration:.15s;transition-property:color;transition-timing-function:ease-in}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: CjStatusService }]; }, propDecorators: { status: [{
                type: Input
            }], size: [{
                type: Input
            }], checked: [{
                type: Input
            }], disabled: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], small: [{
                type: HostBinding,
                args: ['class.size-small']
            }], medium: [{
                type: HostBinding,
                args: ['class.size-medium']
            }], large: [{
                type: HostBinding,
                args: ['class.size-large']
            }], primary: [{
                type: HostBinding,
                args: ['class.status-primary']
            }], success: [{
                type: HostBinding,
                args: ['class.status-success']
            }], warning: [{
                type: HostBinding,
                args: ['class.status-warning']
            }], danger: [{
                type: HostBinding,
                args: ['class.status-danger']
            }], info: [{
                type: HostBinding,
                args: ['class.status-info']
            }], additionalClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CjCheckboxModule {
}
CjCheckboxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjCheckboxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxModule, declarations: [CjCheckboxComponent], imports: [CjSharedModule,
        CjIconModule], exports: [CjCheckboxComponent] });
CjCheckboxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxModule, imports: [[
            CjSharedModule,
            CjIconModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CjSharedModule,
                        CjIconModule,
                    ],
                    declarations: [CjCheckboxComponent],
                    exports: [CjCheckboxComponent],
                }]
        }] });

class CjRadioComponent {
    constructor(cd, renderer, hostElement, zone, statusService) {
        this.cd = cd;
        this.renderer = renderer;
        this.hostElement = hostElement;
        this.zone = zone;
        this.statusService = statusService;
        this._checked = false;
        this._disabled = false;
        /**
         * Radio size:
         * `small`, `medium`, `large`
         */
        this.size = 'medium';
        /**
         * Radio status:
         * `primary`, `success`, `warning`, `danger`, `info`.
         */
        this.status = 'primary';
        /**
         * Output when value is changed by a user
         * @type EventEmitter<any>
         */
        this.valueChange = new EventEmitter();
        /**
         * Output when radio blur
         * @type EventEmitter<void>
         */
        this.blur = new EventEmitter();
    }
    /**
     * Set radio name
     */
    get name() {
        return this._name;
    }
    set name(value) {
        if (this._name !== value) {
            this._name = value;
        }
    }
    /**
     * Controls radio checked state
     */
    get checked() {
        return this._checked;
    }
    set checked(value) {
        const boolValue = convertToBoolProperty(value);
        if (this._checked !== boolValue) {
            this._checked = boolValue;
        }
    }
    /**
     * Set radio value
     */
    get value() {
        return this._value;
    }
    set value(value) {
        if (this._value !== value) {
            this._value = value;
        }
    }
    /**
     * Controls radio disabled state
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(disabled) {
        const boolValue = convertToBoolProperty(disabled);
        if (this._disabled !== boolValue) {
            this._disabled = boolValue;
        }
    }
    get small() {
        return this.size === 'small';
    }
    get medium() {
        return this.size === 'medium';
    }
    get large() {
        return this.size === 'large';
    }
    get isPrimary() {
        return this.status === 'primary';
    }
    get isSuccess() {
        return this.status === 'success';
    }
    get isWarning() {
        return this.status === 'warning';
    }
    get isDanger() {
        return this.status === 'danger';
    }
    get isInfo() {
        return this.status === 'info';
    }
    get additionalClasses() {
        if (this.statusService.isCustomStatus(this.status)) {
            return [this.statusService.getStatusClass(this.status)];
        }
        return [];
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => setTimeout(() => {
            this.renderer.addClass(this.hostElement.nativeElement, 'cj-transition');
        }));
    }
    onChange(event) {
        event.stopPropagation();
        this.checked = true;
        this.valueChange.emit(this.value);
    }
    onClick(event) {
        event.stopPropagation();
    }
    _markForCheck() {
        this.cd.markForCheck();
    }
    _setName(name) {
        this.name = name;
        if (this.input) {
            this.renderer.setProperty(this.input.nativeElement, 'name', name);
        }
    }
}
CjRadioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: CjStatusService }], target: i0.ɵɵFactoryTarget.Component });
CjRadioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjRadioComponent, selector: "cj-radio", inputs: { name: "name", checked: "checked", value: "value", disabled: "disabled", size: "size", status: "status" }, outputs: { valueChange: "valueChange", blur: "blur" }, host: { properties: { "class.size-small": "this.small", "class.size-medium": "this.medium", "class.size-large": "this.large", "class.status-primary": "this.isPrimary", "class.status-success": "this.isSuccess", "class.status-warning": "this.isWarning", "class.status-danger": "this.isDanger", "class.status-info": "this.isInfo", "class": "this.additionalClasses" } }, viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, read: ElementRef }], ngImport: i0, template: `
    <label>
      <input
        #input
        type="radio"
        class="native-input visually-hidden"
        [name]="name"
        [value]="value"
        [checked]="checked"
        [disabled]="disabled"
        (change)="onChange($event)"
        (click)="onClick($event)">
      <span class="outer-circle"></span>
      <span class="inner-circle"></span>
      <span class="text">
        <ng-content></ng-content>
      </span>
    </label>
  `, isInline: true, styles: [":host{display:block;position:relative}:host label{display:inline-flex;margin:0;min-height:inherit;padding:.375rem 0;align-items:flex-start}[dir=ltr] :host label{padding-right:1.5rem}[dir=rtl] :host label{padding-left:1.5rem}:host .outer-circle,:host .inner-circle{border-radius:50%;position:absolute;top:50%;transform:translateY(-50%)}[dir=ltr] :host .outer-circle,[dir=ltr] :host .inner-circle{left:0}[dir=rtl] :host .outer-circle,[dir=rtl] :host .inner-circle{right:0}:host .inner-circle{transform:translateY(-50%) scale(.5)}:host(.cj-transition) .outer-circle,:host(.cj-transition) .inner-circle{transition-duration:.15s;transition-property:background-color,border,box-shadow;transition-timing-function:ease-in}:host(.cj-transition) .text{transition-duration:.15s;transition-property:color;transition-timing-function:ease-in}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-radio', template: `
    <label>
      <input
        #input
        type="radio"
        class="native-input visually-hidden"
        [name]="name"
        [value]="value"
        [checked]="checked"
        [disabled]="disabled"
        (change)="onChange($event)"
        (click)="onClick($event)">
      <span class="outer-circle"></span>
      <span class="inner-circle"></span>
      <span class="text">
        <ng-content></ng-content>
      </span>
    </label>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block;position:relative}:host label{display:inline-flex;margin:0;min-height:inherit;padding:.375rem 0;align-items:flex-start}[dir=ltr] :host label{padding-right:1.5rem}[dir=rtl] :host label{padding-left:1.5rem}:host .outer-circle,:host .inner-circle{border-radius:50%;position:absolute;top:50%;transform:translateY(-50%)}[dir=ltr] :host .outer-circle,[dir=ltr] :host .inner-circle{left:0}[dir=rtl] :host .outer-circle,[dir=rtl] :host .inner-circle{right:0}:host .inner-circle{transform:translateY(-50%) scale(.5)}:host(.cj-transition) .outer-circle,:host(.cj-transition) .inner-circle{transition-duration:.15s;transition-property:background-color,border,box-shadow;transition-timing-function:ease-in}:host(.cj-transition) .text{transition-duration:.15s;transition-property:color;transition-timing-function:ease-in}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: CjStatusService }]; }, propDecorators: { name: [{
                type: Input
            }], checked: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], size: [{
                type: Input
            }], status: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], blur: [{
                type: Output
            }], input: [{
                type: ViewChild,
                args: ['input', { read: ElementRef }]
            }], small: [{
                type: HostBinding,
                args: ['class.size-small']
            }], medium: [{
                type: HostBinding,
                args: ['class.size-medium']
            }], large: [{
                type: HostBinding,
                args: ['class.size-large']
            }], isPrimary: [{
                type: HostBinding,
                args: ['class.status-primary']
            }], isSuccess: [{
                type: HostBinding,
                args: ['class.status-success']
            }], isWarning: [{
                type: HostBinding,
                args: ['class.status-warning']
            }], isDanger: [{
                type: HostBinding,
                args: ['class.status-danger']
            }], isInfo: [{
                type: HostBinding,
                args: ['class.status-info']
            }], additionalClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CjRadioGroupComponent {
    constructor(hostElement, platformId, document) {
        this.hostElement = hostElement;
        this.platformId = platformId;
        this.document = document;
        this.destroy$ = new Subject();
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this._status = 'primary';
        this._size = 'medium';
        this.valueChange = new EventEmitter();
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.updateValues();
    }
    get name() {
        return this._name;
    }
    set name(name) {
        this._name = name;
        this.updateNames();
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(disabled) {
        this._disabled = convertToBoolProperty(disabled);
        this.updateDisabled();
    }
    /**
     * Radio buttons status:
     * `primary` (default), `success`, `warning`, `danger`, `info`.
     */
    get status() {
        return this._status;
    }
    set status(value) {
        if (this._status !== value) {
            this._status = value;
            this.updateStatus();
        }
    }
    /**
     * Radio buttons size:
     * `small`, `medium`, `large`
     */
    get size() {
        return this._size;
    }
    set size(value) {
        if (this._size !== value) {
            this._size = value;
            this.updateSize();
        }
    }
    ngAfterContentInit() {
        this.updateNames();
        this.radios.changes
            .pipe(startWith(this.radios), switchMap((radios) => from(Promise.resolve(radios))), takeUntil(this.destroy$))
            .subscribe(() => this.updateAndSubscribeToRadios());
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    updateAndSubscribeToRadios() {
        this.updateValueFromCheckedOption();
        this.updateNames();
        this.updateValues();
        this.updateDisabled();
        this.updateStatus();
        this.updateSize();
        this.subscribeOnRadiosValueChange();
        this.subscribeOnRadiosBlur();
    }
    updateNames() {
        if (this.radios) {
            this.radios.forEach((radio) => radio._setName(this.name));
        }
    }
    updateValues() {
        this.updateAndMarkForCheckRadios((radio) => radio.checked = radio.value === this.value);
    }
    updateDisabled() {
        if (typeof this.disabled !== 'undefined') {
            this.updateAndMarkForCheckRadios((radio) => radio.disabled = this.disabled);
        }
    }
    subscribeOnRadiosValueChange() {
        if (!this.radios || !this.radios.length) {
            return;
        }
        merge(...this.radios.map((radio) => radio.valueChange))
            .pipe(takeUntil(merge(this.radios.changes, this.destroy$)))
            .subscribe((value) => {
            this.writeValue(value);
            this.propagateValue(value);
        });
    }
    propagateValue(value) {
        this.valueChange.emit(value);
        this.onChange(value);
    }
    subscribeOnRadiosBlur() {
        const hasNoRadios = !this.radios || !this.radios.length;
        if (!isPlatformBrowser(this.platformId) || hasNoRadios) {
            return;
        }
        const hostElement = this.hostElement.nativeElement;
        fromEvent(hostElement, 'focusin')
            .pipe(filter(event => hostElement.contains(event.target)), switchMap(() => merge(fromEvent(this.document, 'focusin'), fromEvent(this.document, 'click'))), filter(event => !hostElement.contains(event.target)), takeUntil(merge(this.radios.changes, this.destroy$)))
            .subscribe(() => this.onTouched());
    }
    updateStatus() {
        this.updateAndMarkForCheckRadios((radio) => radio.status = this.status);
    }
    updateSize() {
        this.updateAndMarkForCheckRadios((radio) => radio.size = this.size);
    }
    updateAndMarkForCheckRadios(updateFn) {
        if (this.radios) {
            this.radios.forEach((radio) => {
                updateFn(radio);
                radio._markForCheck();
            });
        }
    }
    updateValueFromCheckedOption() {
        const checkedRadio = this.radios.find((radio) => radio.checked);
        const isValueMissing = this.value === undefined || this.value === null;
        if (checkedRadio && isValueMissing && checkedRadio.value !== this.value) {
            this.value = checkedRadio.value;
        }
    }
}
CjRadioGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioGroupComponent, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }, { token: CJ_DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
CjRadioGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjRadioGroupComponent, selector: "cj-radio-group", inputs: { value: "value", name: "name", disabled: "disabled", status: "status", size: "size" }, outputs: { valueChange: "valueChange" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CjRadioGroupComponent),
            multi: true,
        },
    ], queries: [{ propertyName: "radios", predicate: CjRadioComponent, descendants: true }], ngImport: i0, template: `
    <ng-content select="cj-radio"></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-radio-group',
                    template: `
    <ng-content select="cj-radio"></ng-content>`,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => CjRadioGroupComponent),
                            multi: true,
                        },
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CJ_DOCUMENT]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], name: [{
                type: Input
            }], disabled: [{
                type: Input
            }], status: [{
                type: Input
            }], size: [{
                type: Input
            }], radios: [{
                type: ContentChildren,
                args: [CjRadioComponent, { descendants: true }]
            }], valueChange: [{
                type: Output
            }] } });

class CjRadioModule {
}
CjRadioModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjRadioModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioModule, declarations: [CjRadioComponent, CjRadioGroupComponent], exports: [CjRadioComponent, CjRadioGroupComponent] });
CjRadioModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [CjRadioComponent, CjRadioGroupComponent],
                    declarations: [CjRadioComponent, CjRadioGroupComponent],
                }]
        }] });

/**
 * Directive to wrap tab lazy content.
 * */
class CjTabContentDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
}
CjTabContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabContentDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
CjTabContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjTabContentDirective, selector: "[cjTabContent]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjTabContent]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class CjTabTitleDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
}
CjTabTitleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabTitleDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
CjTabTitleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjTabTitleDirective, selector: "[cjTabTitle]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjTabTitle]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class CjTabComponent {
    constructor() {
        this.activeValue = false;
        this.responsiveValue = false;
        this.disabledValue = false;
    }
    /**
     * Item is disabled and cannot be opened.
     * @type {boolean}
     */
    get disabled() {
        return this.disabledValue;
    }
    set disabled(val) {
        this.disabledValue = convertToBoolProperty(val);
    }
    /**
     * Show only icons when width is smaller than `tabs-icon-only-max-width`
     * @type {boolean}
     */
    set responsive(val) {
        this.responsiveValue = convertToBoolProperty(val);
    }
    get responsive() {
        return this.responsiveValue;
    }
    /**
     * Specifies active tab
     * @returns {boolean}
     */
    get active() {
        return this.activeValue;
    }
    set active(val) {
        this.activeValue = convertToBoolProperty(val);
    }
}
CjTabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjTabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjTabComponent, selector: "cj-tab", inputs: { tabTitle: "tabTitle", tabId: "tabId", tabIcon: "tabIcon", disabled: "disabled", responsive: "responsive", route: "route", active: "active" }, host: { properties: { "class.disabled": "this.disabled", "class.content-active": "this.activeValue" } }, queries: [{ propertyName: "tabContentDirective", first: true, predicate: CjTabContentDirective, descendants: true }, { propertyName: "tabTitleDirective", first: true, predicate: CjTabTitleDirective, descendants: true }], ngImport: i0, template: `
    <ng-container
      *ngIf="tabContentDirective; else projectedContent"
      [ngTemplateOutlet]="tabContentDirective.templateRef"
    ></ng-container>

    <ng-template #projectedContent>
      <ng-content></ng-content>
    </ng-template>
  `, isInline: true, directives: [{ type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cj-tab',
                    template: `
    <ng-container
      *ngIf="tabContentDirective; else projectedContent"
      [ngTemplateOutlet]="tabContentDirective.templateRef"
    ></ng-container>

    <ng-template #projectedContent>
      <ng-content></ng-content>
    </ng-template>
  `,
                }]
        }], propDecorators: { tabContentDirective: [{
                type: ContentChild,
                args: [CjTabContentDirective]
            }], tabTitleDirective: [{
                type: ContentChild,
                args: [CjTabTitleDirective]
            }], tabTitle: [{
                type: Input
            }], tabId: [{
                type: Input
            }], tabIcon: [{
                type: Input
            }], disabled: [{
                type: Input,
                args: ['disabled']
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], responsive: [{
                type: Input
            }], route: [{
                type: Input
            }], activeValue: [{
                type: HostBinding,
                args: ['class.content-active']
            }], active: [{
                type: Input
            }] } });
class CjTabsetComponent {
    constructor(route, changeDetectorRef) {
        this.route = route;
        this.changeDetectorRef = changeDetectorRef;
        /**
         * Emits when tab is selected
         * @type EventEmitter<any>
         */
        this.changeTab = new EventEmitter();
        this.destroy$ = new Subject();
    }
    // TODO: refactoring this component, avoid change detection loop
    ngAfterContentInit() {
        this.route.params
            .pipe(map((params) => this.tabs.find((tab) => (this.routeParam ? tab.route === params[this.routeParam] : tab.active))), delay(0), map((tab) => tab || this.tabs.first), filter((tab) => !!tab), takeUntil(this.destroy$))
            .subscribe((tabToSelect) => {
            this.selectTab(tabToSelect);
            this.changeDetectorRef.markForCheck();
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    // TODO: navigate to routeParam
    selectTab(selectedTab) {
        if (!selectedTab.disabled) {
            this.tabs.forEach((tab) => (tab.active = tab === selectedTab));
            this.changeTab.emit(selectedTab);
        }
    }
    showTooltip(tabIndex) {
        const tabs = document.getElementsByClassName('tab-link');
        return tabs[tabIndex].clientWidth < 320;
    }
}
CjTabsetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetComponent, deps: [{ token: i6.ActivatedRoute }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CjTabsetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjTabsetComponent, selector: "cj-tabset", inputs: { routeParam: "routeParam" }, outputs: { changeTab: "changeTab" }, queries: [{ propertyName: "tabs", predicate: CjTabComponent }], ngImport: i0, template: `
    <ul class="tabset">
      <li
        *ngFor="let tab of tabs; let i = index"
        (click)="selectTab(tab)"
        (keyup.space)="selectTab(tab)"
        (keyup.enter)="selectTab(tab)"
        [class.responsive]="tab.responsive"
        [class.active]="tab.active"
        [class.disabled]="tab.disabled"
        [attr.tabindex]="tab.disabled ? -1 : 0"
        [attr.data-tab-id]="tab.tabId"
        class="tab"
      >
        <a
          href
          tabindex="-1"
          class="tab-link"
          [cjTooltip]="tab.tabTitle"
          [cjTooltipDisabled]="showTooltip(i)"
          [cjTooltipPlacement]="'bottom'"
          (click)="$event.preventDefault()"
        >
          <cj-icon *ngIf="tab.tabIcon" [config]="tab.tabIcon"></cj-icon>
          <ng-container
            *ngIf="tab.tabTitleDirective; else textTitleTemplate"
            [ngTemplateOutlet]="tab.tabTitleDirective.templateRef"
          ></ng-container>
          <ng-template #textTitleTemplate>
            <span class="tab-text truncate">{{ tab.tabTitle }}</span>
          </ng-template>
        </a>
        <!-- <cj-badge
          *ngIf="tab.badgeText || tab.badgeDot"
          [text]="tab.badgeText"
          [dotMode]="tab.badgeDot"
          [status]="tab.badgeStatus"
          [position]="tab.badgePosition"
        >
        </cj-badge> -->
      </li>
    </ul>
    <ng-content select="cj-tab"></ng-content>
  `, isInline: true, styles: [":host{display:block}:host ::ng-deep cj-tab{flex:1;-ms-flex:1 1 auto;overflow:auto;display:none}:host ::ng-deep cj-tab.content-active{display:block}:host .tabset{display:flex;flex-direction:row;list-style-type:none;margin:0;padding:0}:host .tabset .tab{text-align:center;position:relative;overflow:hidden}:host .tabset .tab.active a:before{display:block}:host .tabset .tab a{display:flex;position:relative;text-decoration:none;align-items:center}:host .tabset .tab a:before{position:absolute;content:\"\";width:100%;border-radius:3px;bottom:-2px;left:0}:host .tabset .tab a cj-icon{vertical-align:middle;font-size:1rem;flex-shrink:0}[dir=ltr] :host .tabset .tab a cj-icon+span,[dir=ltr] :host .tabset .tab a svg+span{margin-left:.5rem}[dir=rtl] :host .tabset .tab a cj-icon+span,[dir=rtl] :host .tabset .tab a svg+span{margin-right:.5rem}\n"], components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: CjTooltipDirective, selector: "[cjTooltip]", inputs: ["cjTooltip", "cjTooltipPlacement", "cjTooltipAdjustment", "cjTooltipClass", "cjTooltipIcon", "cjTooltipTrigger", "cjTooltipOffset", "cjTooltipDisabled"], outputs: ["cjTooltipShowStateChange"], exportAs: ["cjTooltip"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-tabset', template: `
    <ul class="tabset">
      <li
        *ngFor="let tab of tabs; let i = index"
        (click)="selectTab(tab)"
        (keyup.space)="selectTab(tab)"
        (keyup.enter)="selectTab(tab)"
        [class.responsive]="tab.responsive"
        [class.active]="tab.active"
        [class.disabled]="tab.disabled"
        [attr.tabindex]="tab.disabled ? -1 : 0"
        [attr.data-tab-id]="tab.tabId"
        class="tab"
      >
        <a
          href
          tabindex="-1"
          class="tab-link"
          [cjTooltip]="tab.tabTitle"
          [cjTooltipDisabled]="showTooltip(i)"
          [cjTooltipPlacement]="'bottom'"
          (click)="$event.preventDefault()"
        >
          <cj-icon *ngIf="tab.tabIcon" [config]="tab.tabIcon"></cj-icon>
          <ng-container
            *ngIf="tab.tabTitleDirective; else textTitleTemplate"
            [ngTemplateOutlet]="tab.tabTitleDirective.templateRef"
          ></ng-container>
          <ng-template #textTitleTemplate>
            <span class="tab-text truncate">{{ tab.tabTitle }}</span>
          </ng-template>
        </a>
        <!-- <cj-badge
          *ngIf="tab.badgeText || tab.badgeDot"
          [text]="tab.badgeText"
          [dotMode]="tab.badgeDot"
          [status]="tab.badgeStatus"
          [position]="tab.badgePosition"
        >
        </cj-badge> -->
      </li>
    </ul>
    <ng-content select="cj-tab"></ng-content>
  `, styles: [":host{display:block}:host ::ng-deep cj-tab{flex:1;-ms-flex:1 1 auto;overflow:auto;display:none}:host ::ng-deep cj-tab.content-active{display:block}:host .tabset{display:flex;flex-direction:row;list-style-type:none;margin:0;padding:0}:host .tabset .tab{text-align:center;position:relative;overflow:hidden}:host .tabset .tab.active a:before{display:block}:host .tabset .tab a{display:flex;position:relative;text-decoration:none;align-items:center}:host .tabset .tab a:before{position:absolute;content:\"\";width:100%;border-radius:3px;bottom:-2px;left:0}:host .tabset .tab a cj-icon{vertical-align:middle;font-size:1rem;flex-shrink:0}[dir=ltr] :host .tabset .tab a cj-icon+span,[dir=ltr] :host .tabset .tab a svg+span{margin-left:.5rem}[dir=rtl] :host .tabset .tab a cj-icon+span,[dir=rtl] :host .tabset .tab a svg+span{margin-right:.5rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i6.ActivatedRoute }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { tabs: [{
                type: ContentChildren,
                args: [CjTabComponent]
            }], routeParam: [{
                type: Input
            }], changeTab: [{
                type: Output
            }] } });

const CJ_TABSET_COMPONENTS = [CjTabsetComponent, CjTabComponent];
const CJ_TABSET_DIRECTIVES = [CjTabContentDirective, CjTabTitleDirective];
class CjTabsetModule {
}
CjTabsetModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjTabsetModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetModule, declarations: [CjTabsetComponent, CjTabComponent, CjTabContentDirective, CjTabTitleDirective], imports: [CjSharedModule, CjIconModule, CjTooltipModule], exports: [CjTabsetComponent, CjTabComponent, CjTabContentDirective, CjTabTitleDirective] });
CjTabsetModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetModule, imports: [[CjSharedModule, CjIconModule, CjTooltipModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjTabsetModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjSharedModule, CjIconModule, CjTooltipModule],
                    declarations: [...CJ_TABSET_COMPONENTS, ...CJ_TABSET_DIRECTIVES],
                    exports: [...CJ_TABSET_COMPONENTS, ...CJ_TABSET_DIRECTIVES],
                }]
        }] });

class CjMergeConfigsPipe {
    transform(...configs) {
        return Object.assign({}, ...configs);
    }
}
CjMergeConfigsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMergeConfigsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CjMergeConfigsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMergeConfigsPipe, name: "cjMergeConfigs" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjMergeConfigsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cjMergeConfigs',
                }]
        }] });

class CjRouteTabsetComponent {
    constructor() {
        /**
         * Options passed to `routerLinkActiveOptions` directive which set on tab links.
         */
        this.activeLinkOptions = { exact: true };
        /**
         * Emits when tab is selected
         * @type {EventEmitter<CjRouteTab>}
         */
        this.changeTab = new EventEmitter();
    }
    selectTab(tab) {
        this.changeTab.emit(tab);
    }
    showTooltip(tabIndex) {
        const tabs = document.getElementsByClassName('tab-link');
        return tabs[tabIndex].clientWidth < 320;
    }
}
CjRouteTabsetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CjRouteTabsetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjRouteTabsetComponent, selector: "cj-route-tabset", inputs: { tabs: "tabs", activeLinkOptions: "activeLinkOptions" }, outputs: { changeTab: "changeTab" }, ngImport: i0, template: `
    <ul class="route-tabset">
      <ng-container *ngFor="let tab of tabs; let i = index">
        <li
          *ngIf="tab.disabled; else enabled"
          [class.responsive]="tab.responsive"
          class="route-tab disabled"
          tabindex="-1"
        >
          <a tabindex="-1" class="tab-link">
            <cj-icon *ngIf="tab.icon" [config]="tab.icon"></cj-icon>
            <span *ngIf="tab.title" class="tab-text truncate">{{ tab.title }}</span>
          </a>
        </li>

        <ng-template #enabled>
          <li
            routerLinkActive="active"
            [routerLinkActiveOptions]="activeLinkOptions | cjMergeConfigs: tab.activeLinkOptions"
            class="route-tab"
          >
            <a
              (click)="selectTab(tab)"
              [routerLink]="tab.route"
              [class.responsive]="tab.responsive"
              [queryParams]="tab.queryParams"
              [queryParamsHandling]="tab.queryParamsHandling"
              [fragment]="tab.fragment"
              [preserveFragment]="tab.preserveFragment"
              [skipLocationChange]="tab.skipLocationChange"
              [replaceUrl]="tab.replaceUrl"
              [state]="tab.state"
              [cjTooltip]="tab.title"
              [cjTooltipDisabled]="showTooltip(i)"
              [cjTooltipPlacement]="'bottom'"
              class="tab-link"
            >
              <cj-icon *ngIf="tab.icon" [config]="tab.icon"></cj-icon>
              <span *ngIf="tab.title" class="tab-text truncate">{{ tab.title }}</span>
            </a>
          </li>
        </ng-template>
      </ng-container>
    </ul>
    <router-outlet></router-outlet>
  `, isInline: true, styles: [".route-tabset{display:flex;flex-direction:row;list-style-type:none;margin:0;padding:0}.route-tabset .route-tab{text-align:center;padding:0;overflow:hidden}.route-tabset .route-tab.active a:before{display:block}.route-tabset .route-tab a{position:relative;text-decoration:none;display:flex;align-items:center}.route-tabset .route-tab a:before{position:absolute;content:\"\";width:100%;border-radius:3px;bottom:-2px;left:0}.route-tabset .route-tab a cj-icon{vertical-align:middle;font-size:1rem;flex-shrink:0}[dir=ltr] :host .tab-link cj-icon+span,[dir=ltr] :host .tab-link svg+span{margin-left:.5rem}[dir=rtl] :host .tab-link cj-icon+span,[dir=rtl] :host .tab-link svg+span{margin-right:.5rem}\n"], components: [{ type: CjIconComponent, selector: "cj-icon", inputs: ["icon", "pack", "options", "status", "config"] }], directives: [{ type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i6.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }, { type: i6.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo", "routerLink"] }, { type: CjTooltipDirective, selector: "[cjTooltip]", inputs: ["cjTooltip", "cjTooltipPlacement", "cjTooltipAdjustment", "cjTooltipClass", "cjTooltipIcon", "cjTooltipTrigger", "cjTooltipOffset", "cjTooltipDisabled"], outputs: ["cjTooltipShowStateChange"], exportAs: ["cjTooltip"] }, { type: i6.RouterOutlet, selector: "router-outlet", outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }], pipes: { "cjMergeConfigs": CjMergeConfigsPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-route-tabset', template: `
    <ul class="route-tabset">
      <ng-container *ngFor="let tab of tabs; let i = index">
        <li
          *ngIf="tab.disabled; else enabled"
          [class.responsive]="tab.responsive"
          class="route-tab disabled"
          tabindex="-1"
        >
          <a tabindex="-1" class="tab-link">
            <cj-icon *ngIf="tab.icon" [config]="tab.icon"></cj-icon>
            <span *ngIf="tab.title" class="tab-text truncate">{{ tab.title }}</span>
          </a>
        </li>

        <ng-template #enabled>
          <li
            routerLinkActive="active"
            [routerLinkActiveOptions]="activeLinkOptions | cjMergeConfigs: tab.activeLinkOptions"
            class="route-tab"
          >
            <a
              (click)="selectTab(tab)"
              [routerLink]="tab.route"
              [class.responsive]="tab.responsive"
              [queryParams]="tab.queryParams"
              [queryParamsHandling]="tab.queryParamsHandling"
              [fragment]="tab.fragment"
              [preserveFragment]="tab.preserveFragment"
              [skipLocationChange]="tab.skipLocationChange"
              [replaceUrl]="tab.replaceUrl"
              [state]="tab.state"
              [cjTooltip]="tab.title"
              [cjTooltipDisabled]="showTooltip(i)"
              [cjTooltipPlacement]="'bottom'"
              class="tab-link"
            >
              <cj-icon *ngIf="tab.icon" [config]="tab.icon"></cj-icon>
              <span *ngIf="tab.title" class="tab-text truncate">{{ tab.title }}</span>
            </a>
          </li>
        </ng-template>
      </ng-container>
    </ul>
    <router-outlet></router-outlet>
  `, styles: [".route-tabset{display:flex;flex-direction:row;list-style-type:none;margin:0;padding:0}.route-tabset .route-tab{text-align:center;padding:0;overflow:hidden}.route-tabset .route-tab.active a:before{display:block}.route-tabset .route-tab a{position:relative;text-decoration:none;display:flex;align-items:center}.route-tabset .route-tab a:before{position:absolute;content:\"\";width:100%;border-radius:3px;bottom:-2px;left:0}.route-tabset .route-tab a cj-icon{vertical-align:middle;font-size:1rem;flex-shrink:0}[dir=ltr] :host .tab-link cj-icon+span,[dir=ltr] :host .tab-link svg+span{margin-left:.5rem}[dir=rtl] :host .tab-link cj-icon+span,[dir=rtl] :host .tab-link svg+span{margin-right:.5rem}\n"] }]
        }], propDecorators: { tabs: [{
                type: Input
            }], activeLinkOptions: [{
                type: Input
            }], changeTab: [{
                type: Output
            }] } });

class CjRouteTabsetModule {
}
CjRouteTabsetModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjRouteTabsetModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetModule, declarations: [CjRouteTabsetComponent, CjMergeConfigsPipe], imports: [CjSharedModule, CjIconModule, CjTooltipModule], exports: [CjRouteTabsetComponent] });
CjRouteTabsetModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetModule, imports: [[CjSharedModule, CjIconModule, CjTooltipModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjRouteTabsetModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjSharedModule, CjIconModule, CjTooltipModule],
                    declarations: [CjRouteTabsetComponent, CjMergeConfigsPipe],
                    exports: [CjRouteTabsetComponent],
                }]
        }] });

class CjDirectionality extends Directionality {
}
CjDirectionality.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDirectionality, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
CjDirectionality.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDirectionality });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjDirectionality, decorators: [{
            type: Injectable
        }] });

class CjBidiModule extends BidiModule {
}
CjBidiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBidiModule, deps: null, target: i0.ɵɵFactoryTarget.NgModule });
CjBidiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBidiModule });
CjBidiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBidiModule, providers: [
        { provide: CjDirectionality, useExisting: Directionality },
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjBidiModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        { provide: CjDirectionality, useExisting: Directionality },
                    ],
                }]
        }] });

class CjPopoverComponent extends CjPositionedContainerComponent {
    renderContent() {
        this.detachContent();
        this.attachContent();
    }
    detachContent() {
        this.overlayContainer.detach();
    }
    attachContent() {
        if (this.content instanceof TemplateRef) {
            this.attachTemplate();
        }
        else if (this.content instanceof Type) {
            this.attachComponent();
        }
        else {
            this.attachString();
        }
    }
    attachTemplate() {
        this.overlayContainer
            .attachTemplatePortal(new CjTemplatePortal(this.content, null, { $implicit: this.context }));
    }
    attachComponent() {
        const portal = new CjComponentPortal(this.content, null, null, this.cfr);
        const ref = this.overlayContainer.attachComponentPortal(portal, this.context);
        ref.changeDetectorRef.detectChanges();
    }
    attachString() {
        this.overlayContainer.attachStringContent(this.content);
    }
}
CjPopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
CjPopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CjPopoverComponent, selector: "cj-popover", inputs: { content: "content", context: "context", cfr: "cfr" }, viewQueries: [{ propertyName: "overlayContainer", first: true, predicate: CjOverlayContainerComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: `
    <cj-overlay-container></cj-overlay-container>
  `, isInline: true, styles: [""], components: [{ type: CjOverlayContainerComponent, selector: "cj-overlay-container" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cj-popover', template: `
    <cj-overlay-container></cj-overlay-container>
  `, styles: [""] }]
        }], propDecorators: { overlayContainer: [{
                type: ViewChild,
                args: [CjOverlayContainerComponent]
            }], content: [{
                type: Input
            }], context: [{
                type: Input
            }], cfr: [{
                type: Input
            }] } });

class CjPopoverDirective {
    constructor(hostRef, dynamicOverlayHandler) {
        this.hostRef = hostRef;
        this.dynamicOverlayHandler = dynamicOverlayHandler;
        this.popoverComponent = CjPopoverComponent;
        this.destroy$ = new Subject();
        /**
         * Container content context. Will be applied to the rendered component.
         * */
        this.context = {};
        /**
         * Position will be calculated relatively host element based on the position.
         * Can be top, right, bottom, left, start or end.
         * */
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.position = CjPosition.TOP;
        this._adjustment = CjAdjustment.CLOCKWISE;
        /**
         * Describes when the container will be shown.
         * Available options: `click`, `hover`, `hint`, `focus` and `noop`
         * */
        this.trigger = CjTrigger.CLICK;
        /**
         * Sets popover offset
         * */
        this.offset = 8;
        /** Disables the display of the popover. */
        // eslint-disable-next-line @angular-eslint/no-input-rename
        this.disabled = false;
        this._popoverClass = '';
        this.cjPopoverShowStateChange = new EventEmitter();
        this.overlayConfig = { panelClass: this.popoverClass };
    }
    /**
     * Container position will be changes automatically based on this strategy if container can't fit view port.
     * Set this property to `noop` value if you want to disable automatically adjustment.
     * Available values: `clockwise` (default), `counterclockwise`, `vertical`, `horizontal`, `noop`.
     * */
    get adjustment() {
        return this._adjustment;
    }
    set adjustment(value) {
        this._adjustment = value;
    }
    get popoverClass() {
        return this._popoverClass;
    }
    set popoverClass(value) {
        if (value !== this.popoverClass) {
            this._popoverClass = value;
            this.overlayConfig = { panelClass: this.popoverClass };
        }
    }
    get isShown() {
        return !!(this.dynamicOverlay && this.dynamicOverlay.isAttached);
    }
    ngOnInit() {
        this.dynamicOverlayHandler.host(this.hostRef).componentType(this.popoverComponent);
    }
    ngOnChanges() {
        this.rebuild();
    }
    ngAfterViewInit() {
        this.dynamicOverlay = this.configureDynamicOverlay().build();
        this.dynamicOverlay.isShown
            .pipe(skip(1), takeUntil(this.destroy$))
            .subscribe((isShown) => this.cjPopoverShowStateChange.emit({ isShown }));
    }
    rebuild() {
        this.dynamicOverlay = this.configureDynamicOverlay().rebuild();
    }
    show() {
        this.dynamicOverlay.show();
    }
    hide() {
        this.dynamicOverlay.hide();
    }
    toggle() {
        this.dynamicOverlay.toggle();
    }
    ngOnDestroy() {
        this.dynamicOverlayHandler.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }
    configureDynamicOverlay() {
        return this.dynamicOverlayHandler
            .position(this.position)
            .trigger(this.trigger)
            .disabled(this.disabled)
            .offset(this.offset)
            .adjustment(this.adjustment)
            .content(this.content)
            .context(this.context)
            .overlayConfig(this.overlayConfig);
    }
}
CjPopoverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverDirective, deps: [{ token: i0.ElementRef }, { token: CjDynamicOverlayHandler }], target: i0.ɵɵFactoryTarget.Directive });
CjPopoverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CjPopoverDirective, selector: "[cjPopover]", inputs: { content: ["cjPopover", "content"], context: ["cjPopoverContext", "context"], position: ["cjPopoverPlacement", "position"], adjustment: ["cjPopoverAdjustment", "adjustment"], trigger: ["cjPopoverTrigger", "trigger"], offset: ["cjPopoverOffset", "offset"], disabled: ["cjPopoverDisabled", "disabled"], popoverClass: ["cjPopoverClass", "popoverClass"] }, outputs: { cjPopoverShowStateChange: "cjPopoverShowStateChange" }, providers: [CjDynamicOverlayHandler, CjDynamicOverlay], exportAs: ["cjPopover"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cjPopover]',
                    exportAs: 'cjPopover',
                    providers: [CjDynamicOverlayHandler, CjDynamicOverlay],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: CjDynamicOverlayHandler }]; }, propDecorators: { content: [{
                type: Input,
                args: ['cjPopover']
            }], context: [{
                type: Input,
                args: ['cjPopoverContext']
            }], position: [{
                type: Input,
                args: ['cjPopoverPlacement']
            }], adjustment: [{
                type: Input,
                args: ['cjPopoverAdjustment']
            }], trigger: [{
                type: Input,
                args: ['cjPopoverTrigger']
            }], offset: [{
                type: Input,
                args: ['cjPopoverOffset']
            }], disabled: [{
                type: Input,
                args: ['cjPopoverDisabled']
            }], popoverClass: [{
                type: Input,
                args: ['cjPopoverClass']
            }], cjPopoverShowStateChange: [{
                type: Output
            }] } });

class CjPopoverModule {
}
CjPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CjPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverModule, declarations: [CjPopoverDirective, CjPopoverComponent], imports: [CjOverlayModule], exports: [CjPopoverDirective] });
CjPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverModule, imports: [[CjOverlayModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CjPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CjOverlayModule],
                    declarations: [CjPopoverDirective, CjPopoverComponent],
                    exports: [CjPopoverDirective],
                    entryComponents: [CjPopoverComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BUILT_IN_THEMES, CJ_BUILT_IN_JS_THEMES, CJ_DOCUMENT, CJ_JS_THEMES, CJ_LAYOUT_DIRECTION, CJ_MEDIA_BREAKPOINTS, CJ_THEME_OPTIONS, CJ_WINDOW, CjA11yModule, CjAdjustableConnectedPositionStrategy, CjAdjustment, CjBidiModule, CjBlockScrollStrategyAdapter, CjButton, CjButtonComponent, CjButtonModule, CjCardComponent, CjCardModule, CjCdkAdapterModule, CjCdkMappingModule, CjCheckboxComponent, CjCheckboxModule, CjClickTriggerStrategy, CjColorHelper, CjComponentPortal, CjDirectionality, CjDynamicOverlay, CjDynamicOverlayChange, CjDynamicOverlayHandler, CjFlexibleConnectedPositionStrategy, CjFocusKeyManager, CjFocusKeyManagerFactoryService, CjFocusMonitor, CjFocusTrap, CjFocusTrapFactoryService, CjFocusTriggerStrategy, CjFontIcon, CjFormErrorComponent, CjFormFieldComponent, CjFormFieldControl, CjFormFieldControlConfig, CjFormFieldModule, CjFormHintComponent, CjFormLabelComponent, CjGlobalLogicalPosition, CjGlobalPhysicalPosition, CjGlobalPositionStrategy, CjHintTriggerStrategy, CjHoverTriggerStrategy, CjIconComponent, CjIconDefinition, CjIconLibraries, CjIconModule, CjIconPackType, CjInputDirective, CjInputModule, CjJSThemesRegistry, CjLayoutColumnComponent, CjLayoutComponent, CjLayoutDirection, CjLayoutDirectionService, CjLayoutFooterComponent, CjLayoutHeaderComponent, CjLayoutModule, CjLayoutRulerService, CjLayoutScrollService, CjLtrDirective, CjMediaBreakpointsService, CjMenuComponent, CjMenuItem, CjMenuItemComponent, CjMenuModule, CjMenuService, CjNoopTriggerStrategy, CjOverlay, CjOverlayContainer, CjOverlayContainerAdapter, CjOverlayContainerComponent, CjOverlayModule, CjOverlayPositionBuilder, CjOverlayService, CjPlatform, CjPopoverComponent, CjPopoverDirective, CjPopoverModule, CjPortalDirective, CjPortalInjector, CjPortalOutletDirective, CjPosition, CjPositionBuilderService, CjPositionHelper, CjPositionedContainerComponent, CjPrefixDirective, CjRadioComponent, CjRadioGroupComponent, CjRadioModule, CjRestoreScrollTopHelper, CjRouteTabsetComponent, CjRouteTabsetModule, CjRtlDirective, CjScrollDispatcherAdapter, CjScrollStrategyOptions, CjSidebarComponent, CjSidebarFooterComponent, CjSidebarHeaderComponent, CjSidebarModule, CjSidebarService, CjSpinnerService, CjStatusService, CjSuffixDirective, CjSvgIcon, CjTabComponent, CjTabContentDirective, CjTabTitleDirective, CjTabsetComponent, CjTabsetModule, CjTemplatePortal, CjThemeModule, CjThemeService, CjToggleStates, CjTooltipComponent, CjTooltipDirective, CjTooltipModule, CjTrigger, CjTriggerStrategyBase, CjTriggerStrategyBuilderService, CjViewportRulerAdapter, DARK_THEME, DEFAULT_MEDIA_BREAKPOINTS, LIGHT_THEME, VEGA_DARK_THEME, VEGA_LIGHT_THEME, createContainer, patch, windowFactory };
//# sourceMappingURL=cashjar-dev-ui.mjs.map
